/**
 * Clés de traduction françaises (UX-10 : interface en français, libellés courts, verbes
 * d'action). Langue unique pour l'instant (AV-079, aucun impact sur la stack) : i18next reste
 * en place dès P0 pour éviter une réécriture si d'autres langues sont ajoutées plus tard.
 */
export const fr = {
  app: {
    name: 'GIC AGROPELC',
    tagline: 'Gestion intégrée du groupement',
    footer: 'Application professionnelle · utilisable hors ligne',
    screen_loading: 'Ouverture de l’écran…',
  },
  // Libellés des composants partagés (`components/ui`, ADR-049).
  ui: {
    close: 'Fermer',
    back: 'Retour',
    loading: 'Chargement…',
    optional: 'facultatif',
    notifications: 'Notifications',
    theme: {
      title: 'Apparence',
      light: 'Clair',
      dark: 'Sombre',
      auto: 'Automatique',
    },
  },
  auth: {
    login_title: 'Connexion',
    login_subtitle: 'Identifiez-vous pour accéder à votre espace.',
    phone_label: 'Téléphone ou e-mail',
    phone_placeholder: '+237 6XX XX XX XX ou nom@gic-agropelc.com',
    password_label: 'Mot de passe',
    show_password: 'Afficher',
    hide_password: 'Masquer',
    submit: 'Se connecter',
    submitting: 'Connexion…',
    error_invalid_credentials: 'Identifiants invalides.',
    error_device_blocked: 'Cet appareil est bloqué : contactez votre responsable.',
    error_device_lost: 'Cet appareil a été déclaré perdu : contactez votre responsable.',
    error_device_retired: 'Cet appareil est retiré du service : contactez votre responsable.',
    error_offline: 'Pas de réseau : la première connexion doit se faire en ligne.',
    error_locked: 'Trop de tentatives : réessayez plus tard.',
    error_server_error: 'Le service est momentanément indisponible. Réessayez dans un instant.',
    error_session_expired: 'Connexion expirée : reconnectez-vous pour choisir votre mot de passe.',
    password_change_title: 'Choisir votre mot de passe',
    password_change_help:
      'Votre mot de passe actuel est provisoire : remplacez-le par un mot de passe personnel, que vous serez seul à connaître.',
    password_change_current: 'Mot de passe provisoire',
    password_change_new: 'Nouveau mot de passe (8 caractères au moins)',
    password_change_confirm: 'Confirmer le nouveau mot de passe',
    password_change_show: 'Afficher les mots de passe',
    password_change_submit: 'Enregistrer mon mot de passe',
    password_change_submitting: 'Enregistrement…',
    password_change_cancel: 'Annuler et se déconnecter',
    password_change_error: {
      TOO_SHORT: 'Mot de passe trop court : 8 caractères au moins.',
      TOO_LONG: 'Mot de passe trop long : 128 caractères au plus.',
      UNCHANGED: 'Le nouveau mot de passe doit être différent du mot de passe provisoire.',
      MISMATCH: 'Les deux saisies du nouveau mot de passe ne correspondent pas.',
      INVALID_CURRENT: 'Mot de passe provisoire incorrect.',
      LOCKED: 'Trop de tentatives : réessayez plus tard.',
      OFFLINE: 'Pas de réseau : le changement de mot de passe se fait en ligne.',
      SERVER_ERROR: 'Le service est momentanément indisponible. Réessayez dans un instant.',
    },
    pin_setup_title: 'Choisir un code PIN',
    pin_setup_help: 'Ce code à 6 chiffres déverrouillera l’application hors ligne.',
    pin_confirm_title: 'Confirmer le code PIN',
    pin_label: 'Code PIN (6 chiffres)',
    pin_confirm_label: 'Confirmer le code PIN',
    pin_mismatch: 'Les deux codes ne correspondent pas.',
    pin_submit: 'Valider',
    unlock_title: 'Déverrouiller',
    unlock_help: 'Saisissez votre code PIN.',
    unlock_submit: 'Déverrouiller',
    pin_error_attempts: '{{count}} tentative restante',
    pin_error_attempts_other: '{{count}} tentatives restantes',
    pin_wiped: 'Trop d’échecs : reconnexion en ligne obligatoire.',
    logout: 'Se déconnecter',
    logout_confirm_pending:
      'Des opérations sont en attente d’envoi ({{count}}). Se déconnecter quand même ?',
    logout_confirm: 'Se déconnecter ?',
  },
  home: {
    title: 'Accueil',
    greeting: 'Bonjour, {{name}}',
    loading: 'Chargement de votre tableau de bord…',
    no_roles:
      'Aucun rôle actif ne vous est attribué : demandez à un administrateur de vous en donner un.',
    updated_at: 'Mis à jour à {{time}}',
    refresh: 'Actualiser',
    refreshing: 'Actualisation…',
    retry: 'Réessayer',
    banner_offline: 'Hors ligne : ces chiffres datent de {{time}}.',
    banner_stale: 'Actualisation impossible pour le moment : ces chiffres datent de {{time}}.',
    error_offline: 'Pas de réseau et aucune donnée récente à afficher.',
    error_session_invalid: 'Votre session a expiré : reconnectez-vous.',
    error_server_error: 'Le service est momentanément indisponible. Réessayez dans un instant.',
    signals_title: 'À traiter en priorité',
    signals_none: 'Rien d’urgent pour le moment.',
    severity_critical: 'Urgent',
    severity_warning: 'À surveiller',
    severity_info: 'Information',
    actions_title: 'Actions du jour',
    kpis_title: 'Indicateurs',
    activity_empty: 'Rien de récent.',
    soon: 'Bientôt',
    spark_label: 'Évolution sur {{count}} jours',
    no_roles_title: 'Aucun rôle actif',
    role_picker: 'Rôle affiché',
  },
  nav: {
    skip: 'Aller au contenu',
    primary: 'Navigation principale',
    quick_actions: 'Actions rapides',
    quick_open: 'Actions rapides',
    quick_empty: 'Aucune action rapide pour le moment.',
    account: 'Mon compte',
    account_open: 'Ouvrir le menu du compte',
    account_roles: 'Rôles',
    tabs: {
      home: 'Accueil',
      more: 'Plus',
      sales: 'Ventes',
      stock: 'Stock',
      customers: 'Clients',
      orders: 'Commandes',
      receipts: 'Réceptions',
      daily: 'Saisie du jour',
      eggs: 'Collecte',
      lots: 'Lots',
      approvals: 'Validations',
      treasury: 'Trésorerie',
      receivables: 'Créances',
      requests: 'Demandes',
      purchase_orders: 'Commandes',
      devices: 'Appareils',
      users: 'Comptes',
    },
    menu: 'Menu',
    menu_title: 'Menu',
    no_navigation:
      'Vos actions apparaîtront ici après une première ouverture de l’accueil avec du réseau.',
    actions_title: 'Actions du jour',
    offline_ok: 'Hors ligne',
    open_menu: 'Ouvrir le menu',
    offline_menu_hint: 'Vos actions restent accessibles hors ligne depuis le menu.',
    groups: {
      general: 'Général',
      sales: 'Ventes',
      stock: 'Stock',
      crm: 'Clients',
      procurement: 'Achats',
      production: 'Production',
      finance: 'Finance',
    },
    screens: {
      home: 'Accueil',
      sync: 'Synchronisation',
      new_sale: '+ Nouvelle vente',
      sales: 'Ventes récentes',
      sale_detail: 'Détail d’une vente',
      new_order: '+ Nouvelle commande',
      orders: 'Commandes',
      order_detail: 'Détail d’une commande',
      deliver: 'Livrer une commande',
      collect: 'Encaisser une créance',
      stock: 'Stock par emplacement',
      new_prospect: '+ Prospect',
      portfolio: 'Mon portefeuille',
      performance: 'Mes objectifs',
      new_visit: '+ Visite',
      service: 'Prendre / finir le service',
      loss: 'Déclarer une perte',
      receive: 'Recevoir un transfert',
      transfer: 'Transférer',
      count: 'Inventaire',
      consumption: 'Consommer un intrant',
      price_simulator: 'Simulateur de prix',
      price_rules: 'Règles tarifaires',
      catalog: 'Catalogue',
      stock_ledger: 'Registre des mouvements',
      production_output: 'Sortie vers commercialisation',
      lot_setup: 'Nouveau lot ou entrée',
      incubation: 'Incubation',
      lot_sheet: 'Fiche lot',
      targets: 'Gestion des objectifs',
      suppliers: 'Fournisseurs',
      purchase_orders: 'Bons de commande',
      purchase_receipt: 'Réceptionner',
      purchase_request: 'Demande d’achat',
      production_lots: 'Lots actifs',
      daily_entry: 'Saisie du jour',
      egg_collection: 'Collecte d’œufs',
      cash_session: 'Caisse',
      cash_transfers: 'Remises de fonds',
      treasury: 'Trésorerie',
      expenses: 'Dépenses',
      supplier_invoices: 'Factures fournisseurs',
      supplier_payments: 'Paiements fournisseurs',
      order_matching: 'Rapprochement des achats',
      costs_margins: 'Coûts et marges',
      receivables: 'Créances',
      replenish: 'Demander un réapprovisionnement',
      distribution: 'Distribution par point de vente',
      pos_home: 'Mon point de vente',
      allocations: 'Quotas des téléphones',
      devices: 'Appareils',
      approvals: 'Validations',
      users: 'Utilisateurs',
      organization: 'Organisation',
      settings: 'Paramètres',
      audit: 'Journal d’audit',
      reassign_customers: 'Réaffecter un client',
      team_activity: 'Suivre l’équipe',
      sync_monitor: 'Supervision de la synchronisation',
      sync_conflicts: 'Conflits de synchronisation',
      alerts: 'Alertes et notifications',
      notes: 'Notes de direction',
      notes_publish: 'Publier une note de direction',
      analytics_direction: 'Tour de contrôle',
      analytics_commercial: 'Tableau de bord commercial',
      analytics_stock: 'Tableau de bord stock',
      analytics_production: 'Tableau de bord production',
      analytics_finance: 'Tableau de bord finance',
      analytics_explorer: 'Explorateur analytique',
    },
  },
  sales: {
    loading: 'Chargement des données de l’appareil…',
    location_label: 'Vendre depuis',
    no_location:
      'Aucun emplacement de vente sur cet appareil : synchronisez, ou demandez une affectation à un point de vente ou un stock mobile.',
    customer_title: 'Client',
    customer_anonymous: 'Client de passage',
    customer_unknown: 'Client inconnu de cet appareil',
    customer_anonymous_hint: 'Vente payée en totalité',
    customer_search: 'Rechercher un client (nom ou téléphone)',
    customer_none: 'Aucun client trouvé sur cet appareil.',
    customer_change: 'Changer',
    customer_missing:
      'Le client choisi n’est plus sur ce téléphone (création refusée, ou hors de votre périmètre) : choisissez un autre client.',
    customer_missing_anonymous: 'Vendre sans client',
    customer_outstanding: 'Encours : {{amount}}',
    lines_title: 'Articles',
    add_product: '+ Ajouter un article',
    product_search: 'Rechercher un produit',
    product_none: 'Aucun produit vendable sur cet appareil.',
    product_available: 'Disponible : {{quantity}} {{unit}}',
    close_picker: 'Fermer',
    quantity_label: 'Quantité',
    unit_label: 'Unité',
    weight_label: 'Poids pesé (kg)',
    unit_price: 'Prix unitaire : {{amount}}',
    no_catalog_price: 'Aucun prix catalogue : saisissez le prix.',
    change_price: 'Modifier le prix',
    use_catalog_price: 'Revenir au prix catalogue',
    price_label: 'Prix unitaire (F CFA)',
    reason_label: 'Motif du changement de prix',
    reason_placeholder: 'Choisir un motif',
    remove_line: 'Retirer',
    line_total: 'Total ligne : {{amount}}',
    issue: {
      QUANTITY_INVALID: 'Quantité invalide (au plus 3 décimales).',
      WEIGHT_REQUIRED: 'Indiquez le poids pesé.',
      PRICE_REQUIRED: 'Aucun prix catalogue : saisissez le prix et un motif.',
      PRICE_INVALID: 'Prix invalide (francs entiers).',
      REASON_REQUIRED: 'Choisissez le motif du changement de prix.',
      UNIT_INVALID: 'Unité inconnue pour ce produit.',
    },
    total: 'Total',
    payment_title: 'Encaissement',
    method_label: 'Moyen de paiement',
    amount_label: 'Montant encaissé (F CFA)',
    reference_label: 'Référence de la transaction',
    balance_due: 'Reste dû : {{amount}}',
    fully_paid: 'Payé en totalité',
    error_payment_exceeds: 'Le montant encaissé dépasse le total.',
    error_amount_invalid: 'Montant encaissé invalide (francs entiers).',
    error_total_invalid: 'Montant total hors limites : vérifiez les quantités et les prix.',
    error_local_save:
      'Enregistrement impossible sur cet appareil. Réessayez ; si le problème persiste, synchronisez puis redémarrez l’application.',
    no_payment_method: 'Aucun moyen de paiement sur cet appareil : synchronisez.',
    account_label: 'Compte de réception',
    account_placeholder: 'Choisir le compte',
    account_server_choice: 'Laisser le serveur choisir',
    account_unknown_hint:
      'Le compte de réception sera choisi par le serveur. S’il en existe plusieurs de ce type, l’encaissement sera refusé : faites-le désigner par la Finance (AV-218).',
    payment_issue: {
      AMOUNT_INVALID: 'Montant encaissé invalide (francs entiers).',
      REFERENCE_REQUIRED:
        'La référence de la transaction est obligatoire pour ce moyen de paiement.',
      REFERENCE_TOO_LONG: 'Référence trop longue (80 caractères au plus).',
      ACCOUNT_REQUIRED: 'Choisissez le compte qui reçoit le paiement.',
      METHOD_REQUIRED: 'Choisissez un moyen de paiement.',
    },
    stale_prices:
      'Prix peut-être non à jour : aucune synchronisation complète depuis plus de 24 h. Le serveur signalera tout écart.',
    no_catalog_price_no_override:
      'Aucun prix catalogue pour ce produit ici : vente impossible sans dérogation, que votre rôle n’autorise pas.',
    custody_device_mismatch:
      'Ce stock est confié à un autre appareil : la vente hors ligne n’est pas possible ici.',
    quota_title: 'Quota de ce téléphone',
    quota_line: '{{product}} : {{remaining}} {{unit}} restant(s) sur votre quota.',
    quota_line_until:
      '{{product}} : {{remaining}} {{unit}} restant(s) sur votre quota, jusqu’au {{until}}.',
    quota_block_message: {
      ALLOCATION_MISSING:
        '{{product}} : aucun quota pour ce téléphone sur cet emplacement ; la vente hors ligne n’est pas possible.',
      ALLOCATION_EXPIRED:
        '{{product}} : votre quota a expiré ; demandez son renouvellement à votre responsable, il arrivera à la prochaine synchronisation.',
      ALLOCATION_EXCEEDED:
        '{{product}} : quota dépassé, il vous reste {{available}}, {{requested}} demandé(s).',
      INSUFFICIENT_STOCK:
        '{{product}} : stock insuffisant sur cet appareil, {{available}} disponible(s), {{requested}} demandé(s).',
    },
    quota_expired:
      '{{product}} : votre quota a expiré le {{until}} ; demandez son renouvellement à votre responsable, il arrivera à la prochaine synchronisation.',
    quota_signalled:
      '{{product}} : vente au-delà du quota autorisée sur ce site ({{code}}) ; le serveur la signalera.',
    quota_block: {
      ALLOCATION_MISSING: 'sans quota',
      ALLOCATION_EXPIRED: 'quota expiré',
      ALLOCATION_EXCEEDED: 'quota dépassé',
      INSUFFICIENT_STOCK: 'stock insuffisant',
      CUSTODY_DEVICE_MISMATCH: 'stock confié à un autre appareil',
    },
    error_anonymous_full:
      'Un client de passage paie la totalité : choisissez un client pour une vente à crédit.',
    warning_credit_not_allowed:
      'Ce client n’a pas droit au crédit : le serveur refusera la vente en ligne.',
    warning_credit_limit: 'Plafond de crédit dépassé de {{amount}}.',
    offline_banner:
      'Hors ligne : la vente sera enregistrée sur ce téléphone et envoyée au retour du réseau.',
    submit_sale: 'Enregistrer la vente',
    submitting: 'Enregistrement…',
    result: {
      WAITING_offline:
        'Vente enregistrée sur ce téléphone, elle sera envoyée dès le retour du réseau.',
      WAITING_online: 'Vente en cours d’envoi…',
      DONE: 'Vente {{doc}} enregistrée.',
      WARNING: 'Vente {{doc}} enregistrée, avec un avertissement à examiner.',
      WARNING_CASH_SESSION_MISSING:
        'Vente {{doc}} enregistrée sans caisse ouverte : l’encaissement en espèces est signalé à la Finance.',
      CONFLICT: 'Vente mise en attente d’examen (conflit).',
      REJECTED: 'Vente refusée : {{reason}}',
    },
    new_sale: 'Nouvelle vente',
    view_sale: 'Voir la vente',
    pending_number: 'En attente du numéro',
    list: {
      empty: 'Aucune vente sur les 7 derniers jours sur cet appareil.',
      window_hint: 'Ventes des 7 derniers jours connues de l’appareil.',
      anonymous: 'Client de passage',
      unknown_customer: 'Client',
      open: 'Ouvrir la vente {{doc}}',
      flagged_banner:
        'Ventes du jour signalées par le serveur ({{count}} sur cet appareil ; une vente pas encore téléchargée n’y figure pas).',
      flagged_empty: 'Aucune vente du jour signalée sur cet appareil.',
      show_all: 'Toutes les ventes',
      flags: 'Signalé : {{flags}}',
    },
    status: {
      PENDING: 'En attente d’envoi',
      REJECTED: 'Refusée',
      IN_CONFLICT: 'En conflit',
      CONFIRMED: 'Confirmée',
      CANCELLATION_REQUESTED: 'Annulation demandée',
      CANCELLED: 'Annulée',
    },
    total_to_collect: 'Total à encaisser',
    payment_status: {
      UNPAID: 'Non payée',
      PARTIALLY_PAID: 'Payée en partie',
      PAID: 'Payée',
    },
    detail: {
      not_found:
        'Vente introuvable sur cet appareil (au-delà de 7 jours, ou hors de votre périmètre).',
      back: 'Ventes récentes',
      receipt_title: 'Reçu',
      date: 'Le {{date}} à {{time}}',
      seller_you: 'Vendeur : vous',
      offline_captured: 'Saisie hors ligne',
      line: '{{quantity}} {{unit}} × {{price}}',
      discount: 'Remise : {{amount}}',
      cancelled_part: 'Annulé : {{amount}}',
      total: 'Total net',
      paid: 'Encaissé',
      balance: 'Reste dû',
      due_date: 'Échéance : {{date}}',
      print: 'Bon de vente',
      collect: 'Encaisser le reste dû',
      rejection: 'Refusée par le serveur : {{reason}}',
      cancellation_pending: 'Une annulation de cette vente attend d’être envoyée.',
      flag_hint: 'Points signalés par le serveur : {{flags}}',
      flag: {
        SESSION_REJECTED: 'Prise de service refusée',
        OUT_OF_SESSION: 'Vente hors prise de service',
        PRICE_MISMATCH: 'Prix différent du catalogue',
        PRODUCT_INACTIVE: 'Produit désactivé',
        ANONYMOUS_UNPAID: 'Vente anonyme non payée',
        CREDIT_OVER_LIMIT: 'Encours du client dépassé',
        STOCK_NEGATIVE: 'Stock devenu négatif',
        LOT_NOT_SELLABLE: 'Lot non vendable',
        PAYMENT_SUSPECT_DUPLICATE: 'Encaissement suspect de doublon',
        CASH_SESSION_MISSING: 'Espèces encaissées sans caisse ouverte',
        ORDER_OVER_FULFILMENT: 'Livraison au-delà de la commande',
        ALLOCATION_EXCEEDED: 'Quota dépassé',
        ALLOCATION_REVOKED_CONSUMED: 'Quota révoqué déjà consommé',
        ALLOCATION_OVERCOMMITTED: 'Quotas au-delà du stock',
        CUSTODY_DEVICE_MISMATCH: 'Stock confié à un autre appareil',
      },
    },
    cancel: {
      open: 'Annuler la vente',
      title: 'Annuler la vente',
      direct_hint:
        'Vous êtes le vendeur et la vente a moins de {{minutes}} min : l’annulation est immédiate.',
      request_hint:
        'Au-delà de {{minutes}} min, ou pour la vente d’un autre vendeur, l’annulation devient une demande soumise à validation.',
      reason_label: 'Motif',
      reason_placeholder: 'Choisir un motif',
      comment_label: 'Commentaire',
      comment_required: 'Indiquez un motif ou un commentaire.',
      treatment_legend: 'Somme déjà encaissée ({{amount}})',
      treatment_REFUND: 'Rembourser le client',
      treatment_CUSTOMER_CREDIT: 'Garder en avoir du client',
      treatment_required: 'Choisissez le sort de la somme encaissée.',
      submit: 'Confirmer l’annulation',
      close: 'Ne pas annuler',
      result: {
        WAITING_offline:
          'Annulation enregistrée sur ce téléphone, elle sera envoyée dès le retour du réseau.',
        WAITING_online: 'Annulation en cours d’envoi…',
        DONE: 'Annulation {{doc}} enregistrée.',
        WARNING: 'Annulation transformée en demande soumise à validation ({{doc}}).',
        CONFLICT: 'Annulation mise en attente d’examen (conflit).',
        REJECTED: 'Annulation refusée : {{reason}}',
      },
    },
  },
  orders: {
    customer_required: 'Choisissez le client de la commande.',
    fulfilment_label: 'Préparée à',
    no_fulfilment: 'Aucun emplacement de préparation connu de l’appareil : synchronisez.',
    stock_note:
      'La commande n’est jamais refusée faute de stock : le disponible est vendu à la confirmation, le reste attend.',
    delivery_title: 'Livraison',
    delivery_date_label: 'Livraison souhaitée le',
    delivery_address_label: 'Adresse de livraison',
    weight_not_orderable:
      'Un produit vendu au poids ne se commande pas : faites une vente directe.',
    total_estimated: 'Total estimé',
    total_of_order: 'Total de la commande',
    advance_title: 'Acompte',
    advance_toggle: 'Encaisser un acompte',
    advance_amount_label: 'Montant de l’acompte (F CFA)',
    error_advance_exceeds: 'L’acompte dépasse le total estimé.',
    error_advance_invalid: 'Montant de l’acompte invalide (francs entiers).',
    warning_credit_not_allowed:
      'Ce client n’a pas droit au crédit : le serveur refusera la commande en ligne si le reste n’est pas payé.',
    warning_credit_limit:
      'Plafond de crédit dépassé de {{amount}} : le serveur refusera la commande en ligne si le reste n’est pas payé.',
    offline_banner:
      'Hors ligne : la commande sera enregistrée sur ce téléphone et confirmée par le serveur au retour du réseau.',
    submit: 'Passer la commande',
    new_order: 'Nouvelle commande',
    view_orders: 'Voir les commandes',
    status: {
      PENDING: 'En attente d’envoi',
      REJECTED: 'Refusée',
      IN_CONFLICT: 'En conflit',
      DRAFT: 'Brouillon',
      CONFIRMED: 'Confirmée',
      PARTIALLY_FULFILLED: 'Livrée en partie',
      FULFILLED: 'Livrée',
      CLOSED: 'Close',
      CANCELLED: 'Annulée',
    },
    list: {
      hint: 'Commandes ouvertes connues de l’appareil (brouillons, confirmées, livrées en partie).',
      filter_label: 'Afficher',
      filter_all: 'Toutes',
      filter_to_deliver: 'À livrer',
      filter_waiting_stock: 'En attente de stock',
      empty: 'Aucune commande ouverte sur cet appareil.',
      open: 'Ouvrir la commande {{doc}}',
      to_deliver_badge: 'À livrer',
      waiting_stock_badge: 'Attend du stock',
      delivery_on: 'Livraison le {{date}}',
      closed_title: 'Commandes livrées ou closes (en ligne)',
      closed_load: 'Afficher les commandes livrées récemment',
      closed_offline: 'Disponible avec du réseau seulement.',
      closed_empty: 'Aucune commande livrée ou close récemment.',
      closed_error: 'Lecture impossible pour le moment.',
    },
    detail: {
      not_found:
        'Commande introuvable sur cet appareil (livrée, close, annulée, ou hors de votre périmètre).',
      back: 'Commandes',
      title: 'Commande {{doc}}',
      prepared_at: 'Préparée à {{location}}',
      ordered: 'Commandé',
      sold: 'Vendu',
      delivered: 'Livré',
      pending: 'En attente de stock',
      to_deliver: 'À livrer',
      delivering: 'Livraison en attente d’envoi : {{quantity}}',
      total_estimated: 'Total estimé',
      advance_paid: 'Acompte versé',
      deliver: 'Livrer',
      confirm_remaining: 'Vendre le disponible (reste en attente)',
      retire: 'Annuler la commande',
      close_remaining: 'Clôturer le reste',
      action_pending: 'Une opération sur cette commande attend d’être envoyée.',
      rejection: 'Refusée par le serveur : {{reason}}',
    },
    retire: {
      title_cancel: 'Annuler la commande',
      title_close: 'Clôturer le reste de la commande',
      hint_cancel:
        'Tout ce qui n’est pas livré est annulé : les ventes de la commande sont contre-passées.',
      hint_close:
        'La part en attente et la part vendue non livrée sont retirées ; le livré reste acquis.',
      treatment_legend: 'Acompte ou somme payée libérée',
      submit: 'Confirmer',
      close: 'Revenir',
    },
    confirm_result: {
      WAITING_offline:
        'Demande enregistrée sur ce téléphone, elle sera envoyée dès le retour du réseau.',
      WAITING_online: 'Envoi en cours…',
      DONE: 'Commande {{doc}} mise à jour.',
      WARNING: 'Commande {{doc}} mise à jour, avec un avertissement à examiner.',
      CONFLICT: 'Opération mise en attente d’examen (conflit).',
      REJECTED: 'Opération refusée : {{reason}}',
    },
    deliver: {
      title: 'Livrer la commande {{doc}}',
      hint: 'Indiquez la quantité remise au client pour chaque article (unité de base). Le reste demeure à livrer.',
      nothing: 'Rien à livrer sur cette commande pour le moment.',
      line_max: 'À livrer : {{quantity}} {{unit}}',
      quantity_label: 'Quantité remise ({{unit}})',
      recipient_label: 'Remis à (nom de la personne)',
      notes_label: 'Observations',
      error_quantity:
        'Quantité invalide pour {{product}} : entre 0 et {{max}}, au plus 3 décimales.',
      error_empty: 'Indiquez au moins une quantité remise.',
      submit: 'Enregistrer la livraison',
      offline_banner:
        'Hors ligne : la livraison sera enregistrée sur ce téléphone et envoyée au retour du réseau.',
      result: {
        WAITING_offline:
          'Livraison enregistrée sur ce téléphone, elle sera envoyée dès le retour du réseau.',
        WAITING_online: 'Livraison en cours d’envoi…',
        DONE: 'Livraison {{doc}} enregistrée.',
        WARNING: 'Livraison {{doc}} enregistrée, avec un avertissement à examiner.',
        CONFLICT: 'Livraison mise en attente d’examen (conflit).',
        REJECTED: 'Livraison refusée : {{reason}}',
      },
      back_to_order: 'Retour à la commande',
    },
    result: {
      WAITING_offline:
        'Commande enregistrée sur ce téléphone, elle sera envoyée dès le retour du réseau.',
      WAITING_online: 'Commande en cours d’envoi…',
      DONE: 'Commande {{doc}} confirmée.',
      WARNING: 'Commande {{doc}} confirmée, avec un avertissement à examiner.',
      WARNING_CASH_SESSION_MISSING:
        'Commande {{doc}} confirmée sans caisse ouverte : l’acompte en espèces est signalé à la Finance.',
      CONFLICT: 'Commande mise en attente d’examen (conflit).',
      REJECTED: 'Commande refusée : {{reason}}',
    },
  },
  prospect: {
    offline_banner:
      'Hors ligne : le prospect sera enregistré sur ce téléphone et envoyé au retour du réseau ; il est aussitôt proposé à la vente.',
    name_label: 'Nom du client ou de l’établissement',
    phone_label: 'Téléphone',
    phone_normalized: 'Enregistré comme {{phone}}',
    position_label: 'Position GPS',
    position_value: 'Position : {{lat}}, {{lng}} (précision {{accuracy}} m)',
    locate: 'Relever ma position',
    locating: 'Localisation…',
    gps_denied: 'Position indisponible : autorisez la localisation, ou saisissez un téléphone.',
    zone_label: 'Zone',
    zone_placeholder: 'Choisir la zone',
    source_label: 'Origine du contact',
    sources: {
      PROSPECTION_TERRAIN: 'Prospection terrain',
      VISITE_SPONTANEE: 'Visite spontanée',
      RECOMMANDATION: 'Recommandation',
      RESEAU_SOCIAL: 'Réseau social',
      AUTRE: 'Autre',
    },
    more: 'Plus d’informations (facultatif)',
    type_label: 'Type de client',
    types: {
      PARTICULIER: 'Particulier',
      ENTREPRISE: 'Entreprise',
    },
    contact_label: 'Personne à contacter',
    activity_label: 'Activité',
    address_label: 'Adresse',
    category_label: 'Catégorie de client',
    category_none: 'Aucune',
    issue: {
      NAME_REQUIRED: 'Indiquez le nom.',
      PHONE_OR_POSITION_REQUIRED: 'Indiquez un téléphone ou relevez la position GPS (BR-CRM-002).',
      PHONE_INVALID: 'Téléphone invalide.',
      ZONE_REQUIRED: 'Choisissez la zone.',
      SOURCE_REQUIRED: 'Choisissez l’origine du contact.',
    },
    duplicate_online:
      'Ce téléphone est déjà celui de « {{name}} » : ouvrez ce compte plutôt que d’en créer un autre.',
    duplicate_offline:
      'Ce téléphone est déjà celui de « {{name}} ». Hors ligne, le compte sera créé et signalé comme doublon à fusionner.',
    role_hint: 'Votre rôle ne permet peut-être pas de créer un compte : le serveur décidera.',
    submit: 'Enregistrer le prospect',
    another: 'Nouveau prospect',
    to_sale: 'Vendre à ce client',
    result: {
      WAITING_offline:
        'Prospect enregistré sur ce téléphone, il sera envoyé dès le retour du réseau.',
      WAITING_online: 'Prospect en cours d’envoi…',
      DONE: 'Prospect enregistré.',
      WARNING: 'Prospect enregistré, signalé comme doublon possible d’un compte existant.',
      CONFLICT: 'Prospect mis en attente d’examen (conflit).',
      REJECTED: 'Prospect refusé : {{reason}}',
    },
  },
  portfolio: {
    stage_label: 'Stade',
    stage_all_short: 'Tous',
    col: {
      customer: 'Client',
      stage: 'Stade',
      next_action: 'Prochaine action',
      outstanding: 'Encours',
    },
    stage_filter: {
      ALL: 'Tous les comptes',
      PROSPECT: 'Prospects',
      CUSTOMER: 'Clients',
      LOST: 'Perdus',
    },
    stage: {
      PROSPECT: 'Prospect',
      CUSTOMER: 'Client',
      LOST: 'Perdu',
      MERGED: 'Fusionné',
    },
    empty: 'Aucun compte ne correspond.',
    overdue: 'en retard',
    last_activity: 'Dernier contact {{when}}',
    next_action: 'À faire le {{date}} {{note}}',
    pending: 'Non confirmé',
  },
  customer: {
    back: 'Mon portefeuille',
    not_found:
      'Ce compte n’est pas sur ce téléphone (hors de votre périmètre, ou pas encore synchronisé).',
    pending: 'Compte créé sur ce téléphone, pas encore confirmé par la synchronisation.',
    contact: 'Contact',
    phone: 'Téléphone',
    address: 'Adresse',
    zone: 'Zone',
    outstanding: 'Encours',
    credit: 'Crédit',
    credit_none: 'Non accordé',
    credit_no_limit: 'Accordé, plafond non défini : toute vente à crédit demande une validation',
    credit_limit: 'Plafond {{amount}}',
    call: 'Appeler',
    sell: '+ Vente',
    visit_action: '+ Visite',
    interaction_action: 'Noter un échange',
    order: '+ Commande',
    collect: 'Encaisser',
    orders_title: 'Commandes ouvertes',
    sales_title: 'Ventes des 7 derniers jours',
    activity_title: 'Visites et échanges (90 jours)',
    balance_due: 'Reste {{amount}}',
    none: 'Rien à afficher.',
    visit: 'Visite {{outcome}}',
    channel: {
      APPEL: 'Appel',
      SMS: 'SMS',
      EMAIL: 'Courriel',
      AUTRE: 'Échange',
    },
  },
  visit: {
    offline_banner:
      'Hors ligne : la visite sera enregistrée sur ce téléphone et envoyée au retour du réseau.',
    out_of_session:
      'Vous n’avez pas pris service : la visite sera signalée « hors session » à votre responsable.',
    session_rejected:
      'Votre prise de service a été refusée : la visite sera signalée à votre responsable.',
    customer_title: 'Compte visité',
    position_label: 'Position',
    position_value: 'Position relevée (précision {{accuracy}} m).',
    gps_unavailable:
      'Position indisponible : autorisez la localisation, ou enregistrez la visite sans position.',
    locate: 'Relever ma position',
    locating: 'Localisation…',
    far_from_customer:
      'Vous êtes à {{distance}} m du compte (plus de {{max}} m) : la visite sera signalée.',
    save_customer_position: 'Enregistrer cette position comme celle du compte',
    outcome_label: 'Résultat de la visite',
    outcome_placeholder: 'Choisir le résultat',
    no_outcomes:
      'Aucun résultat de visite n’est paramétré (motifs « résultat de visite ») : demandez-le à l’administrateur.',
    notes_label: 'Commentaire (facultatif)',
    notes_label_required: 'Commentaire (obligatoire pour ce résultat)',
    next_action_at_label: 'Prochaine action le (facultatif)',
    next_action_note_label: 'Prochaine action à faire (facultatif)',
    issue: {
      COMMENT_REQUIRED: 'Ce résultat exige un commentaire : précisez ce qui s’est passé.',
      NEXT_ACTION_PAST: 'La prochaine action ne peut pas être dans le passé.',
    },
    role_hint:
      'Votre rôle ne permet peut-être pas d’enregistrer une visite ; le serveur tranchera.',
    submit: 'Enregistrer la visite',
    another: 'Autre visite',
    take_service: 'Prendre service',
    to_customer: 'Voir la fiche du compte',
    result: {
      WAITING_offline:
        'Visite enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Visite en cours d’envoi…',
      DONE: 'Visite enregistrée.',
      WARNING: 'Visite enregistrée, avec un signalement.',
      CONFLICT: 'Visite mise en attente d’examen (conflit).',
      REJECTED: 'Visite refusée : {{reason}}',
    },
  },
  performance: {
    period_label: 'Période',
    period: {
      MONTH: 'Ce mois-ci',
      TODAY: 'Aujourd’hui',
    },
    loading: 'Lecture des chiffres du serveur…',
    source_server: 'Chiffres du serveur, du {{from}} au {{to}}.',
    source_device:
      'Hors ligne : estimation de ce téléphone (90 derniers jours) ; le chiffre d’affaires, les quantités vendues et les nouveaux clients se lisent en ligne.',
    source_error:
      'Le serveur n’a pas répondu : estimation de ce téléphone (90 derniers jours) ; le chiffre d’affaires, les quantités vendues et les nouveaux clients se lisent en ligne.',
    retry: 'Réessayer',
    effort_title: 'Mon activité',
    effort: {
      visits: 'Visites',
      customersVisited: 'Comptes visités',
      interactions: 'Échanges',
      prospectsCreated: 'Prospects créés',
      newCustomers: 'Nouveaux clients',
    },
    online_only: 'en ligne',
    targets_title: 'Mes objectifs',
    no_target: 'Aucun objectif sur la période.',
    metric: {
      CA: 'Chiffre d’affaires',
      QTE_PRODUIT: 'Quantité vendue',
      NOUVEAUX_CLIENTS: 'Nouveaux clients',
      VISITES: 'Visites',
      PROSPECTS_CREES: 'Prospects créés',
    },
    target_period: 'du {{from}} au {{to}}',
    realized: '{{realized}} sur {{target}}',
    realized_online: 'Objectif {{target}} (réalisé en ligne)',
    rate: '{{rate}} %',
  },
  interaction: {
    title: 'Noter un échange',
    back: 'Fiche du compte',
    offline_banner:
      'Hors ligne : l’échange sera enregistré sur ce téléphone et envoyé au retour du réseau.',
    channel_label: 'Canal',
    direction_label: 'Sens',
    direction: {
      SORTANT: 'Vers le client',
      ENTRANT: 'Du client',
    },
    summary_label: 'Résumé (facultatif)',
    issue: {
      CHANNEL_REQUIRED: 'Choisissez le canal de l’échange.',
    },
    role_hint: 'Votre rôle ne permet peut-être pas de noter un échange ; le serveur tranchera.',
    submit: 'Enregistrer l’échange',
    another: 'Autre échange',
    result: {
      WAITING_offline: 'Échange enregistré sur ce téléphone ; il sera envoyé au retour du réseau.',
      WAITING_online: 'Échange en cours d’envoi…',
      DONE: 'Échange enregistré.',
      WARNING: 'Échange enregistré, avec un signalement.',
      CONFLICT: 'Échange mis en attente d’examen (conflit).',
      REJECTED: 'Échange refusé : {{reason}}',
    },
  },
  service: {
    offline_banner:
      'Hors ligne : la tentative est évaluée sur ce téléphone et enregistrée ; elle sera envoyée au retour du réseau.',
    start_title: 'Prendre service',
    zone_label: 'Zone de travail',
    zone_placeholder: 'Choisir la zone',
    no_zone:
      'Aucune zone avec géorepère n’est connue de ce téléphone : synchronisez, ou demandez à l’administrateur de définir le géorepère de votre zone.',
    position_label: 'Position',
    locate: 'Relever ma position',
    locating: 'Localisation…',
    gps_unavailable: 'Position indisponible : autorisez la localisation, puis relevez à nouveau.',
    gps_unavailable_end:
      'Position indisponible : la fin de service sera enregistrée sans position (signalée).',
    position_value: 'Position relevée (précision {{accuracy}} m).',
    evaluation: {
      ACCEPTED: 'Dans la zone : à {{distance}} m du centre (rayon {{radius}} m).',
      REJECTED_OUT_OF_ZONE:
        'Hors de la zone : à {{distance}} m du centre (rayon {{radius}} m). Rapprochez-vous puis relevez à nouveau.',
      REJECTED_LOW_ACCURACY:
        'Position trop imprécise (± {{accuracy}} m, au plus {{max}} m) : attendez quelques secondes puis relevez à nouveau.',
      NO_POSITION: 'Position indisponible : autorisez la localisation, puis relevez à nouveau.',
    },
    role_hint: 'Votre rôle ne permet peut-être pas de pointer ; le serveur tranchera.',
    start: 'PRENDRE SERVICE',
    change_zone_title: 'Changer de zone',
    start_supersedes: 'Une prise de service ici clôt la session en cours (zone {{zone}}).',
    change_zone: 'Prendre service dans cette zone',
    in_service_title: 'En service',
    in_service: 'En service depuis {{time}} — zone {{zone}}.',
    auto_close: 'Sans fin de service, la session se termine automatiquement à {{time}}.',
    override_pending:
      'Prise de service par dérogation : en attente de validation par votre responsable. Vos visites sont enregistrées.',
    override_rejected:
      'Dérogation refusée : vos visites de cette session sont signalées à votre responsable.',
    end: 'FIN DE SERVICE',
    override_label: 'Demander une dérogation',
    override_hint:
      'Plusieurs tentatives ont été refusées. Expliquez pourquoi : la session s’ouvrira en attente de validation.',
    override_submit: 'Demander la dérogation',
    attempts_title: 'Pointages du jour',
    no_attempt: 'Aucun pointage aujourd’hui sur ce téléphone.',
    type: {
      START_SERVICE: 'Prise de service',
      END_SERVICE: 'Fin de service',
    },
    attempt_result: {
      ACCEPTED: 'acceptée',
      REJECTED_OUT_OF_ZONE: 'hors zone',
      REJECTED_LOW_ACCURACY: 'position imprécise',
      NO_POSITION: 'sans position',
    },
    result_START: {
      WAITING_offline:
        'Service pris sur ce téléphone ; le pointage sera envoyé au retour du réseau.',
      WAITING_online: 'Pointage en cours d’envoi…',
      DONE: 'Service pris.',
      WARNING: 'Service pris, avec un signalement.',
      CONFLICT: 'Pointage mis en attente d’examen (conflit).',
      REJECTED: 'Pointage refusé : {{reason}}',
    },
    result_ATTEMPT: {
      WAITING_offline:
        'Tentative refusée sur ce téléphone, enregistrée ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Tentative refusée, en cours d’envoi…',
      DONE: 'Tentative refusée enregistrée.',
      WARNING: 'Tentative refusée enregistrée, avec un signalement.',
      CONFLICT: 'Tentative mise en attente d’examen (conflit).',
      REJECTED: 'Tentative refusée par le serveur : {{reason}}',
    },
    result_END: {
      WAITING_offline:
        'Fin de service enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Fin de service en cours d’envoi…',
      DONE: 'Fin de service enregistrée.',
      WARNING: 'Fin de service enregistrée, avec un signalement.',
      CONFLICT: 'Fin de service mise en attente d’examen (conflit).',
      REJECTED: 'Fin de service refusée : {{reason}}',
    },
    result_OVERRIDE: {
      WAITING_offline:
        'Dérogation demandée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Demande de dérogation en cours d’envoi…',
      DONE: 'Dérogation demandée : session ouverte, en attente de validation.',
      WARNING: 'Dérogation demandée, avec un signalement.',
      CONFLICT: 'Demande de dérogation mise en attente d’examen (conflit).',
      REJECTED: 'Dérogation refusée : {{reason}}',
    },
  },
  production: {
    no_lot: 'Aucun lot actif sur les fermes de ce téléphone.',
    lot_type: {
      POULET_CHAIR: 'Poulet de chair',
      PONDEUSE: 'Pondeuses',
      PORC_ENGRAISSEMENT: 'Porcs à l’engrais',
      REPRODUCTEUR_VOLAILLE: 'Reproducteurs',
      PORC_NAISSAGE: 'Truies (naissage)',
    },
    age_one: '{{count}} jour',
    age_other: '{{count}} jours',
    today_summary: 'Aujourd’hui : {{deaths}} mort(s), {{entries}} saisie(s)',
    headcount: '{{heads}}',
    headcount_unknown: 'Effectif inconnu',
    status: {
      PLANNED: 'Planifié',
      ACTIVE: 'Actif',
      SELLING: 'En vente',
    },
    col: {
      lot: 'Lot',
      age: 'Âge',
      heads: 'Effectif',
      today: 'Saisies du jour',
      status: 'Situation',
    },
    planned_title: 'Lots planifiés (pas encore de saisie)',
    back: 'Lots actifs',
    lot_not_daily: 'Ce lot n’accepte pas de saisie (planifié, clôturé, ou absent de ce téléphone).',
    offline_banner:
      'Hors ligne : chaque saisie est enregistrée sur ce téléphone et envoyée au retour du réseau.',
    to_egg_collection: 'Saisir la collecte d’œufs de ce lot',
    kind_label: 'Saisie',
    kind: {
      MORTALITY: 'Mortalité',
      INPUT: 'Aliment et intrants',
      WEIGHING: 'Pesée',
      OBSERVATION: 'Observation',
    },
    no_rearing_location: 'Aucun bâtiment ni case connu pour cette ferme.',
    rearing_location_label: 'Bâtiment ou case',
    deaths_label: 'Nombre de morts',
    count_invalid: 'Nombre entier attendu.',
    location_headcount: 'Effectif sur cet emplacement : {{heads}}',
    deaths_over_online:
      'Plus de morts que l’effectif de cet emplacement : le serveur refuserait la saisie. Vérifiez le nombre ou l’emplacement.',
    deaths_over_offline:
      'Plus de morts que l’effectif de cet emplacement connu du téléphone : la saisie sera tout de même enregistrée.',
    comment_required: 'Cette cause exige un commentaire.',
    comment_required_label: 'Commentaire (exigé par cette cause)',
    to_output: 'Sortie vers commercialisation →',
    to_sheet: 'Fiche du lot →',
    journal_pending_approval: 'En attente de validation',
    cause_label: 'Cause (facultatif)',
    cause_placeholder: 'Cause non précisée',
    comment_label: 'Commentaire (facultatif)',
    input_location_label: 'Pris sur',
    input_product_label: 'Aliment ou intrant',
    no_consumable:
      'Aucun intrant consommable n’est connu de ce téléphone (produits marqués consommables).',
    weighing_location_label: 'Bâtiment ou case pesé',
    whole_lot: 'Tout le lot',
    sample_label: 'Nombre de sujets pesés',
    avg_weight_label: 'Poids moyen (g)',
    total_weight_label: 'Poids total (kg, facultatif)',
    weighing_invalid: 'Nombre de sujets entier, poids positifs (1 décimale pour les grammes).',
    observation_type_label: 'Type',
    observation_type: {
      SANITAIRE: 'Sanitaire',
      COMPORTEMENT: 'Comportement',
      ENVIRONNEMENT: 'Environnement',
      INCIDENT: 'Incident',
      AUTRE: 'Autre (dont RAS)',
    },
    severity_label: 'Gravité',
    severity: {
      INFO: 'Information',
      WARNING: 'À surveiller',
      CRITICAL: 'Critique',
    },
    observation_text_label: 'Observation',
    submit: {
      MORTALITY: 'Enregistrer la mortalité',
      INPUT: 'Enregistrer la consommation',
      WEIGHING: 'Enregistrer la pesée',
      OBSERVATION: 'Enregistrer l’observation',
    },
    journal_title: 'Journal du jour',
    journal_empty: 'Aucune saisie aujourd’hui pour ce lot.',
    journal: {
      MORTALITY: '{{quantity}} mort(s)',
      INPUT: '{{product}} : {{quantity}} {{unit}}',
      WEIGHING: 'Pesée : {{sample}} sujets, {{weight}} g en moyenne',
      OBSERVATION: 'Observation',
      EGGS: 'Collecte : {{collected}} œufs, {{marketable}} commercialisables',
    },
    result_MORTALITY: {
      WAITING_offline:
        'Mortalité enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Mortalité en cours d’envoi…',
      DONE: 'Mortalité enregistrée : {{doc}}.',
      WARNING: 'Mortalité {{doc}} enregistrée, avec un signalement.',
      CONFLICT: 'Mortalité mise en attente d’examen (conflit).',
      REJECTED: 'Mortalité refusée : {{reason}}',
    },
    result_INPUT: {
      WAITING_offline:
        'Consommation enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Consommation en cours d’envoi…',
      DONE: 'Consommation enregistrée.',
      WARNING: 'Consommation enregistrée, avec un signalement.',
      CONFLICT: 'Consommation mise en attente d’examen (conflit).',
      REJECTED: 'Consommation refusée : {{reason}}',
    },
    result_WEIGHING: {
      WAITING_offline:
        'Pesée enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Pesée en cours d’envoi…',
      DONE: 'Pesée enregistrée.',
      WARNING: 'Pesée enregistrée, avec un signalement.',
      CONFLICT: 'Pesée mise en attente d’examen (conflit).',
      REJECTED: 'Pesée refusée : {{reason}}',
    },
    result_OBSERVATION: {
      WAITING_offline:
        'Observation enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Observation en cours d’envoi…',
      DONE: 'Observation enregistrée.',
      WARNING: 'Observation enregistrée, avec un signalement.',
      CONFLICT: 'Observation mise en attente d’examen (conflit).',
      REJECTED: 'Observation refusée : {{reason}}',
    },
  },
  eggs: {
    back: 'Saisie du jour {{code}}',
    offline_banner:
      'Hors ligne : la collecte sera enregistrée sur ce téléphone et envoyée au retour du réseau ; les œufs entrent aussitôt au disponible.',
    no_lot: 'Aucun lot de pondeuses ou de reproducteurs actif sur les fermes de ce téléphone.',
    lot_label: 'Lot',
    day_label: 'Jour de la collecte',
    day_invalid: 'La collecte ne peut pas être datée après aujourd’hui.',
    storage_label: 'Stockés sur',
    collected_label: 'Œufs collectés',
    broken_label: 'Cassés',
    nonconforming_label: 'Non conformes',
    hatching_label: 'À couver',
    grades_title: 'Commercialisables par calibre',
    grades_not_configured:
      'Calibres non paramétrés sur ce téléphone : produits commercialisables proposés ; le serveur peut refuser en ligne un calibre non paramétré.',
    no_grade: 'Aucun produit de calibre connu.',
    count_invalid: 'Nombres entiers attendus.',
    remaining_one: 'Reste {{count}} œuf à répartir (le bilan doit tomber juste).',
    remaining_other: 'Reste {{count}} œufs à répartir (le bilan doit tomber juste).',
    remaining_label: 'À répartir',
    hatching_missing:
      'Le produit des œufs à couver n’est pas paramétré : le serveur refuserait la collecte. Laissez « À couver » à 0.',
    submit: 'Enregistrer la collecte',
    result: {
      WAITING_offline:
        'Collecte enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Collecte en cours d’envoi…',
      DONE: 'Collecte enregistrée : {{doc}}.',
      WARNING: 'Collecte {{doc}} enregistrée, avec un signalement.',
      CONFLICT: 'Collecte mise en attente d’examen (conflit).',
      REJECTED: 'Collecte refusée : {{reason}}',
    },
  },
  purchase_request: {
    offline_banner:
      'Hors ligne : la demande sera enregistrée sur ce téléphone et envoyée au retour du réseau.',
    new_title: 'Nouvelle demande',
    site_label: 'Pour le site',
    site_placeholder: 'Choisir le site',
    scope_sites: 'Vos sites',
    other_sites: 'Autres sites (droit vérifié par le serveur)',
    needed_by_label: 'Souhaitée pour le (facultatif)',
    justification_label: 'Justification',
    no_product: 'Aucun produit achetable n’est connu de ce téléphone.',
    price_label: 'Prix estimé par {{unit}} (F, facultatif)',
    notes_label: 'Note (facultatif)',
    price_invalid: 'Prix estimé : nombre entier de francs.',
    issue: {
      SITE_REQUIRED: 'Aucun site connu : synchronisez avant de saisir une demande.',
      JUSTIFICATION_REQUIRED: 'La justification est obligatoire.',
      DATE_INVALID: 'La date souhaitée ne peut pas être passée.',
      LINES_REQUIRED: 'Ajoutez au moins une ligne.',
      TOO_MANY_LINES: 'Une demande compte au plus 100 lignes.',
    },
    validation_hint:
      'La demande ne se modifie plus une fois soumise ; un valideur autre que vous l’approuve ou la refuse.',
    submit: 'Soumettre la demande',
    mine_title: 'Mes demandes',
    col: {
      number: 'Numéro',
      justification: 'Justification',
      status: 'Statut',
      date: 'Date',
    },
    none: 'Aucune demande.',
    status: {
      PENDING: 'En attente d’envoi',
      SUBMITTED: 'Soumise',
      APPROVED: 'Approuvée',
      REJECTED: 'Refusée',
      PARTIALLY_ORDERED: 'Commandée en partie',
      ORDERED: 'Commandée',
      CANCELLED: 'Abandonnée',
      CLOSED: 'Close',
    },
    result: {
      WAITING_offline:
        'Demande enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Demande en cours d’envoi…',
      DONE: 'Demande soumise : {{doc}}.',
      WARNING: 'Demande {{doc}} soumise, avec un signalement.',
      CONFLICT: 'Demande mise en attente d’examen (conflit).',
      REJECTED: 'Demande refusée : {{reason}}',
    },
  },
  purchase_receipt: {
    offline_banner:
      'Hors ligne : la réception sera enregistrée sur ce téléphone et envoyée au retour du réseau ; l’accepté entre aussitôt au disponible.',
    orders_title: 'Bons de commande à recevoir',
    no_order:
      'Aucun bon de commande à recevoir sur vos emplacements (la réception sans bon de commande n’est pas encore proposée par l’application).',
    open: 'Réceptionner le bon de commande {{doc}}',
    expected: 'livraison prévue le {{date}}',
    order_status: {
      SENT: 'Envoyé',
      PARTIALLY_RECEIVED: 'Reçu en partie',
    },
    recent_title: 'Réceptions des 30 derniers jours',
    col: {
      order: 'Bon de commande',
      supplier: 'Fournisseur',
      expected: 'Livraison prévue',
      status: 'Statut',
      receipt: 'Réception',
      date: 'Date',
    },
    no_receipt: 'Aucune réception récente.',
    status: {
      POSTED: 'Comptabilisée',
      POSTED_PENDING_REVIEW: 'En revue',
      QUARANTINED: 'En quarantaine',
      REJECTED: 'Refusée',
      REVIEW_REJECTED: 'Revue refusée',
      CANCELLATION_PENDING: 'Annulation demandée',
      CANCELLED: 'Annulée',
    },
    supplier_unknown: 'Fournisseur',
    back: 'Bons de commande à recevoir',
    not_found:
      'Ce bon de commande n’est plus à recevoir (reçu, clôturé ou hors de vos emplacements).',
    order_title: 'Réceptionner {{doc}}',
    no_location: 'Aucun emplacement actif connu sur le site de livraison de ce bon de commande.',
    location_label: 'Reçu sur',
    location_placeholder: 'Choisir l’emplacement de réception',
    delivery_note_label: 'N° du bon de livraison du fournisseur',
    delivery_note_hint:
      'Recommandé : il permet au serveur de repérer une livraison saisie deux fois.',
    remaining: 'Reste à recevoir : {{quantity}} {{unit}}',
    delivered_label: 'Livré ({{unit}})',
    rejected_label: 'Rejeté ({{unit}})',
    accepted: 'Accepté : {{quantity}} {{unit}}',
    reason_label: 'Motif du rejet',
    reason_placeholder: 'Choisir un motif',
    no_reason:
      'Aucun motif de rejet n’est paramétré : un rejet est impossible, demandez-le à l’administrateur.',
    lot_label: 'Lot du fournisseur (facultatif)',
    expiry_label: 'Péremption (facultatif)',
    issue: {
      QUANTITY_INVALID: 'Quantité invalide (au plus 3 décimales).',
      REJECTED_OVER_DELIVERED: 'Le rejeté ne peut pas dépasser le livré.',
      REASON_REQUIRED: 'Un rejet se justifie par un motif.',
      EXPIRY_INVALID: 'Date de péremption invalide.',
    },
    over_online:
      'Accepté au-delà du reste à recevoir de {{quantity}} {{unit}} : le serveur refuserait la réception. Ne recevez que le reste.',
    over_offline:
      'Accepté au-delà du reste à recevoir de {{quantity}} {{unit}} : la réception sera mise en quarantaine pour validation et n’entrera pas au stock avant décision.',
    over_offline_apply:
      'Accepté au-delà du reste à recevoir de {{quantity}} {{unit}} : la réception sera appliquée, avec un signalement pour revue.',
    observations_label: 'Observations (facultatif)',
    observations_required_label: 'Observations (exigées par le motif de rejet)',
    duplicate_note:
      'Ce bon de livraison est déjà reçu pour ce fournisseur : la réception sera mise en quarantaine pour vérification et n’entrera pas au stock avant décision.',
    comment_required:
      'Un motif de rejet choisi exige une précision : décrivez-la dans les observations.',
    location_override_notice:
      'Cet emplacement appartient à un autre site que celui du bon de commande : la réception sera mise en quarantaine pour validation par les Achats ou la Direction (AV-096) et n’entrera pas au stock avant décision.',
    location_override_reason_required:
      'Un emplacement hors du site du bon de commande exige un motif dans les observations.',
    submit: 'Enregistrer la réception',
    result: {
      WAITING_offline:
        'Réception enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Réception en cours d’envoi…',
      DONE: 'Réception enregistrée : {{doc}}.',
      WARNING: 'Réception {{doc}} enregistrée, avec un signalement.',
      WARNING_RECEIPT_QUARANTINED: 'Réception {{doc}} mise en quarantaine pour validation.',
      WARNING_RECEIPT_PHOTO_MISSING:
        'Réception {{doc}} enregistrée sans photo du bon de livraison : elle est signalée aux Achats ; joignez la photo du bon dès que possible.',
      CONFLICT: 'Réception mise en attente d’examen (conflit).',
      REJECTED: 'Réception refusée : {{reason}}',
    },
  },
  orders_po: {
    online_only:
      'Écran en ligne seulement : les bons de commande se lisent et se gèrent sur le serveur. Reconnectez-vous pour continuer.',
    unavailable: 'Bons de commande indisponibles : réessayez quand le serveur répond.',
    denied: 'Lecture des bons de commande refusée : droit insuffisant.',
    refreshing: 'Relecture des bons après la dernière opération…',
    truncated:
      'Seuls les bons les plus récents sont affichés : filtrez par statut, ou affichez les plus anciens.',
    older: 'Afficher les bons plus anciens',
    none: 'Aucun bon de commande.',
    section_label: 'Rubrique',
    section: {
      LIST: 'Bons de commande',
      NEW: 'Nouveau bon de commande',
    },
    status_filter: 'Statut',
    all_statuses: 'Tous',
    col: {
      order: 'Bon de commande',
      supplier: 'Fournisseur et lieu',
      delivery: 'Livraison',
      total: 'Montant',
      status: 'Statut',
    },
    summary_total: 'Total du bon',
    status: {
      DRAFT: 'Brouillon',
      PENDING_APPROVAL: 'En validation',
      APPROVED: 'Approuvé',
      SENT: 'Envoyé',
      PARTIALLY_RECEIVED: 'Reçu en partie',
      RECEIVED: 'Reçu',
      CLOSED: 'Clôturé',
      CANCELLED: 'Annulé',
    },
    expected: 'livraison prévue le {{date}}',
    line_progress: 'commandé {{ordered}}, reste {{remaining}}',
    edit: 'Modifier',
    submit: 'Soumettre',
    mark_sent: 'Marquer envoyé',
    close_remaining: 'Clôturer le reliquat',
    cancel: 'Annuler le bon',
    expected_label: 'Livraison prévue (facultatif)',
    qty_base_label: 'Quantité ({{unit}})',
    price_label: 'Prix par {{unit}} (F)',
    cancel_line: 'Annuler cette ligne',
    line_legend: '{{name}} (ligne {{no}})',
    save: 'Enregistrer les changements',
    edit_issue: {
      LINE_INVALID:
        'Quantité positive (3 décimales au plus) et prix entier attendus sur chaque ligne.',
      ORDER_EMPTY: 'Le bon doit garder au moins une ligne.',
      DATE_INVALID: 'Date de livraison invalide.',
      OVER_REQUEST:
        'Une ligne tirée d’une demande d’achat dépasse ce qui reste à commander sur la demande.',
      REMAINING_UNKNOWN:
        'Reste à commander sur la demande pas encore lu : la hausse d’une ligne tirée d’une demande attend la lecture des demandes d’achat.',
      AMOUNT_TOO_LARGE: 'Montant du bon trop grand : vérifiez les quantités et les prix.',
    },
    cancel_reason_label: 'Motif (facultatif)',
    no_reason: 'Aucun',
    cancel_comment_label: 'Commentaire (obligatoire)',
    close_reason_label: 'Motif de la clôture (obligatoire)',
    cancel_confirm: 'Confirmer l’annulation',
    close_confirm: 'Confirmer la clôture',
    supplier_label: 'Fournisseur',
    location_label: 'Livré sur',
    from_request: 'Demande d’achat',
    remaining_request: 'Reste à commander sur la demande : {{quantity}} {{unit}}',
    remove: 'Retirer',
    remove_line: 'Retirer la ligne {{name}}',
    requests_loading: 'Lecture des demandes d’achat approuvées…',
    requests_unavailable: 'Demandes d’achat indisponibles : lignes directes seulement.',
    requests_stale_failed:
      'Relecture des demandes d’achat en échec : une ligne tirée d’une demande attend leur relecture.',
    requests_unavailable_list:
      'Demandes d’achat indisponibles : la hausse d’une ligne tirée d’une demande attend leur relecture.',
    suppliers_unavailable: 'Fournisseurs indisponibles : réessayez quand le serveur répond.',
    suppliers_denied: 'Lecture des fournisseurs refusée : droit insuffisant.',
    create_retrying:
      'Le bon envoyé est toujours réessayé automatiquement par le téléphone : la saisie reste pour le corriger s’il est refusé, mais ne le renvoyez pas tel quel, il serait créé deux fois.',
    requests_refreshing:
      'Relecture des demandes d’achat… une ligne tirée d’une demande attend sa fin.',
    request_line_label: 'Ligne d’une demande approuvée',
    request_line: '{{doc}} — {{product}} ({{quantity}} {{unit}} à commander)',
    add_request_line: 'Ajouter la ligne de demande',
    direct_product_label: 'Produit achetable',
    add_direct_line: 'Ajouter le produit',
    total: 'Total : {{total}}',
    approval_hint:
      'Au-delà de {{threshold}}, la soumission part en validation auprès de la Direction.',
    create: 'Créer le bon de commande',
    line_issue: {
      PRODUCT_REQUIRED: 'Choisissez le produit.',
      QUANTITY_INVALID: 'Quantité invalide (au plus 3 décimales).',
      OVER_REQUEST: 'Plus que le reste à commander de la demande.',
      PRICE_INVALID: 'Prix : nombre entier de francs.',
    },
    issue: {
      DATE_INVALID: 'Date de livraison invalide.',
      TOO_MANY_LINES: '200 lignes au plus.',
      DUPLICATE_REQUEST_LINE: 'Une ligne de demande ne figure qu’une fois.',
      AMOUNT_TOO_LARGE: 'Montant du bon trop grand : vérifiez les quantités et les prix.',
    },
    result: {
      WAITING_offline:
        'Opération enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Opération en cours d’envoi…',
      DONE: 'Opération enregistrée.',
      WARNING: 'Opération enregistrée, avec un signalement.',
      CONFLICT: 'Opération mise en attente d’examen (conflit).',
      REJECTED: 'Opération refusée : {{reason}}',
    },
    result_create: {
      WAITING_offline:
        'Bon de commande enregistré sur ce téléphone ; il sera envoyé au retour du réseau.',
      WAITING_online: 'Bon de commande en cours d’envoi…',
      DONE: 'Bon de commande {{doc}} créé.',
      WARNING: 'Bon de commande {{doc}} créé, avec un signalement.',
      CONFLICT: 'Bon de commande mis en attente d’examen (conflit).',
      REJECTED: 'Bon de commande refusé : {{reason}}. Corrigez la saisie et renvoyez-le.',
    },
  },
  suppliers: {
    online_only:
      'Écran en ligne seulement : les fournisseurs se lisent et se gèrent sur le serveur. Reconnectez-vous pour continuer.',
    unavailable: 'Fournisseurs indisponibles : réessayez quand le serveur répond.',
    denied: 'Lecture des fournisseurs refusée : droit insuffisant.',
    refreshing: 'Relecture des fournisseurs après la dernière opération…',
    col: {
      supplier: 'Fournisseur',
      contact: 'Contact',
      terms: 'Paiement',
      status: 'Statut',
    },
    none: 'Aucun fournisseur.',
    new: 'Nouveau fournisseur',
    new_title: 'Nouveau fournisseur',
    edit_title: 'Modifier {{name}}',
    edit: 'Modifier',
    edit_hint:
      'Seuls les champs changés et remplis sont envoyés : un champ ne s’efface pas. Adresse et note ne sont pas relues : laissées vides, elles ne changent pas.',
    deactivate: 'Désactiver',
    reactivate: 'Réactiver',
    create: 'Créer le fournisseur',
    save: 'Enregistrer les changements',
    terms: 'paiement à {{days}} jours',
    categories_label: 'Catégories fournies',
    field: {
      code: 'Code',
      name: 'Raison sociale',
      contactName: 'Contact (facultatif)',
      phone: 'Téléphone (facultatif)',
      email: 'Adresse électronique (facultatif)',
      address: 'Adresse (facultatif)',
      taxId: 'Numéro d’identifiant fiscal (facultatif)',
      paymentTermsText: 'Délai de paiement en jours (facultatif)',
      notes: 'Note (facultatif)',
    },
    status: {
      ACTIVE: 'Actif',
      INACTIVE: 'Inactif',
    },
    issue: {
      CODE_REQUIRED: 'Le code est obligatoire.',
      CODE_TAKEN: 'Ce code est déjà celui d’un autre fournisseur.',
      NAME_REQUIRED: 'La raison sociale est obligatoire.',
      EMAIL_INVALID: 'Adresse électronique invalide.',
      TERMS_INVALID: 'Délai de paiement : nombre entier de jours, de 0 à 3 650.',
    },
    result: {
      WAITING_offline:
        'Opération enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Opération en cours d’envoi…',
      DONE: 'Opération enregistrée.',
      WARNING: 'Opération enregistrée, avec un signalement.',
      CONFLICT: 'Opération mise en attente d’examen (conflit).',
      REJECTED: 'Opération refusée : {{reason}}',
    },
  },
  targets: {
    online_only:
      'Écran en ligne seulement : les objectifs se lisent et se définissent sur le serveur. Reconnectez-vous pour continuer.',
    unavailable: 'Objectifs indisponibles : réessayez quand le serveur répond.',
    denied: 'Lecture des objectifs refusée : droit insuffisant.',
    refreshing: 'Relecture des objectifs après la dernière opération…',
    none: 'Aucun objectif.',
    sites_only:
      'Commerciaux et équipes ne sont pas encore publiés au téléphone : seuls les objectifs par site se définissent ici.',
    no_target_for_role:
      'Vous fixez les objectifs des équipes que vous dirigez et de leurs membres : ils ne sont pas encore publiés au téléphone, aucun objectif ne peut être défini ici pour l’instant.',
    roles_unknown:
      'Rôles de l’utilisateur pas encore connus du téléphone : reconnectez-vous en ligne pour définir un objectif.',
    overlap_pending:
      'Lecture des objectifs existants en cours : le chevauchement sera contrôlé ensuite.',
    new: 'Nouvel objectif',
    new_title: 'Nouvel objectif',
    type_label: 'Objectif de',
    type: {
      USER: 'Un commercial',
      TEAM: 'Une équipe',
      SITE: 'Un site',
    },
    target_label: {
      USER: 'Commercial',
      TEAM: 'Équipe',
      SITE: 'Site',
    },
    metric_label: 'Mesure',
    product_label: 'Produit',
    start_label: 'Du',
    end_label: 'Au',
    value_label: {
      CA: 'Chiffre d’affaires visé (F)',
      QTE_PRODUIT: 'Quantité visée',
      NOUVEAUX_CLIENTS: 'Nouveaux clients visés',
      VISITES: 'Visites visées',
      PROSPECTS_CREES: 'Prospects créés visés',
    },
    value_label_unit: 'Quantité visée (en {{unit}}, unité de base du produit)',
    save: 'Définir l’objectif',
    cancel: 'Annuler l’objectif',
    cancel_comment_label: 'Commentaire (facultatif)',
    cancel_confirm: 'Confirmer l’annulation',
    status: {
      ACTIVE: 'Actif',
      CANCELLED: 'Annulé',
    },
    issue: {
      TARGET_REQUIRED: 'Choisissez la cible.',
      PRODUCT_REQUIRED: 'Choisissez le produit.',
      PERIOD_INVALID: 'La fin de la période doit suivre son début.',
      VALUE_INVALID: 'Valeur : nombre entier positif (trois décimales pour une quantité).',
      OVERLAP:
        'Un objectif actif de même cible, mesure et produit couvre déjà cette période : annulez-le d’abord.',
    },
    result: {
      WAITING_offline:
        'Opération enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Opération en cours d’envoi…',
      DONE: 'Opération enregistrée.',
      WARNING: 'Opération enregistrée, avec un signalement.',
      CONFLICT: 'Opération mise en attente d’examen (conflit).',
      REJECTED: 'Opération refusée : {{reason}}',
    },
  },
  lot_sheet: {
    no_lot: 'Aucun lot de production sur vos fermes.',
    lot_label: 'Lot',
    requested_lot: 'Lot demandé',
    closed_group: 'Lots clôturés',
    closed_unavailable: 'Lots clôturés indisponibles : réessayez quand le serveur répond.',
    more_closed: 'Afficher plus de lots clôturés',
    more_failed: 'Lots clôturés suivants indisponibles : réessayez quand le serveur répond.',
    closed_on: 'clôturé le {{date}}',
    not_on_device:
      'Hors ligne : ce lot n’est pas sur le téléphone (lot clôturé ou hors de vos fermes) ; sa fiche se lit en ligne.',
    not_found: 'Lot introuvable ou hors de votre périmètre.',
    started: 'démarré le {{date}}',
    status: {
      PLANNED: 'Planifié',
      ACTIVE: 'En élevage',
      SELLING: 'En vente',
      CLOSED: 'Clôturé',
      CANCELLED: 'Annulé',
    },
    offline:
      'Hors ligne : effectif calculé par ce téléphone ; coûts, ventes, marges et indicateurs se lisent en ligne.',
    unavailable: 'Fiche indisponible : réessayez quand le serveur répond.',
    heads_title: 'Effectif',
    initial: 'Mis en place',
    unsold: 'Non vendu',
    rearing: 'En élevage',
    rearing_device: 'En élevage (estimation du téléphone)',
    deaths: 'Morts comptés',
    deaths_pending: 'Morts en attente de validation',
    lost: 'Pertes (mortalité comprise)',
    indicators_title: 'Indicateurs',
    mortality_rate: 'Taux de mortalité',
    adg: 'Gain moyen quotidien',
    grams_per_day: '{{value}} g/jour',
    fcr: 'Indice de consommation',
    laying_rate: 'Taux de ponte',
    eggs: 'Œufs collectés',
    costs_title: 'Coûts',
    amounts_hidden: 'Montants réservés aux rôles qui voient les valorisations.',
    cost_type: {
      ANIMAUX: 'Animaux',
      OEUFS: 'Œufs',
      ALIMENT: 'Aliment',
      VETERINAIRE: 'Vétérinaire',
      AUTRE_INTRANT: 'Autres intrants',
      DEPENSE_DIRECTE: 'Dépenses directes',
      AJUSTEMENT: 'Ajustements',
      FRAIS_GENERAUX: 'Frais généraux',
      PRODUCTION_TRANSFEREE: 'Production transférée',
    },
    cost_total: 'Coût du lot',
    cost_remaining: 'Coût restant',
    cost_per_head: 'Coût par tête',
    sales_title: 'Ventes et marges',
    sold: 'Vendus',
    revenue: 'Chiffre d’affaires net',
    cost_of_sales: 'Coût des ventes',
    lot_margin: 'Marge du lot',
    gross_margin: 'Marge brute des ventes',
    monthly_title: 'Résultat mensuel',
    month: 'coûts {{cost}} · ventes {{revenue}}',
    entries_title: 'Entrées',
    none: 'Aucune.',
    cancelled: 'annulée',
    weighings_title: 'Pesées',
    weighing: '{{weight}} g en moyenne ({{sample}} sujets)',
  },
  incubation: {
    offline_banner:
      'Hors ligne : les étapes seront enregistrées sur ce téléphone et envoyées au retour du réseau ; un lot démarré ici reçoit son numéro du serveur.',
    section_label: 'Rubrique',
    section: {
      BATCHES: 'Lots en cours',
      START: 'Démarrer un lot',
    },
    none: 'Aucun lot d’incubation en cours sur vos fermes.',
    done_title: 'Lots terminés',
    pending_batch: 'Lot démarré sur ce téléphone (numéro à venir)',
    pending_steps: 'étapes en attente d’envoi',
    status: {
      INCUBATING: 'En incubateur',
      IN_HATCHER: 'En éclosoir',
      CLOSED: 'Éclos',
      CANCELLED: 'Perdu',
    },
    counters:
      'Mis à couver {{set}} · retirés au mirage {{removed}} · pertes {{lost}} · restants {{remaining}}',
    schedule: 'Mirage le {{candling}} · transfert le {{transfer}} · éclosion le {{hatch}}',
    candling: 'Mirage',
    transfer: 'Transférer vers l’éclosoir',
    hatch: 'Éclosion',
    infertile_label: 'Infertiles',
    early_dead_label: 'Mortalité embryonnaire',
    save_candling: 'Enregistrer le mirage',
    no_hatcher: 'Aucun éclosoir actif sur la ferme du lot.',
    hatcher_label: 'Éclosoir',
    transfer_hint: 'Tous les œufs restants ({{remaining}}) passent dans l’éclosoir.',
    save_transfer: 'Enregistrer le transfert',
    viable_label: 'Poussins viables',
    nonviable_label: 'Poussins non viables',
    unhatched_label: 'Non éclos',
    output_label: 'Poussins placés sur',
    output_default: 'L’éclosoir ({{name}})',
    hatch_hint: 'Viables + non viables + non éclos = {{remaining}} œufs restants.',
    candling_over:
      'Mirage : {{counted}} œufs retirés, mais il n’en reste que {{remaining}} dans l’incubateur.',
    hatch_unbalanced:
      'Éclosion : {{counted}} œufs comptés pour {{remaining}} restants ; le total doit être égal.',
    stock_gap:
      'Écart de stock à confirmer : {{gap}} œufs de moins en stock que le restant annoncé (perte déclarée ?). L’éclosion se contrôle sur le stock.',
    stock_stale:
      'Plus aucun œuf de ce lot en stock alors que le lot en annonce : données à resynchroniser avant l’éclosion.',
    other_author_waiting:
      'Une étape saisie par un autre utilisateur de ce téléphone attend sa synchronisation : l’éclosion attend.',
    pending_loss_hold: 'Éclosion suspendue jusqu’à la confirmation de la perte en attente.',
    all_lost:
      'Plus aucun œuf de ce lot en stock, ni poussin : perte totale probable (sauf si une perte attend une validation). Le lot se clôt par annulation, pas par une éclosion ; mirage et transfert sont suspendus.',
    cancel_no_longer:
      'Le lot n’est plus reconnu en perte totale (stock relu) : l’annulation n’est plus proposée.',
    cancel_open: 'Clore par annulation (perte totale)',
    cancel_hint:
      'Le serveur vérifie que plus aucun œuf du lot n’est en stock ni en attente de validation d’une perte.',
    cancel_comment_label: 'Motif de l’annulation',
    cancel_online_only:
      'L’annulation se fait en ligne (le serveur vérifie que le stock du lot est vide).',
    save_cancel: 'Confirmer l’annulation',
    pending_loss:
      'Une perte de ce lot saisie sur ce téléphone n’est pas encore confirmée : si elle attend une validation, le serveur refusera l’éclosion jusqu’à la décision.',
    save_hatch: 'Enregistrer l’éclosion',
    count_invalid: 'Nombre entier attendu.',
    no_incubator: 'Aucune ferme avec un incubateur actif.',
    species_label: 'Espèce',
    species_not_configured:
      'Durées d’incubation non servies au téléphone : saisissez le code de l’espèce paramétrée, en majuscules (par exemple POULE) ; le serveur refusera en ligne une espèce inconnue.',
    egg_product: 'Œufs à couver : {{name}}',
    egg_label: 'Œufs à couver',
    chick_label: 'Poussins produits',
    incubator_label: 'Incubateur',
    no_eggs: 'Aucun œuf à couver en stock sur la ferme (hors incubateurs et lots déjà engagés).',
    source_label: 'Œufs pris sur',
    source: '{{place}} ({{quantity}})',
    egg_lot_label: 'Lot d’œufs',
    egg_lot_required:
      'Cette salle contient aussi des œufs déjà engagés dans un lot : désignez le lot d’œufs.',
    egg_source_label: 'Origine des œufs',
    egg_source: {
      INTERNAL: 'Collecte de la ferme',
      PURCHASED: 'Achetés',
    },
    eggs_label: 'Œufs mis à couver',
    over_online:
      'Plus d’œufs que le stock de l’emplacement : le serveur refuserait le démarrage. Vérifiez le nombre.',
    over_offline:
      'Plus d’œufs que le stock connu du téléphone : le démarrage sera tout de même enregistré.',
    start: 'Démarrer l’incubation',
    result: {
      WAITING_offline:
        'Saisie enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Saisie en cours d’envoi…',
      DONE: 'Saisie enregistrée {{doc}}.',
      WARNING: 'Saisie enregistrée {{doc}}, avec un signalement.',
      CONFLICT: 'Saisie mise en attente d’examen par le responsable de production (conflit).',
      REJECTED: 'Saisie refusée : {{reason}}',
    },
  },
  lot_setup: {
    offline_banner:
      'Hors ligne : le lot et les entrées seront enregistrés sur ce téléphone et envoyés au retour du réseau ; le numéro du lot sera donné par le serveur.',
    section_label: 'Que faire ?',
    section: {
      CREATE: 'Nouveau lot',
      ENTRY: 'Entrée dans un lot',
    },
    choose: 'Choisir…',
    no_farm: 'Aucune ferme connue : synchronisez avant de créer un lot.',
    farm_label: 'Ferme',
    type_label: 'Type de lot',
    product_label: 'Animaux du lot',
    no_product:
      'Aucun produit biologique actif, suivi par lot, de l’espèce de ce type de lot au catalogue.',
    location_label: 'Bâtiment ou case principal',
    parent_label: 'Lot lié (truies, bande ; facultatif)',
    no_parent: 'Aucun',
    supplier_label: 'Fournisseur (facultatif)',
    no_supplier: 'Aucun',
    strain_label: 'Souche (facultatif)',
    start_label: 'Démarrage prévu (facultatif)',
    end_label: 'Fin prévue (facultatif)',
    notes_label: 'Note (facultatif)',
    more_title: 'Autres informations (facultatif)',
    create: 'Créer le lot',
    no_entry_lot: 'Aucun lot planifié, actif ou en vente sur vos fermes.',
    entry_lot_label: 'Lot',
    pending_lot: 'Nouveau lot {{type}} (numéro à venir)',
    pending_lot_hint:
      'Lot créé sur ce téléphone : l’entrée partira après sa création, dans le même envoi.',
    kind_label: 'Origine des animaux',
    kind: {
      INTERNAL_STOCK: 'Depuis le stock de la ferme',
      PURCHASE: 'Depuis une réception d’achat',
      TRANSFER: 'Depuis un autre lot',
      WEANING: 'Sevrage (porcelets du naissage)',
      BIRTH: 'Naissance',
    },
    no_stock_source:
      'Aucun animal de cette espèce en stock sur la ferme (hors lots de production).',
    stock_source_label: 'Animaux en stock',
    stock_source: '{{product}} — {{place}} ({{quantity}})',
    stock_lot_label: 'Lot de stock d’origine',
    fifo: 'Le plus ancien d’abord',
    lot_fifo_risk:
      'Des sorties sans lot désigné attendent l’envoi : le serveur peut les prendre sur ce lot (le plus ancien d’abord) et le trouver insuffisant.',
    stock_lot_required:
      'Ce stock contient aussi des animaux d’un lot de production : désignez le lot de stock d’origine.',
    no_receipt:
      'Aucune réception comptabilisée d’animaux de cette espèce encore à mettre en place sur la ferme.',
    receipt_label: 'Réception d’achat',
    receipt_product_label: 'Animaux reçus',
    source_lot_label: 'Lot d’origine',
    source_location_label: 'Bâtiment ou case d’origine',
    source_location: '{{name}} ({{heads}})',
    no_source_heads:
      'Ce lot n’a aucune tête connue du téléphone dans un bâtiment ou une case de la ferme.',
    parent_inactive_offline:
      'Le lot de truies n’est pas actif : la naissance sera enregistrée et signalée en conflit à la synchronisation.',
    to_label: 'Vers le bâtiment ou la case',
    main_location: 'Bâtiment principal du lot ({{name}})',
    quantity_label: 'Nombre de têtes',
    weight_label: 'Poids moyen (g, facultatif)',
    stillborn_label: 'Mort-nés (facultatif)',
    available: 'Disponibles à l’origine : {{quantity}}',
    over_online:
      'Plus de têtes que l’origine n’en a : le serveur refuserait l’entrée. Vérifiez le nombre.',
    over_offline:
      'Plus de têtes que l’origine connue du téléphone : l’entrée sera tout de même enregistrée.',
    record_entry: 'Enregistrer l’entrée',
    issue: {
      DATES_INVALID: 'La fin prévue doit suivre le démarrage prévu.',
      QUANTITY_INVALID: 'Nombre de têtes : entier supérieur à 0.',
      STILLBORN_INVALID: 'Mort-nés : nombre entier.',
      WEIGHT_INVALID: 'Poids moyen : nombre positif, une décimale au plus.',
    },
    create_result: {
      WAITING_offline: 'Lot enregistré sur ce téléphone ; il sera envoyé au retour du réseau.',
      WAITING_online: 'Lot en cours d’envoi…',
      DONE: 'Lot créé : {{doc}}.',
      WARNING: 'Lot {{doc}} créé, avec un signalement.',
      CONFLICT: 'Création du lot mise en attente d’examen (conflit).',
      REJECTED: 'Création du lot refusée : {{reason}}',
    },
    entry_result: {
      WAITING_offline:
        'Entrée enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Entrée en cours d’envoi…',
      DONE: 'Entrée enregistrée.',
      WARNING: 'Entrée enregistrée, avec un signalement.',
      CONFLICT: 'Entrée mise en attente d’examen (conflit).',
      REJECTED: 'Entrée refusée : {{reason}}',
    },
  },
  output: {
    back: 'Saisie du jour {{code}}',
    offline_banner:
      'Hors ligne : la sortie sera enregistrée sur ce téléphone et envoyée au retour du réseau ; les têtes quittent aussitôt l’effectif affiché.',
    no_lot: 'Aucun lot actif ou en vente sur vos fermes.',
    lot_label: 'Lot',
    lot_status: {
      ACTIVE: 'En élevage',
      SELLING: 'Prêt à la vente depuis l’élevage',
    },
    status_title: 'Vente depuis l’élevage',
    status_hint:
      'Un lot prêt à la vente se vend directement depuis son bâtiment ; une sortie vers un point de vente est possible dès l’élevage.',
    status_online_only: 'Changement de statut en ligne seulement.',
    ready_to_sell: 'Déclarer prêt à la vente',
    back_to_rearing: 'Revenir en élevage',
    dispatch_title: 'Sortir des têtes vers un point de vente',
    source_label: 'Depuis le bâtiment ou la case',
    destination_label: 'Vers',
    destination_placeholder: 'Choisir le magasin, le PDV ou le commercial',
    no_destination: 'Aucun emplacement commercial connu : synchronisez avant de saisir une sortie.',
    quantity_label: 'Nombre de têtes ({{unit}})',
    over_online:
      'Plus de têtes que l’effectif de cet emplacement : le serveur refuserait la sortie. Vérifiez le nombre ou l’emplacement.',
    over_offline:
      'Plus de têtes que l’effectif connu du téléphone : la sortie sera tout de même enregistrée et signalée.',
    carrier_label: 'Transporteur (facultatif)',
    notes_label: 'Note (facultatif)',
    receive_hint: 'Le destinataire confirme l’arrivée (« Recevoir un transfert »).',
    submit: 'Enregistrer la sortie',
    result: {
      WAITING_offline:
        'Sortie enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Sortie en cours d’envoi…',
      DONE: 'Sortie enregistrée : les têtes sont en transit vers le point de vente.',
      WARNING: 'Sortie enregistrée, avec un signalement.',
      CONFLICT: 'Sortie mise en attente d’examen (conflit).',
      REJECTED: 'Sortie refusée : {{reason}}',
    },
    status_result: {
      WAITING_offline: 'Statut enregistré sur ce téléphone ; il sera envoyé au retour du réseau.',
      WAITING_online: 'Changement de statut en cours d’envoi…',
      DONE: 'Statut du lot mis à jour.',
      WARNING: 'Statut du lot mis à jour, avec un signalement.',
      CONFLICT: 'Changement de statut mis en attente d’examen (conflit).',
      REJECTED: 'Changement de statut refusé : {{reason}}',
    },
  },
  ledger: {
    online_only:
      'Écran en ligne seulement : le registre se lit sur le serveur. Reconnectez-vous pour continuer.',
    location_label: 'Emplacement',
    product_label: 'Produit',
    all_products: 'Tous les produits',
    from_label: 'Du',
    to_label: 'Au',
    moves_list_label: 'Mouvements de stock',
    col_move: 'Mouvement',
    col_quantity: 'Quantité',
    col_when: 'Date',
    col_value: 'Valeur',
    issue: {
      DATE_INVALID: 'Dates invalides.',
      ORDER: 'La fin de la période doit suivre son début.',
      FUTURE: 'La période ne peut pas finir après aujourd’hui.',
      TOO_LONG: 'Période trop longue : un an au plus.',
    },
    explain_title: 'Explication de l’écart',
    opening: 'Solde au début',
    entries: 'Entrées',
    exits: 'Sorties',
    closing: 'Solde à la fin',
    explained: 'Les mouvements listés expliquent tout l’écart.',
    unexplained:
      'Écart non expliqué : {{quantity}} {{unit}}. Des opérations ont probablement été enregistrées pendant la lecture (synchronisation en cours) : actualisez.',
    refresh: 'Actualiser',
    retry: 'Réessayer',
    out_of_scope: 'Emplacement hors de votre périmètre de registre : choisissez-en un autre.',
    explain_unavailable: 'Explication indisponible tant que les mouvements ne sont pas lus.',
    counterpart: {
      customer: 'client',
      transit: 'transit',
      loss: 'pertes',
      supplier: 'fournisseur',
      opening: 'stock d’ouverture',
      adjustment: 'ajustement d’inventaire',
      consumption: 'consommation',
      production: 'production',
    },
    explain_incomplete: 'Affichez toute la liste pour totaliser les mouvements.',
    balance_unavailable: 'Soldes indisponibles : réessayez quand le serveur répond.',
    moves_title: 'Mouvements',
    none: 'Aucun mouvement sur la période.',
    unavailable: 'Registre indisponible : réessayez quand le serveur répond.',
    more: 'Afficher la suite',
    from_place: 'depuis {{name}}',
    to_place: 'vers {{name}}',
    unknown_place: 'emplacement hors de votre périmètre',
    reversal: 'contre-écriture',
    offline: 'saisi hors ligne',
    move_type: {
      OPENING_BALANCE: 'Stock d’ouverture',
      PURCHASE_RECEIPT: 'Réception d’achat',
      SUPPLIER_RETURN: 'Retour fournisseur',
      TRANSFER_DISPATCH: 'Expédition de transfert',
      TRANSFER_RECEIPT: 'Réception de transfert',
      TRANSFER_DISCREPANCY: 'Écart de transfert',
      INTERNAL_MOVE: 'Mouvement interne',
      SALE: 'Vente',
      CUSTOMER_RETURN: 'Retour client',
      DELIVERY: 'Livraison',
      LOSS: 'Perte',
      LOSS_PENDING: 'Perte en attente de validation',
      LOSS_CONFIRMATION: 'Perte confirmée',
      LOSS_RELEASE: 'Perte levée',
      CONSUMPTION: 'Consommation',
      CONSUMPTION_REVERSAL: 'Consommation annulée',
      PRODUCTION_OUTPUT: 'Sortie de production',
      PRODUCTION_INPUT: 'Entrée en production',
      INVENTORY_GAIN: 'Gain d’inventaire',
      INVENTORY_LOSS: 'Perte d’inventaire',
    },
  },
  catalog: {
    online_only:
      'Écran en ligne seulement : le catalogue se lit et se gère sur le serveur. Reconnectez-vous pour continuer.',
    unavailable: 'Catalogue indisponible : réessayez quand le serveur répond.',
    refreshing: 'Relecture du catalogue après la dernière opération…',
    col: {
      product: 'Produit',
      family: 'Famille',
      unit: 'Unité',
      status: 'Statut',
      reason: 'Motif',
      comment: 'Commentaire',
    },
    section_label: 'Rubrique',
    section: {
      PRODUCTS: 'Produits',
      UNITS: 'Unités',
      CATEGORIES: 'Catégories',
      PACKAGING: 'Conditionnements',
      REASONS: 'Motifs',
    },
    none: 'Aucun élément.',
    choose: 'Choisir…',
    code_label: 'Code',
    name_label: 'Libellé',
    label_label: 'Libellé',
    count_unit: 'Comptage',
    measure_unit: 'Mesure',
    is_count_label: 'Unité de comptage (pièces, têtes, plateaux…)',
    new_unit: 'Nouvelle unité',
    create_unit: 'Créer l’unité',
    new_category: 'Nouvelle catégorie',
    parent_label: 'Catégorie parente',
    no_parent: 'Aucune (catégorie racine)',
    create_category: 'Créer la catégorie',
    new_product: 'Nouveau produit',
    create_product: 'Créer le produit',
    category_label: 'Catégorie',
    family_label: 'Famille de stock',
    base_unit_label: 'Unité de base',
    lot_label: 'Suivi par lot',
    pricing_mode_label: 'Mode de prix',
    species_label: 'Espèce',
    flags_label: 'Usages',
    immutable_hint:
      'L’unité de base, la famille et le suivi par lot ne se modifient plus après la création : vérifiez-les avant d’enregistrer.',
    pending: 'En attente d’envoi',
    created_pending_pull: 'Créée, arrive à la prochaine synchronisation',
    family: {
      BIOLOGIQUE: 'Biologique (animaux)',
      PRODUCTION_COMMERCIALISABLE: 'Production commercialisable',
      INTRANT: 'Intrant',
      MARCHANDISE: 'Marchandise',
      EMBALLAGE_CONSOMMABLE: 'Emballage, consommable',
      SERVICE: 'Service',
    },
    lot: {
      NONE: 'Aucun',
      OPTIONAL: 'Facultatif',
      REQUIRED: 'Obligatoire',
    },
    pricing_mode: {
      PER_UNIT: 'À l’unité',
      PER_WEIGHT: 'Au poids',
    },
    species: {
      POULET_CHAIR: 'Poulet de chair',
      PONDEUSE: 'Pondeuse',
      PORC: 'Porc',
    },
    flag: {
      isSellable: 'Vendable',
      isPurchasable: 'Achetable',
      isProducible: 'Produit par le GIC',
      isConsumable: 'Consommable (intrant)',
      expiryTracking: 'Suivi de la date de péremption',
    },
    status: {
      ACTIVE: 'Actif',
      INACTIVE: 'Inactif',
    },
    deactivate_short: 'Désactiver',
    reactivate_short: 'Réactiver',
    deactivate: 'Désactiver {{name}}',
    reactivate: 'Réactiver {{name}}',
    product_label: 'Produit',
    unit_label: 'Unité',
    factor: '= {{factor}} {{unit}}',
    factor_label: 'Équivaut à (en {{unit}})',
    new_packaging: 'Nouveau conditionnement',
    packaging_use_label: 'Utilisé pour',
    packaging_flag: {
      isSalesUnit: 'La vente',
      isPurchaseUnit: 'L’achat',
      isCountUnit: 'L’inventaire',
    },
    packaging_immutable:
      'Un conditionnement ne se modifie plus : un facteur erroné se corrige par un nouveau conditionnement.',
    create_packaging: 'Ajouter le conditionnement',
    reason_category_label: 'Catégorie de motif',
    reason_category: {
      LOSS: 'Perte',
      REJECTION: 'Rejet à la réception',
      INVENTORY_ADJUSTMENT: 'Ajustement d’inventaire',
      CANCELLATION: 'Annulation',
      PRICE_OVERRIDE: 'Dérogation de prix',
      VISIT_OUTCOME: 'Issue de visite',
      PROSPECT_LOST: 'Prospect perdu',
      CHECKIN_OVERRIDE: 'Dérogation de pointage',
      PRODUCTION_YIELD: 'Rendement de production',
      CASH_VARIANCE: 'Écart de caisse',
      TRANSFER_DISCREPANCY: 'Écart de transfert',
    },
    loss_category_label: 'Catégorie de perte',
    any_loss: 'Toutes',
    requires_comment_label: 'Commentaire obligatoire',
    requires_comment_short: 'Commentaire exigé',
    new_reason: 'Nouveau motif',
    create_reason: 'Enregistrer le motif',
    edit_short: 'Modifier',
    edit_reason: 'Modifier le motif {{name}}',
    save_reason: 'Enregistrer le motif {{name}}',
    issue: {
      CODE_INVALID: 'Code : lettres majuscules, chiffres, tiret ou souligné, sans espace.',
      CODE_TAKEN: 'Ce code existe déjà.',
      NAME_REQUIRED: 'Le libellé est obligatoire.',
      CATEGORY_REQUIRED: 'Choisissez une catégorie.',
      UNIT_REQUIRED: 'Choisissez une unité (autre que l’unité de base pour un conditionnement).',
      SPECIES_REQUIRED: 'Un produit biologique exige une espèce.',
      SERVICE_LOT: 'Un service ne se suit pas par lot.',
      BIO_LOT:
        'Un produit biologique se suit par lot : sans suivi, il ne pourrait entrer dans aucun lot de production.',
      FACTOR_INVALID: 'Facteur : nombre positif, 8 chiffres avant la virgule et 6 après au plus.',
      FACTOR_NOT_WHOLE: 'L’unité de base se compte : le facteur est un nombre entier.',
      PACKAGING_EXISTS: 'Ce conditionnement existe déjà pour ce produit.',
    },
    result: {
      WAITING_offline:
        'Opération enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Opération en cours d’envoi…',
      DONE: 'Opération enregistrée.',
      WARNING: 'Opération enregistrée, avec un signalement.',
      CONFLICT: 'Opération mise en attente d’examen (conflit).',
      REJECTED: 'Opération refusée : {{reason}}',
    },
  },
  price_rules: {
    online_only: 'Écran en ligne seulement : les règles se lisent et se gèrent sur le serveur.',
    loading: 'Lecture des règles…',
    col: {
      price: 'Prix',
      scope: 'Portée',
      period: 'Période',
      rule: 'Règle',
    },
    unavailable: 'Règles indisponibles : réessayez quand le serveur répond.',
    new_rule: 'Nouvelle règle',
    active_title: 'En vigueur ou à venir',
    drafts_title: 'Brouillons',
    history_title: 'Historique',
    none: 'Aucune.',
    price: '{{price}} par {{unit}}',
    period_open: 'Depuis le {{from}}',
    period_bounded: 'Du {{from}} au {{to}}',
    supersede: 'Nouvelle version',
    end: 'Terminer',
    end_label: 'Fin de validité (heure de Douala)',
    end_confirm: 'Avancer la fin',
    activate: 'Activer',
    activation_by_direction: 'Activation par la Direction.',
    activation_hint:
      'La Direction active la règle au plus tard 5 minutes après sa date d’effet : prévoyez une date qui lui laisse le temps.',
    codes_loading: 'Vérification des codes existants…',
    codes_unavailable:
      'Codes existants indisponibles : réessayez quand le serveur répond (un code déjà pris bloquerait l’envoi).',
    campaign_kept:
      'Règle de campagne commerciale : la nouvelle version reste rattachée à la même campagne.',
    end_invalid:
      'La fin doit être à venir, après la date d’effet de la règle et avant sa fin actuelle.',
    resume: 'Reprendre la saisie',
    refreshing: 'Relecture des règles après la dernière opération…',
    superseded_changed:
      'La règle remplacée a changé depuis l’ouverture (terminée, déjà remplacée ou d’un autre produit) : annulez et recommencez.',
    inactive_option: '{{name}} (inactive)',
    unknown_option: '{{name}} (hors du référentiel du téléphone)',
    end_before_successor:
      'Cette fin précède la date d’effet du brouillon v{{version}} : il ne pourrait plus être activé et resterait à vie.',
    end_confirm_successor: 'Je confirme cette fin malgré le brouillon de la version suivante',
    product_read_only: '{{name}} (consultation seulement)',
    read_only_hint:
      'Produit désactivé ou non vendable : ses règles se consultent et se terminent, sans nouvelle rédaction.',
    newer_version_exists: 'Une version plus récente existe déjà (brouillon compris).',
    supersede_pending: 'Une nouvelle version de cette règle attend l’envoi.',
    supersede_retrying:
      'Une nouvelle version de cette règle, saisie sur ce téléphone, est renvoyée sans succès au serveur ; elle reste réessayée.',
    supersede_pending_other:
      'Une nouvelle version de cette règle, saisie par un autre utilisateur de ce téléphone, attend son envoi (à sa reconnexion).',
    supersede_blocked:
      'Votre nouvelle version de cette règle, en attente, ne pourra pas être enregistrée.',
    outside_superseded:
      'Date d’effet hors de la période de la règle remplacée : l’activation échouerait. Rédigez une nouvelle version.',
    retroactive:
      'Date d’effet passée de plus de 5 minutes : la règle ne peut plus être activée ; rédigez-en une nouvelle.',
    conflict_warning_one:
      'En conflit avec {{count}} règle active (même priorité, même précision, périodes qui se chevauchent).',
    conflict_warning_other:
      'En conflit avec {{count}} règles actives (même priorité, même précision, périodes qui se chevauchent).',
    draft_title: 'Nouvelle règle (brouillon)',
    supersede_title: 'Nouvelle version de {{code}} v{{version}}',
    code_label: 'Code de la règle',
    price_label: 'Prix par {{unit}} (F)',
    min_quantity_label: 'À partir de (en {{unit}}, facultatif)',
    priority_label: 'Priorité',
    valid_from_label: 'Date d’effet (heure de Douala)',
    valid_to_label: 'Fin (facultatif)',
    notes_label: 'Note (facultatif)',
    any: 'Tous',
    issue: {
      CODE_REQUIRED: 'Le code de la règle est obligatoire.',
      CODE_TAKEN: 'Ce code est déjà celui d’une autre règle.',
      PRICE_INVALID: 'Prix : nombre entier de francs supérieur à 0.',
      MIN_QUANTITY_INVALID:
        'Seuil de quantité : nombre positif, 3 décimales au plus, 99 999 999 999 au plus.',
      PRIORITY_INVALID: 'Priorité : nombre entier entre −2 147 483 648 et 2 147 483 647.',
      VALID_FROM_PAST:
        'La date d’effet doit être à venir (une date passée ne pourrait être activée).',
      OUTSIDE_SUPERSEDED:
        'La date d’effet doit suivre le début de la règle remplacée et ne pas dépasser sa fin.',
      SITE_NOT_IN_ZONE: 'Le site choisi n’appartient pas à la zone choisie.',
      VALID_FROM_INVALID: 'Date d’effet invalide.',
      VALID_TO_INVALID: 'La fin doit suivre la date d’effet.',
    },
    submit_draft: 'Enregistrer le brouillon',
    submit_supersede: 'Enregistrer la nouvelle version',
    cancel: 'Annuler',
    result: {
      WAITING_offline:
        'Opération enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Opération en cours d’envoi…',
      DONE: 'Opération enregistrée.',
      WARNING: 'Opération enregistrée, avec un signalement.',
      CONFLICT: 'Opération mise en attente d’examen (conflit).',
      REJECTED: 'Opération refusée : {{reason}}',
    },
  },
  price_sim: {
    hint: 'Quel prix la vente appliquerait-elle ? Calcul sur ce téléphone avec les règles téléchargées.',
    product_label: 'Produit',
    quantity_label: 'Quantité ({{unit}})',
    day_label: 'Date',
    site_label: 'Site',
    zone_label: 'Zone',
    category_label: 'Catégorie de client',
    channel_label: 'Canal',
    any: 'Indifférent',
    channel: {
      POINT_DE_VENTE: 'Point de vente',
      TERRAIN: 'Terrain',
      SEDENTAIRE: 'Sédentaire',
      DIRECT: 'Direct',
    },
    result_title: 'Prix appliqué',
    price: '{{price}} par {{unit}}',
    total: 'Pour {{quantity}} {{unit}} : {{total}}',
    total_too_large: 'Montant trop grand pour être calculé : réduisez la quantité.',
    quantity_too_large: 'Quantité trop grande : {{max}} au plus.',
    at_now: 'Prix à l’instant (règles en vigueur maintenant).',
    at_noon: 'Prix à 12 h, heure de Douala, ce jour-là.',
    rule: 'Règle retenue : {{scope}} — en vigueur depuis le {{from}}.',
    others_title: 'Autres règles applicables (ordre du départage)',
    none: 'Aucune règle ne s’applique : la vente demanderait un prix saisi, soumis à dérogation.',
    scope: {
      site: 'site {{name}}',
      zone: 'zone {{name}}',
      category: 'catégorie {{name}}',
      channel: 'canal {{name}}',
      min_quantity: 'dès {{quantity}} {{unit}}',
      campaign: 'campagne commerciale',
      priority: 'priorité {{priority}}',
      general: 'règle générale',
    },
  },
  consumption: {
    offline_banner:
      'Hors ligne : la consommation sera enregistrée sur ce téléphone et envoyée au retour du réseau ; l’intrant quitte aussitôt le disponible.',
    no_site:
      'Cet emplacement n’est rattaché à aucun site : la consommation ne peut pas lui être imputée.',
    product_label: 'Intrant',
    cost_label: 'Nature du coût',
    cost_placeholder: 'Choisir la nature',
    cost: {
      ALIMENT: 'Aliment',
      VETERINAIRE: 'Produit vétérinaire',
      AUTRE_INTRANT: 'Autre intrant (emballage, litière…)',
    },
    over_online:
      'La quantité dépasse le stock de l’emplacement (ou du lot choisi) : le serveur refuserait la consommation.',
    over_offline:
      'La quantité dépasse le stock connu de l’appareil : la consommation sera tout de même enregistrée et signalée.',
    submit: 'Enregistrer la consommation',
    another: 'Autre consommation',
    result: {
      WAITING_offline:
        'Consommation enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Consommation en cours d’envoi…',
      DONE: 'Consommation enregistrée.',
      WARNING: 'Consommation enregistrée, avec un signalement.',
      CONFLICT: 'Consommation mise en attente d’examen (conflit).',
      REJECTED: 'Consommation refusée : {{reason}}',
    },
  },
  count: {
    offline_banner:
      'Hors ligne : le comptage est enregistré sur ce téléphone et envoyé au retour du réseau.',
    open_title: 'Ouvrir un inventaire',
    type_label: 'Type d’inventaire',
    type: {
      FULL: 'Complet (tous les produits)',
      PARTIAL: 'Partiel (produits choisis)',
      SPOT: 'Tournant (contrôle ponctuel)',
      OPENING: 'Inventaire d’ouverture',
    },
    open_hint:
      'L’activité continue pendant le comptage : chaque quantité vaut à l’instant où elle est enregistrée.',
    open: 'Ouvrir l’inventaire',
    sheet_title: 'Inventaire {{doc}} — {{type}}',
    pending_number: 'numéro à venir',
    sheet_hint:
      'Comptez, enregistrez, puis terminez l’inventaire. L’écart estimé compare au stock connu du téléphone ; le serveur calcule l’écart réel.',
    nothing_listed: 'Ajoutez les produits à compter.',
    counted_label: 'Compté ({{unit}})',
    recorded: 'Enregistré',
    recorded_pending: 'Enregistré sur le téléphone',
    no_variance: 'Aucun écart estimé.',
    variance: 'Écart estimé : {{quantity}} {{unit}}',
    reason_label: 'Motif de l’écart',
    reason_placeholder: 'Choisir un motif',
    reason_required: 'Un écart se justifie par un motif.',
    comment_label: 'Commentaire (facultatif)',
    comment_required_label: 'Commentaire (exigé par ce motif)',
    comment_required: 'Ce motif exige un commentaire.',
    quantity_required: 'Indiquez la quantité comptée, ou annulez les saisies.',
    variance_unknown:
      'Écart estimé indisponible : des opérations de ce produit attendent l’envoi et ne peuvent pas être réparties entre les lots.',
    clear_drafts: 'Annuler les saisies',
    add_label: 'Ajouter un produit à compter',
    add_placeholder: 'Choisir un produit',
    save: 'Enregistrer les comptages',
    unsaved: 'Des comptages ne sont pas encore enregistrés.',
    submit: 'Terminer l’inventaire',
    cancel: 'Abandonner l’inventaire',
    cancel_confirm: 'Confirmer l’abandon de l’inventaire',
    history_title: 'Inventaires de l’emplacement',
    lines_count_one: '{{count}} ligne',
    lines_count_other: '{{count}} lignes',
    status: {
      IN_PROGRESS: 'En cours',
      SUBMITTING: 'Fin envoyée, en attente',
      CANCELLING: 'Abandon en attente d’envoi',
      PENDING_APPROVAL: 'En attente de validation',
      POSTED: 'Comptabilisé',
      REJECTED: 'Refusé',
      CANCELLED: 'Abandonné',
    },
    result_OPEN: {
      WAITING_offline: 'Inventaire ouvert sur ce téléphone ; il sera envoyé au retour du réseau.',
      WAITING_online: 'Ouverture de l’inventaire en cours d’envoi…',
      DONE: 'Inventaire ouvert.',
      WARNING: 'Inventaire ouvert, avec un signalement.',
      CONFLICT: 'Ouverture mise en attente d’examen (conflit).',
      REJECTED: 'Ouverture refusée : {{reason}}',
    },
    result_RECORD: {
      WAITING_offline:
        'Comptages enregistrés sur ce téléphone ; ils seront envoyés au retour du réseau.',
      WAITING_online: 'Comptages en cours d’envoi…',
      DONE: 'Comptages enregistrés.',
      WARNING: 'Comptages enregistrés, avec un signalement.',
      CONFLICT: 'Comptages mis en attente d’examen (conflit).',
      REJECTED: 'Comptages refusés : {{reason}}',
    },
    result_SUBMIT: {
      WAITING_offline: 'Inventaire terminé sur ce téléphone ; il sera envoyé au retour du réseau.',
      WAITING_online: 'Fin de l’inventaire en cours d’envoi…',
      DONE: 'Inventaire terminé : comptabilisé, ou soumis à validation selon les écarts.',
      WARNING: 'Inventaire terminé, avec un signalement.',
      CONFLICT: 'Fin d’inventaire mise en attente d’examen (conflit).',
      REJECTED: 'Fin d’inventaire refusée : {{reason}}',
    },
    result_CANCEL: {
      WAITING_offline:
        'Inventaire abandonné sur ce téléphone ; l’abandon sera envoyé au retour du réseau.',
      WAITING_online: 'Abandon en cours d’envoi…',
      DONE: 'Inventaire abandonné.',
      WARNING: 'Inventaire abandonné, avec un signalement.',
      CONFLICT: 'Abandon mis en attente d’examen (conflit).',
      REJECTED: 'Abandon refusé : {{reason}}',
    },
  },
  transfer: {
    offline_banner:
      'Hors ligne : l’expédition sera enregistrée sur ce téléphone et envoyée au retour du réseau ; la marchandise quitte aussitôt le disponible.',
    requests_title: 'Demandes à expédier',
    no_request: 'Aucune demande de transfert à expédier depuis vos emplacements.',
    open_request: 'Expédier la demande {{doc}}',
    route: '{{from}} → {{to}}',
    lines_count_one: '{{count}} ligne',
    lines_count_other: '{{count}} lignes',
    dispatching_badge: 'Expédition en attente d’envoi',
    declining_badge: 'Refus en attente d’envoi',
    declining: 'Cette demande est déjà refusée depuis ce téléphone ; le refus attend l’envoi.',
    decline_open: 'Refuser la demande',
    decline_title: 'Refuser la demande',
    decline_comment_label: 'Motif du refus',
    decline_hint:
      'Le motif est transmis au demandeur ; il remplace la note de la demande sur le document.',
    decline_online_only:
      'Le refus d’une demande se fait en ligne (il se décide sur l’état à jour de la demande).',
    lot_label: 'Lot déplacé',
    lot_option: '{{code}} ({{quantity}} {{unit}})',
    lot_required: 'Plusieurs lots sur la source : choisissez le lot déplacé.',
    lot_fifo_risk:
      'Des sorties sans lot désigné attendent l’envoi : le serveur peut les prendre sur ce lot (le plus ancien d’abord) et le trouver insuffisant.',
    reenter_direct: 'Ressaisir en expédition directe',
    reenter_hint:
      'Expédition préremplie depuis une expédition que le serveur n’a pas acceptée, quantités en unité de base : vérifiez les emplacements et les quantités, puis expédiez.',
    refused_title: 'Expéditions non acceptées par le serveur',
    refused_label: 'Expédition non acceptée {{doc}}',
    refused: {
      NOT_DISPATCHABLE:
        'La demande {{doc}} a été refusée ou annulée avant votre expédition, que le serveur n’a pas acceptée. Si la marchandise est partie, ressaisissez-la en expédition directe.',
      ALREADY_DISPATCHED:
        'La demande {{doc}} a déjà été expédiée depuis un autre appareil : votre expédition n’a pas été acceptée. Ne la ressaisissez que si une seconde expédition a réellement eu lieu.',
      NOT_REREAD:
        'Votre expédition de la demande {{doc}} n’a pas été acceptée, et la demande paraît encore à expédier sur ce téléphone : attendez la prochaine synchronisation pour savoir ce qu’elle est devenue.',
      UNKNOWN:
        'Une expédition de demande n’a pas été acceptée par le serveur, et la demande n’est plus sur ce téléphone. Ne la ressaisissez que si la marchandise est bien partie.',
    },
    refused_confirm: {
      ALREADY_DISPATCHED: 'Une seconde expédition a réellement eu lieu',
      UNKNOWN: 'La marchandise est bien partie',
    },
    refused_dismiss: 'Ignorer',
    reentry_done:
      'Cette expédition refusée a déjà été ressaisie ou écartée : rien n’est envoyé, pour ne pas expédier deux fois la même marchandise.',
    decline_cancel: 'Ne pas refuser',
    decline_submit: 'Confirmer le refus',
    mode_label: 'Opération',
    mode: {
      DISPATCH: 'Expédier vers un autre emplacement',
      MOVE: 'Déplacer dans le même site',
    },
    move_title: 'Mouvement interne',
    move_hint:
      'Déplacement immédiat vers un autre emplacement du même site, sans transit ni réception : la marchandise passe aussitôt sur la destination.',
    move_no_destination:
      'Aucun autre emplacement de ce site sous votre garde ou celle de ce téléphone ; un stock confié à un autre détenteur se transfère par expédition.',
    move_over_online:
      'Quantité supérieure au disponible ({{products}}) : le serveur refuserait le mouvement. Vérifiez les quantités.',
    move_over_offline:
      'Quantité supérieure au disponible connu ({{products}}) : le mouvement sera tout de même enregistré et signalé.',
    move_submit: 'Déplacer',
    dispatching:
      'Cette demande est déjà expédiée depuis ce téléphone ; l’expédition attend l’envoi.',
    not_found:
      'Cette demande n’est plus à expédier (déjà expédiée, annulée, ou hors de vos emplacements).',
    back: 'Transférer',
    request_title: 'Expédier la demande {{doc}}',
    requested: 'Demandé : {{quantity}} {{unit}}',
    dispatched_label: 'Expédié ({{unit}})',
    nothing: 'Indiquez au moins une quantité à expédier.',
    direct_title: 'Nouvelle expédition',
    source_label: 'Depuis',
    destination_label: 'Vers',
    destination_placeholder: 'Choisir la destination',
    line_label: 'Ligne {{index}}',
    add_line: '+ Ajouter une ligne',
    remove_line: 'Retirer la ligne {{index}}',
    carrier_label: 'Transporteur (facultatif)',
    notes_label: 'Note (facultatif)',
    over_online:
      'Quantité supérieure au disponible ({{products}}) : le serveur refuserait l’expédition. Vérifiez les quantités.',
    over_offline:
      'Quantité supérieure au disponible connu ({{products}}) : l’expédition sera tout de même enregistrée et signalée.',
    submit: 'Expédier',
    submit_request: 'Expédier la demande',
    another: 'Autre expédition',
    result: {
      WAITING_offline:
        'Expédition enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Expédition en cours d’envoi…',
      DONE: 'Expédition enregistrée : la marchandise est en transit.',
      WARNING: 'Expédition enregistrée, avec un signalement.',
      CONFLICT: 'Expédition mise en attente d’examen (conflit).',
      REJECTED: 'Expédition refusée : {{reason}}',
    },
    decline_result: {
      WAITING_offline: 'Refus enregistré sur ce téléphone ; il sera envoyé au retour du réseau.',
      WAITING_online: 'Refus en cours d’envoi…',
      DONE: 'Demande refusée : le demandeur en est informé au prochain tirage.',
      WARNING: 'Demande refusée, avec un signalement.',
      CONFLICT: 'Refus mis en attente d’examen (conflit).',
      REJECTED: 'Refus non pris en compte : {{reason}}',
    },
    move_result: {
      WAITING_offline:
        'Mouvement enregistré sur ce téléphone ; il sera envoyé au retour du réseau.',
      WAITING_online: 'Mouvement en cours d’envoi…',
      DONE: 'Mouvement enregistré : la marchandise est sur la destination.',
      WARNING: 'Mouvement enregistré, avec un signalement.',
      CONFLICT: 'Mouvement mis en attente d’examen (conflit).',
      REJECTED: 'Mouvement refusé : {{reason}}',
    },
  },
  blind: {
    title: 'Réception sans document',
    inactive_product: '{{name}} (désactivé)',
    hint: 'Marchandise arrivée sans transfert connu de ce téléphone : le serveur la rapprochera de l’expédition correspondante.',
    offline_banner:
      'Hors ligne : la réception sera enregistrée sur ce téléphone et envoyée au retour du réseau ; la marchandise est aussitôt ajoutée au disponible.',
    destination_label: 'Reçu sur',
    not_exclusive_online:
      'Cet emplacement est partagé : la réception sans document n’y est pas permise. Recevez le transfert avec son document.',
    not_exclusive_offline:
      'Cet emplacement est partagé : la réception sera enregistrée mais signalée au responsable.',
    source_label: 'En provenance de',
    source_placeholder: 'Choisir la source',
    known_dispatch: 'Le transfert {{doc}} de cette source est connu de ce téléphone :',
    receive_with_document: 'le recevoir avec son document',
    duplicate: 'Ce produit figure déjà dans la réception : regroupez les quantités.',
    lot_required:
      'Un produit suivi par lot est reçu sans lot : le serveur peut le refuser s’il ne trouve pas l’expédition correspondante.',
    submit: 'Enregistrer la réception',
    another: 'Autre réception sans document',
    result: {
      WAITING_offline:
        'Réception enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Réception en cours d’envoi…',
      DONE: 'Réception enregistrée et rapprochée de son expédition.',
      WARNING: 'Réception enregistrée, en attente de rapprochement ou signalée.',
      CONFLICT: 'Réception mise en attente d’examen (conflit).',
      REJECTED: 'Réception refusée : {{reason}}',
    },
  },
  receive: {
    list_hint: 'Transferts expédiés vers vos emplacements, en attente de réception.',
    empty: 'Aucun transfert à recevoir sur cet appareil.',
    open: 'Recevoir le transfert {{doc}}',
    from: 'De {{from}}',
    to_receive: 'À recevoir',
    receiving: 'Réception en attente d’envoi',
    title: 'Réception {{doc}}',
    route: '{{from}} → {{to}}',
    back: 'Transferts à recevoir',
    not_found: 'Transfert introuvable sur cet appareil, ou déjà reçu.',
    already_receiving:
      'Une réception de ce transfert est déjà enregistrée sur l’appareil et attend d’être envoyée.',
    hint: 'Comptez la marchandise reçue : saisissez la quantité réellement reçue pour chaque article (unité de base).',
    dispatched: 'Expédié : {{quantity}} {{unit}}',
    received_label: 'Reçu ({{unit}})',
    quantity_invalid: 'Quantité invalide (0 ou plus, 3 décimales au plus).',
    short: 'Manque : {{quantity}} {{unit}} (écart à justifier)',
    reason_label: 'Motif de l’écart',
    reason_placeholder: 'Choisir un motif (facultatif)',
    over_online:
      'Une quantité reçue dépasse l’expédié : corrigez-la (le serveur refuse un excédent en ligne).',
    over_offline: 'Une quantité reçue dépasse l’expédié : l’excédent sera signalé au responsable.',
    offline_banner:
      'Hors ligne : la réception sera enregistrée sur ce téléphone et envoyée au retour du réseau.',
    submit: 'Enregistrer la réception',
    result: {
      WAITING_offline:
        'Réception enregistrée sur ce téléphone, elle sera envoyée dès le retour du réseau.',
      WAITING_online: 'Réception en cours d’envoi…',
      DONE: 'Réception enregistrée.',
      WARNING: 'Réception enregistrée, avec un écart ou un excédent à examiner.',
      CONFLICT: 'Réception mise en quarantaine : le transfert a déjà été reçu ou retourné.',
      REJECTED: 'Réception refusée : {{reason}}',
    },
  },
  loss: {
    offline_banner:
      'Hors ligne : la perte sera enregistrée sur ce téléphone et envoyée au retour du réseau.',
    product_label: 'Produit',
    product_placeholder: 'Choisir le produit',
    lot_label: 'Lot',
    lot_fifo: 'Le plus ancien (choisi par le serveur)',
    lot_choose: 'Choisir le lot',
    lot_required_incubation:
      'Incubateur ou éclosoir : désignez le lot d’incubation touché (sinon le bilan d’un autre lot serait faussé).',
    lot_unknown_incubation:
      'Un lot d’incubation démarré sur ce téléphone n’a pas encore de lot de stock (le serveur le crée à la synchronisation) : une perte de ses œufs ne peut pas lui être imputée avant. Aucun lot n’est présélectionné : choisissez le lot touché.',
    lot_stock_pending: 'stock à mettre à jour',
    category_label: 'Nature de la perte',
    categories: {
      CASSE: 'Casse',
      DETERIORATION: 'Détérioration',
      IMPROPRE: 'Impropre à la vente',
      DESTRUCTION: 'Destruction',
      INEXPLIQUEE: 'Perte inexpliquée',
      VOL_SUSPECTE: 'Vol suspecté',
      MORTALITE: 'Mortalité',
    },
    reason_label: 'Motif',
    reason_placeholder: 'Choisir un motif (facultatif)',
    comment_label: 'Commentaire',
    over_online:
      'La quantité dépasse le disponible (ou le lot choisi) : le serveur refuserait la perte. Vérifiez la quantité.',
    over_offline:
      'La quantité dépasse le disponible connu de l’appareil : la perte sera tout de même enregistrée et signalée.',
    issue: {
      PRODUCT_REQUIRED: 'Choisissez le produit.',
      QUANTITY_INVALID: 'Quantité invalide (au plus 3 décimales).',
      CATEGORY_REQUIRED: 'Choisissez la nature de la perte.',
      COMMENT_REQUIRED: 'Un commentaire est obligatoire pour cette perte.',
    },
    submit: 'Déclarer la perte',
    another: 'Nouvelle déclaration',
    result: {
      WAITING_offline:
        'Perte enregistrée sur ce téléphone, elle sera envoyée dès le retour du réseau.',
      WAITING_online: 'Déclaration en cours d’envoi…',
      DONE: 'Perte {{doc}} enregistrée.',
      WARNING: 'Perte {{doc}} enregistrée, avec un avertissement à examiner.',
      CONFLICT: 'Déclaration mise en attente d’examen (conflit).',
      REJECTED: 'Déclaration refusée : {{reason}}',
    },
  },
  stock: {
    guard: {
      CUSTODY_DEVICE_MISMATCH:
        'Ce stock est confié à un autre appareil : aucune sortie hors ligne n’est possible ici.',
      ALLOCATION_MISSING:
        '{{product}} : aucun quota pour ce téléphone sur cet emplacement ; la sortie hors ligne n’est pas possible.',
      ALLOCATION_EXPIRED:
        '{{product}} : votre quota a expiré ; demandez son renouvellement à votre responsable, il arrivera à la prochaine synchronisation.',
      ALLOCATION_EXCEEDED:
        '{{product}} : quota dépassé, il vous reste {{available}}, {{requested}} demandé(s).',
      INSUFFICIENT_STOCK:
        '{{product}} : stock insuffisant sur cet appareil, {{available}} disponible(s), {{requested}} demandé(s).',
    },
    guard_signal:
      '{{product}} : au-delà de ce que permet la garde hors ligne ({{code}}) ; la sortie est enregistrée et le serveur la signalera.',
    location_label: 'Emplacement',
    no_location:
      'Aucun stock sur cet appareil : synchronisez, ou demandez une affectation à un emplacement.',
    custody: {
      EXCLUSIVE_USER: 'Stock confié à une personne',
      EXCLUSIVE_DEVICE: 'Stock confié à un appareil',
      SHARED: 'Stock partagé entre plusieurs appareils',
    },
    alerts_only: 'Seulement les produits sous le seuil ({{count}})',
    empty: 'Aucun produit en stock sur cet emplacement.',
    no_alert: 'Aucun produit sous le seuil.',
    actions_title: 'Opérations du stock',
    list_label: 'Soldes de l’emplacement',
    col_product: 'Produit',
    col_state: 'Situation',
    state: {
      STOCK_LOW: 'Sous le seuil',
      STOCK_OUT: 'Rupture',
    },
    on_hand: 'Physique',
    available: 'Disponible',
    pending: 'Opérations de l’appareil en attente : {{quantity}} {{unit}}',
    threshold: 'Seuil : {{min}} · réapprovisionnement suggéré : {{suggested}}',
    lots: 'Lots ({{count}})',
    no_lot: 'Sans lot',
    expiry: 'Péremption : {{date}}',
  },
  collect: {
    amount_to_collect: 'Montant à encaisser',
    customer_required: 'Choisissez le client qui règle.',
    customer_unreadable:
      'Le client désigné n’a pas pu être lu (réseau ou droits) : choisissez le client qui règle.',
    customer_loading: 'Lecture du client…',
    customer_retry: 'Relire le client',
    outstanding: 'Encours du client : {{amount}}',
    dues_title: 'Créances à régler',
    dues_online: 'Toutes les créances du client (lues en ligne).',
    dues_offline:
      'Hors ligne : seules les ventes des 7 derniers jours sont connues de l’appareil ; l’encours reste la référence.',
    dues_loading: 'Lecture des créances…',
    dues_none: 'Aucune créance connue pour ce client.',
    due_line: '{{doc}} · échéance {{date}}',
    due_line_no_date: '{{doc}}',
    preview_title: 'Affectation prévue (la plus ancienne d’abord)',
    preview_title_targeted: 'Affectation prévue (vente désignée)',
    targeted:
      'Encaissement affecté à la vente {{doc}} (reste dû {{amount}}) ; un surplus éventuel reste en avoir du client.',
    target_auto: 'Répartir sur toutes les créances',
    target_gone:
      'La vente désignée n’a plus de reste dû connu : l’encaissement est réparti sur les créances, de la plus ancienne à la plus récente.',
    allocated: 'Réglé : {{amount}}',
    site_label: 'Site de l’encaissement',
    site_placeholder: 'Déduit par le serveur',
    remote_search_hint: 'Recherche en ligne parmi tous les clients.',
    remote_search_offline: 'Hors ligne : seuls les clients de l’appareil sont proposés.',
    dues_total: 'Total des créances : {{amount}}',
    preview_credit: 'Reste en avoir du client : {{amount}}',
    pending_title: 'Encaissements en attente d’envoi',
    amount_label: 'Montant reçu (F CFA)',
    submit: 'Enregistrer l’encaissement',
    new_payment: 'Nouvel encaissement',
    offline_banner:
      'Hors ligne : l’encaissement sera enregistré sur ce téléphone et affecté par le serveur au retour du réseau.',
    result: {
      WAITING_offline:
        'Encaissement enregistré sur ce téléphone, il sera envoyé dès le retour du réseau.',
      WAITING_online: 'Encaissement en cours d’envoi…',
      DONE: 'Encaissement {{doc}} enregistré.',
      WARNING:
        'Encaissement {{doc}} enregistré, avec un avertissement à examiner (doublon possible ou part non affectée).',
      WARNING_CASH_SESSION_MISSING:
        'Encaissement {{doc}} enregistré sans caisse ouverte : il est signalé à la Finance.',
      CONFLICT: 'Encaissement mis en attente d’examen (conflit).',
      REJECTED: 'Encaissement refusé : {{reason}}',
    },
  },
  errors: {
    title: 'Un problème est survenu sur cet écran.',
    help: 'Vos opérations déjà enregistrées sont conservées sur l’appareil.',
    reload: 'Recharger l’application',
  },
  devices: {
    intro:
      'Téléphones et tablettes de votre portée : approuvez un appareil neuf, bloquez ou retirez un appareil perdu ou inutilisé.',
    online_only: 'Écran en ligne seulement : reconnectez-vous pour gérer les appareils.',
    help_title: 'À quoi sert cet écran ?',
    denied: 'Lecture des appareils refusée : droit insuffisant.',
    unavailable: 'Appareils indisponibles : réessayez quand le serveur répond.',
    refreshing: 'Relecture des appareils après la dernière décision…',
    refresh: 'Actualiser la liste',
    truncated: 'Seuls les 1 000 appareils les plus récents sont affichés : affinez avec le filtre.',
    filter_label: 'Afficher',
    filter: {
      PENDING: 'En attente',
      ACTIVE: 'Actifs',
      BLOCKED: 'Bloqués',
      ALL: 'Tous',
    },
    search_label: 'Rechercher (code, utilisateur, plateforme)',
    none: {
      PENDING: 'Aucun appareil en attente d’approbation.',
      ACTIVE: 'Aucun appareil actif.',
      BLOCKED: 'Aucun appareil bloqué.',
      ALL: 'Aucun appareil dans votre portée.',
    },
    card_label: 'Appareil {{code}}',
    status: {
      PENDING: 'En attente',
      ACTIVE: 'Actif',
      BLOCKED: 'Bloqué',
      LOST: 'Perdu',
      RETIRED: 'Retiré',
    },
    this_device: 'Ce téléphone',
    this_device_warning:
      'C’est le téléphone que vous utilisez : il sera déconnecté et ne pourra plus envoyer d’opérations.',
    enrolled: 'connecté par {{name}} le {{date}}',
    unknown_user: 'utilisateur inconnu',
    contact: 'Dernier contact : {{date}}',
    contact_stale: 'Sans contact récent : dernier le {{date}}',
    contact_never: 'Aucun contact enregistré.',
    shared: 'Partagé entre plusieurs utilisateurs',
    site: 'rattaché à {{name}}',
    reason: 'Motif : {{reason}}',
    self_pending:
      'Vous avez connecté cet appareil : son approbation revient à l’Admin ou à un autre responsable.',
    action: {
      APPROVE: 'Approuver l’appareil {{code}}',
      REFUSE: 'Refuser l’appareil {{code}}',
      BLOCK: 'Bloquer l’appareil {{code}}',
      UNBLOCK: 'Débloquer l’appareil {{code}}',
      LOST: 'Déclarer perdu l’appareil {{code}}',
      RETIRE: 'Retirer l’appareil {{code}}',
      DESIGNATE: 'Rattacher l’appareil {{code}}',
    },
    confirm: {
      APPROVE: 'Confirmer l’approbation',
      REFUSE: 'Confirmer le refus de {{code}}',
      BLOCK: 'Confirmer le blocage de {{code}}',
      UNBLOCK: 'Confirmer le déblocage',
      LOST: 'Confirmer la perte de {{code}}',
      RETIRE: 'Confirmer le retrait de {{code}}',
      DESIGNATE: 'Enregistrer le rattachement de {{code}}',
    },
    reason_required: 'Motif (obligatoire)',
    reason_optional: 'Motif (facultatif)',
    shared_label: 'Appareil partagé entre plusieurs utilisateurs (tablette)',
    site_label: 'Site de rattachement',
    no_site: 'Aucun site',
    pending_banner:
      'Cet appareil attend l’approbation de votre responsable ou de l’administrateur : communiquez-lui le code {{code}}. Vos opérations partiront après l’approbation.',
    pending_banner_no_code:
      'Cet appareil attend l’approbation de votre responsable ou de l’administrateur. Vos opérations partiront après l’approbation.',
    result: {
      WAITING_offline:
        'Décision enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Décision en cours d’envoi…',
      DONE: 'Décision enregistrée.',
      WARNING: 'Décision enregistrée, avec un signalement.',
      CONFLICT: 'Décision mise en attente d’examen (conflit).',
      REJECTED: 'Décision refusée : {{reason}}',
    },
  },
  users: {
    intro:
      'Comptes de votre portée. L’administrateur crée un compte avec un mot de passe provisoire, y ajoute ou retire des rôles, suspend ou désactive un compte, et réinitialise le mot de passe d’une personne qui l’a oublié : elle devra le changer à sa prochaine connexion.',
    online_only: 'Écran en ligne seulement : reconnectez-vous pour gérer les comptes.',
    help_title: 'À quoi sert cet écran ?',
    refreshing: 'Relecture des comptes après la dernière opération…',
    denied: 'Lecture des comptes refusée : droit insuffisant.',
    unavailable: 'Comptes indisponibles : réessayez quand le serveur répond.',
    truncated: 'Seuls les 1 000 comptes les plus récents sont lus : affinez avec le filtre.',
    filter_label: 'Afficher',
    filter: {
      ACTIVE: 'Actifs',
      SUSPENDED: 'Suspendus',
      DEACTIVATED: 'Désactivés',
      ALL: 'Tous',
    },
    none: {
      ACTIVE: 'Aucun compte actif dans votre portée.',
      SUSPENDED: 'Aucun compte suspendu.',
      DEACTIVATED: 'Aucun compte désactivé.',
      ALL: 'Aucun compte dans votre portée.',
    },
    search_label: 'Rechercher (nom ou téléphone)',
    status: {
      ACTIVE: 'Actif',
      SUSPENDED: 'Suspendu',
      DEACTIVATED: 'Désactivé',
    },
    role: {
      DIRECTION: 'Direction',
      ADMIN: 'Administrateur',
      RESP_COMMERCIAL: 'Responsable commercial',
      COMMERCIAL_TERRAIN: 'Commercial terrain',
      COMMERCIAL_SEDENTAIRE: 'Commercial sédentaire',
      VENDEUR_PDV: 'Vendeur de point de vente',
      RESP_PRODUCTION: 'Responsable production',
      RESP_FERME: 'Responsable de ferme',
      MAGASINIER: 'Magasinier',
      RESP_ACHATS: 'Responsable achats',
      FINANCE: 'Finance',
    },
    no_role: 'aucun rôle',
    last_login: 'Dernière connexion : {{date}}',
    never_logged_in: 'Jamais connecté.',
    must_change: 'Mot de passe provisoire : à changer à la prochaine connexion.',
    reset_action: 'Réinitialiser le mot de passe',
    reset_open: 'Réinitialiser le mot de passe de {{name}}',
    reset_title: 'Mot de passe provisoire de {{name}}',
    reset_help:
      'Saisissez un mot de passe provisoire (8 caractères au moins) et communiquez-le à la personne, de vive voix : il n’est enregistré nulle part sur ce téléphone.',
    reset_password: 'Mot de passe provisoire',
    reset_confirm: 'Confirmer le mot de passe provisoire',
    reset_mismatch: 'Les deux saisies du mot de passe provisoire ne correspondent pas.',
    reset_show: 'Afficher le mot de passe',
    reset_submit: 'Réinitialiser pour {{name}}',
    reset_sending: 'Réinitialisation…',
    reset_done:
      'Mot de passe de {{name}} réinitialisé : ses sessions sont fermées et il devra choisir son propre mot de passe à sa prochaine connexion.',
    reset_offline:
      'Réponse non reçue : la réinitialisation a peut-être été faite. Recommencez en ligne pour en être sûr.',
    reset_session:
      'Session expirée : reconnectez-vous puis recommencez. La réinitialisation n’a pas été envoyée.',
    reset_refused: 'Réinitialisation refusée : {{reason}}',
    create_action: 'Nouveau compte',
    create_title: 'Nouveau compte',
    create_help_title: 'Comment saisir un compte ?',
    create_help:
      'Saisissez le nom, le numéro de téléphone (avec +237 ou sans) et un mot de passe provisoire de 8 caractères au moins, à communiquer de vive voix : il n’est enregistré nulle part sur ce téléphone et la personne devra le changer à sa première connexion. Le rôle peut être ajouté tout de suite ou plus tard.',
    name_label: 'Nom complet',
    phone_label: 'Téléphone',
    email_label: 'Adresse e-mail (facultative)',
    create_problem: {
      PHONE: 'Numéro de téléphone invalide : 9 chiffres, avec ou sans +237.',
      EMAIL: 'Adresse e-mail invalide.',
      SITE: 'Choisissez le site de ce rôle.',
      ZONE: 'Choisissez la zone de ce rôle.',
      TEAM: 'Choisissez l’équipe de ce rôle.',
      SCOPE: 'Ce rôle ne permet pas ce périmètre.',
    },
    create_submit: 'Créer le compte',
    create_sending: 'Création…',
    create_done:
      'Compte de {{name}} créé : communiquez-lui son mot de passe provisoire, il devra le changer à sa première connexion.',
    create_offline:
      'Réponse non reçue : le compte a peut-être été créé. Vérifiez la liste avant de recommencer.',
    create_session:
      'Session expirée : reconnectez-vous puis recommencez. Le compte n’a pas été envoyé.',
    roles_unavailable:
      'Lecture des rôles impossible pour l’instant : la création de compte et l’ajout de rôle sont indisponibles.',
    create_refused: 'Création refusée : {{reason}}',
    role_label: 'Rôle',
    no_role_option: 'Aucun rôle pour l’instant',
    choose_role: 'Choisir le rôle',
    scope_label: 'Périmètre',
    scope: {
      GLOBAL: 'Toute l’entreprise',
      SITE: 'Un site',
      ZONE: 'Une zone',
      TEAM: 'Une équipe',
    },
    scope_unavailable:
      'Ce rôle ne se donne ici sur aucun périmètre : vérifiez les périmètres qu’il permet.',
    scope_site: 'site {{name}}',
    site_unknown: 'inconnu',
    site_label: 'Site',
    choose_site: 'Choisir le site',
    scope_zone: 'zone {{name}}',
    zone_unknown: 'inconnue',
    zone_label: 'Zone',
    choose_zone: 'Choisir la zone',
    scope_team: 'équipe {{name}}',
    team_unknown: 'inconnue',
    team_label: 'Équipe',
    choose_team: 'Choisir l’équipe',
    assignments_label: 'Rôles de {{name}}',
    assign_action: 'Ajouter un rôle',
    assign_open: 'Ajouter un rôle à {{name}}',
    assign_title: 'Nouveau rôle de {{name}}',
    assign_confirm: 'Ajouter le rôle à {{name}}',
    assign_duplicate: 'Ce compte a déjà ce rôle sur ce périmètre.',
    revoke_action: 'Retirer',
    revoke_open: 'Retirer le rôle {{role}} de {{name}}',
    revoke_title: 'Retrait du rôle {{role}} de {{name}}',
    revoke_confirm: 'Confirmer le retrait du rôle {{role}}',
    reason_required: 'Motif (obligatoire)',
    reason_optional: 'Motif (facultatif)',
    status_short: {
      SUSPEND: 'Suspendre',
      REACTIVATE: 'Réactiver',
      DEACTIVATE: 'Désactiver',
    },
    status_action: {
      SUSPEND: 'Suspendre le compte de {{name}}',
      REACTIVATE: 'Réactiver le compte de {{name}}',
      DEACTIVATE: 'Désactiver le compte de {{name}}',
    },
    status_confirm: {
      SUSPEND: 'Confirmer la suspension de {{name}}',
      REACTIVATE: 'Confirmer la réactivation de {{name}}',
      DEACTIVATE: 'Confirmer la désactivation de {{name}}',
    },
    result: {
      WAITING_offline:
        'Opération enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Opération en cours d’envoi…',
      DONE: 'Opération enregistrée.',
      WARNING: 'Opération enregistrée, avec un signalement.',
      CONFLICT: 'Opération mise en attente d’examen (conflit).',
      REJECTED: 'Opération refusée : {{reason}}',
    },
  },
  approvals: {
    intro:
      'Opérations qui attendent votre décision, la plus ancienne d’abord. Seules celles de votre portée sont affichées.',
    online_only: 'Écran en ligne seulement : reconnectez-vous pour décider les validations.',
    help_title: 'À quoi sert cet écran ?',
    denied: 'Lecture des validations refusée : droit insuffisant.',
    unavailable: 'Validations indisponibles : réessayez quand le serveur répond.',
    refreshing: 'Relecture de la file après la dernière décision…',
    refresh: 'Actualiser la file',
    truncated: 'Seules les 1 000 demandes les plus anciennes sont affichées : décidez-les d’abord.',
    none: 'Aucune validation ne vous attend.',
    none_filtered: 'Aucune validation de ce type ne vous attend.',
    filtered: 'Filtre : {{types}}',
    show_all: 'Voir toutes les validations',
    card_label: '{{type}} : {{summary}}',
    type: {
      LOSS_DECLARATION: 'Perte',
      MORTALITY: 'Mortalité',
      INVENTORY_ADJUSTMENT: 'Écart d’inventaire',
      ANIMAL_COUNT_ADJUSTMENT: 'Écart de comptage d’animaux',
      TRANSFER_DISCREPANCY: 'Écart de transfert',
      EXPENSE: 'Dépense',
      PURCHASE_REQUEST: 'Demande d’achat',
      PURCHASE_ORDER: 'Bon de commande',
      RECEIPT_WITHOUT_PO: 'Réception sans bon de commande',
      RECEIPT_VALUE: 'Valeur de réception',
      SUPPLIER_PAYMENT: 'Paiement fournisseur',
      PRICE_OVERRIDE: 'Remise au-delà du plafond',
      SALE_CANCELLATION: 'Annulation de vente',
      CREDIT_LIMIT_EXCEEDED: 'Dépassement d’encours',
      CASH_VARIANCE: 'Écart de caisse',
      CHECKIN_OVERRIDE: 'Dérogation de pointage',
      RECEIPT_QUARANTINE: 'Réception en quarantaine',
      RECEIPT_CANCELLATION: 'Annulation de réception',
      PAYMENT_CANCELLATION: 'Annulation d’encaissement',
      PAYMENT_DUPLICATE: 'Encaissement en double',
      CASH_TRANSFER_DISCREPANCY: 'Écart de remise de fonds',
      INVOICE_MISMATCH: 'Écart de facture fournisseur',
    },
    requested: 'Demandé par {{name}} le {{date}}',
    unknown_user: 'utilisateur inconnu',
    site: 'Site : {{name}}',
    amount: 'Montant : {{amount}}',
    amount_masked: 'Montant au coût masqué (droit de valorisation requis).',
    attachment_pending:
      'Justificatif attendu (pas encore reçu par le serveur) : l’approbation sera possible dès sa réception ; « Actualiser la file » relit la demande.',
    self_request:
      'Votre propre demande : en tant que Direction, vous pouvez la valider en le déclarant explicitement.',
    record_only:
      'Validation a posteriori : l’opération est déjà enregistrée ; votre décision est tracée sans la modifier.',
    accept_only:
      'Un écart s’accepte avec son motif ; il ne se refuse pas (une erreur se corrige par un ajustement ou une régularisation).',
    reason_note: {
      default: 'L’approbation exige un motif ; un refus reste possible.',
      INVOICE_MISMATCH:
        'L’écart s’approuve en le motivant ; refusé, la facture passe en contestation.',
    },
    action: {
      APPROVE: 'Approuver',
      REJECT: 'Refuser',
    },
    action_label: {
      APPROVE: 'Approuver : {{summary}}',
      REJECT: 'Refuser : {{summary}}',
    },
    confirm: {
      APPROVE: 'Confirmer l’approbation',
      REJECT: 'Confirmer le refus',
    },
    comment_required: 'Motif du refus (obligatoire)',
    reason_required: 'Motif de l’acceptation (obligatoire, 200 caractères au plus)',
    comment_optional: 'Commentaire (facultatif)',
    option_legend: {
      SALE_CANCELLATION_APPROVE: 'Argent déjà payé par le client',
      CASH_TRANSFER_DISCREPANCY_APPROVE: 'Imputation de l’écart',
      LOSS_DECLARATION_REJECT: 'Issue du refus',
      MORTALITY_REJECT: 'Issue du refus',
      default: 'Choix',
    },
    option: {
      REFUND: 'Rembourser le client',
      CUSTOMER_CREDIT: 'Garder en crédit au nom du client',
      ERREUR_DECLARATION: 'Erreur de déclaration : la quantité revient au stock',
      PERTE_NON_JUSTIFIEE: 'Perte non justifiée : la perte est reconnue, à instruire',
      SENDER: 'Expéditeur',
      CARRIER: 'Porteur',
      RECEIVER: 'Récepteur',
      COMPANY: 'Entreprise',
    },
    option_none: 'Selon la demande',
    data: {
      correctedReference: 'Référence corrigée (facultatif)',
    },
    self_confirm: {
      APPROVE: 'J’approuve ma propre demande (auto-validation de la Direction, tracée)',
      REJECT: 'Je refuse ma propre demande (décision de la Direction sur sa demande, tracée)',
    },
    block: {
      ATTACHMENT_PENDING: 'Approbation impossible tant que le justificatif n’est pas reçu.',
      COMMENT_REQUIRED: 'Indiquez le motif du refus.',
      TOO_LONG: 'Texte trop long : 2 000 caractères au plus (200 pour une référence).',
      OPTION_REQUIRED: 'Faites un choix pour décider.',
      SELF_CONFIRM: 'Cochez la déclaration d’auto-validation pour décider votre propre demande.',
    },
    result: {
      WAITING_offline:
        'Décision enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Décision en cours d’envoi…',
      DONE: 'Décision enregistrée.',
      WARNING: 'Décision enregistrée, avec un signalement.',
      CONFLICT: 'Décision mise en attente d’examen (conflit).',
      REJECTED: 'Décision refusée : {{reason}}',
    },
  },
  quotas: {
    online_only:
      'Gestion des quotas en ligne seulement : reconnectez-vous. La libération d’un quota se fait sur l’accueil du point de vente du téléphone qui le détient.',
    no_location: 'Aucun emplacement partagé connu de ce téléphone.',
    location_label: 'Emplacement',
    denied: 'Quotas de cet emplacement non accessibles avec vos droits.',
    failed: 'Quotas indisponibles : le serveur n’a pas répondu.',
    active_title: 'Quotas actifs',
    active_empty: 'Aucun quota actif sur cet emplacement.',
    active_partial:
      'Liste partielle : plus de {{count}} quotas actifs sur cet emplacement, seuls les plus récents sont montrés.',
    holder: 'utilisateur {{user}}, appareil {{device}}',
    you: 'vous',
    this_device: 'ce téléphone',
    remaining: 'Reste {{remaining}} sur {{granted}} accordé(s).',
    remaining_until: 'Reste {{remaining}} sur {{granted}} accordé(s), jusqu’au {{until}}.',
    revocation_pending:
      'Appareil signalé : le quota ne se consomme plus, sa révocation est attendue.',
    expired:
      'Quota échu : il ne se consomme plus et attend sa libération par le téléphone détenteur ; révoquez-le pour rendre son reste au stock commun.',
    row_label: '{{name}}, {{holder}}',
    increase_open: 'Augmenter le quota {{name}} ({{holder}})',
    revoke_open: 'Révoquer le quota {{name}} ({{holder}})',
    increase_label: 'Quantité à ajouter ({{unit}})',
    reason_label: 'Motif de la révocation',
    increase_submit: 'Augmenter',
    revoke_submit: 'Confirmer la révocation',
    cancel_edit: 'Annuler',
    grant_title: 'Accorder un quota',
    holder_label: 'Détenteur',
    holder_placeholder: 'Choisir le détenteur',
    holder_hint:
      'Détenteurs déjà connus sur cet emplacement, ou vous-même sur ce téléphone si votre rôle peut libérer un quota : les utilisateurs et appareils ne sont pas encore servis au téléphone. Un appareil signalé n’est pas proposé.',
    holder_partial:
      'Détenteurs tirés des {{count}} quotas les plus récents de cet emplacement : les plus anciens ne sont pas proposés.',
    product_label: 'Produit',
    product_placeholder: 'Choisir le produit',
    product_held: '{{name}} (quota actif de ce détenteur : augmentez-le ou révoquez-le)',
    unallocated: 'Non alloué (indicatif) : {{quantity}} {{unit}} ; le serveur le recalcule.',
    overcommitted: 'Quotas au-delà du stock : {{quantity}} {{unit}}.',
    quantity_label: 'Quantité accordée ({{unit}})',
    quantity_label_no_unit: 'Quantité accordée',
    hours_label: 'Durée en heures (facultatif)',
    hours_hint: 'Vide : jusqu’à la fin du jour.',
    hours_invalid: 'Durée invalide : plus de 0 et au plus 744 heures.',
    grant_submit: 'Accorder le quota',
    grant_result: {
      WAITING_offline: 'Octroi enregistré ; il sera envoyé au retour du réseau.',
      WAITING_online: 'Octroi en cours d’envoi…',
      DONE: 'Quota accordé.',
      WARNING: 'Quota accordé, avec un signalement.',
      CONFLICT: 'Octroi mis en attente d’examen (conflit).',
      REJECTED: 'Octroi refusé : {{reason}}',
    },
    increase_result: {
      WAITING_offline: 'Augmentation enregistrée ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Augmentation en cours d’envoi…',
      DONE: 'Quota augmenté.',
      WARNING: 'Quota augmenté, avec un signalement.',
      CONFLICT: 'Augmentation mise en attente d’examen (conflit).',
      REJECTED: 'Augmentation refusée : {{reason}}',
    },
    revoke_result: {
      WAITING_offline: 'Révocation enregistrée ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Révocation en cours d’envoi…',
      DONE: 'Quota révoqué.',
      WARNING: 'Quota révoqué, avec un signalement.',
      CONFLICT: 'Révocation mise en attente d’examen (conflit).',
      REJECTED: 'Révocation refusée : {{reason}}',
    },
  },
  supplier_invoice: {
    offline_banner:
      'Hors ligne : les factures et les dettes affichées peuvent être périmées ; décisions et enregistrement attendent le retour du réseau.',
    suppliers_failed: 'Fournisseurs indisponibles : le serveur n’a pas répondu.',
    retry_suppliers: 'Relire les fournisseurs',
    retry_payables: 'Relire les dettes',
    retry_list: 'Relire les factures',
    denied: 'Factures fournisseurs non accessibles avec vos droits.',
    failed: 'Factures indisponibles : le serveur n’a pas répondu.',
    failed_stale:
      'Actualisation impossible : le serveur n’a pas répondu. La liste ci-dessous peut être périmée ; les actions restent bloquées jusqu’à une relecture.',
    empty: 'Aucune facture dans ce groupe.',
    payables_title: 'Dettes fournisseurs',
    payables_failed: 'Dettes indisponibles : le serveur n’a pas répondu.',
    payables_empty: 'Aucune dette fournisseur.',
    payable_line: 'échu {{overdue}} · avance {{advance}} · net {{net}}',
    // Refonte (lot L8) : tuiles des dettes, colonnes du tableau des factures, libellés courts des actions
    // (le nom accessible reste la phrase complète du document).
    payables_as_of: 'Situation au {{date}}',
    tile_balance: 'Dette totale',
    tile_advance: 'Avances versées',
    col_supplier: 'Fournisseur',
    col_debt: 'Dette',
    col_position: 'Échu · avance · net',
    col_invoice: 'Facture',
    col_dates: 'Dates',
    col_total: 'Montant',
    col_status: 'Situation',
    decision_short: {
      approve: 'Approuver',
      dispute: 'Contester',
      cancel: 'Annuler',
    },
    lines_show_short: 'Voir les lignes',
    lines_hide_short: 'Masquer les lignes',
    buckets_intro: 'Par retard :',
    bucket: '{{bucket}} : {{amount}}',
    bucket_label: {
      '0-30': 'non échu ou jusqu’à 30 j',
      '31-60': '31 à 60 j',
      '61-90': '61 à 90 j',
      '>90': 'plus de 90 j',
    },
    list_title: 'Factures',
    group_label: 'Factures à afficher',
    group: {
      TO_DECIDE: 'À décider (écart ou contestation)',
      TO_PAY: 'À payer',
      PAID: 'Payées',
      CANCELLED: 'Annulées',
    },
    filter_supplier: 'Filtrer par fournisseur',
    decide_link: 'Décider les écarts dans la file des validations',
    all_suppliers: 'Tous les fournisseurs',
    card_dates: 'Réf. fournisseur {{ref}} · facture du {{date}} · échéance le {{due}}',
    card_author: 'Saisie par {{name}}',
    no_order: 'sans bon de commande',
    status: {
      MISMATCH: 'Écart de rapprochement',
      DISPUTED: 'Contestée',
      APPROVED: 'Approuvée',
      PARTIALLY_PAID: 'Payée en partie',
      PAID: 'Payée',
      CANCELLED: 'Annulée (avoir)',
    },
    card_paid: 'payé {{paid}}, reste {{balance}}',
    approval_reason: 'Motif de l’approbation : {{reason}}',
    dispute_reason: 'Motif de la contestation : {{reason}}',
    cancel_reason: 'Motif de l’annulation : {{reason}}',
    no_scan: 'Aucune photo de la facture.',
    scan_alt: 'Facture {{doc}}',
    scan_view: 'Voir la facture {{doc}}',
    scan_uploading: 'Photo de la facture en cours d’envoi.',
    scan_refused: 'Photo de la facture refusée par le serveur.',
    scan_missing: 'Photo de la facture jamais reçue par le serveur.',
    online_only:
      'Lecture en ligne seulement : reconnectez-vous pour afficher les factures et les dettes.',
    attach_scan: 'Joindre la photo de la facture {{doc}}',
    lines_show: 'Voir les lignes de {{doc}}',
    lines_hide: 'Masquer les lignes de {{doc}}',
    lines_failed: 'Lignes indisponibles : le serveur n’a pas répondu.',
    lines_denied: 'Lignes de cette facture non accessibles avec vos droits.',
    retry_lines: 'Relire les lignes de {{doc}}',
    line_read: '{{description}} : {{quantity}} × {{price}} = {{total}}',
    allocation_line: 'Paiement de {{amount}} ({{status}})',
    allocation_status: {
      PLANNED: 'en attente de la Direction',
      ACTIVE: 'décaissé',
      REVERSED: 'annulé',
    },
    own_invoice: 'Vous avez saisi cette facture : un autre approbateur décide de son écart.',
    reason_label: {
      dispute: 'Motif de la contestation',
      approve: 'Motif de l’approbation de l’écart',
      cancel: 'Motif de l’annulation (avoir)',
    },
    submit_decision: {
      dispute: 'Contester la facture',
      approve: 'Approuver la facture',
      cancel: 'Annuler la facture',
    },
    open_decision: {
      approve: 'Approuver {{doc}}',
      dispute: 'Contester {{doc}}',
      cancel: 'Annuler {{doc}}',
    },
    decision_result: {
      dispute: {
        WAITING_offline:
          'Contestation enregistrée sur ce téléphone : elle partira au retour du réseau.',
        WAITING_online: 'Contestation en cours d’envoi…',
        DONE: 'Facture {{doc}} contestée.',
        WARNING: 'Facture {{doc}} contestée, avec un signalement.',
        CONFLICT: 'Contestation en attente d’examen (conflit).',
        REJECTED: 'Contestation refusée : {{reason}}',
      },
      approve: {
        WAITING_offline:
          'Approbation enregistrée sur ce téléphone : elle partira au retour du réseau.',
        WAITING_online: 'Approbation en cours d’envoi…',
        DONE: 'Facture {{doc}} approuvée.',
        WARNING: 'Facture {{doc}} approuvée, avec un signalement.',
        CONFLICT: 'Approbation en attente d’examen (conflit).',
        REJECTED: 'Approbation refusée : {{reason}}',
      },
      cancel: {
        WAITING_offline:
          'Annulation enregistrée sur ce téléphone : elle partira au retour du réseau.',
        WAITING_online: 'Annulation en cours d’envoi…',
        DONE: 'Facture {{doc}} annulée.',
        WARNING: 'Facture {{doc}} annulée, avec un signalement.',
        CONFLICT: 'Annulation en attente d’examen (conflit).',
        REJECTED: 'Annulation refusée : {{reason}}',
      },
    },
    mismatch: {
      NO_PURCHASE_ORDER: 'Facture sans bon de commande : à examiner.',
      SUPPLIER_MISMATCH: 'Fournisseur différent de celui du bon de commande.',
      LINE_NOT_ON_ORDER: 'Ligne {{line}} hors du bon de commande ({{actual}}).',
      QUANTITY_ABOVE_ACCEPTED:
        'Quantité facturée au-delà de l’accepté pour {{product}} : {{actual}} facturés au total pour {{expected}} acceptés.',
      PRICE_MISMATCH: 'Ligne {{line}} : prix {{actual}} au lieu de {{expected}}.',
      LINE_AMOUNT_MISMATCH:
        'Ligne {{line}} : montant {{actual}} au lieu de {{expected}} (quantité × prix).',
      TOTAL_MISMATCH: 'Total imprimé {{actual}} au lieu de {{expected}} (somme des lignes).',
      PRODUCT_MISMATCH: 'Ligne {{line}} : produit différent de celui du bon de commande.',
    },
    mismatch_order_line: 'une ligne du bon',
    new_title: 'Nouvelle facture fournisseur',
    another: 'Nouvelle facture',
    supplier_label: 'Fournisseur de la facture',
    supplier_placeholder: 'Choisir le fournisseur',
    form_offline:
      'Hors ligne : la facture sera enregistrable au retour du réseau ; votre saisie est gardée.',
    form_denied: 'Lecture non accessible avec vos droits.',
    invoices_failed:
      'Factures du fournisseur indisponibles : le doublon de référence ne peut pas être contrôlé.',
    retry_invoices: 'Relire les factures du fournisseur',
    retry_orders: 'Relire les bons de commande',
    retry_matching: 'Relire le rapprochement du bon',
    reading: 'Lecture des bons et des factures du fournisseur en cours…',
    order_label: 'Bon de commande',
    without_order: 'Sans bon de commande',
    order_option: '{{doc}} ({{status}})',
    orders_failed: 'Bons de commande indisponibles : le serveur n’a pas répondu.',
    matching_failed: 'Rapprochement du bon indisponible : le serveur n’a pas répondu.',
    ref_label: 'Référence de la facture (fournisseur)',
    duplicate: 'Référence déjà enregistrée pour ce fournisseur ({{doc}}).',
    date_label: 'Date de la facture',
    due_label: 'Échéance (facultative)',
    due_default_one:
      'Par défaut : {{date}} (date de la facture + {{count}} jour, délai du fournisseur).',
    due_default_other:
      'Par défaut : {{date}} (date de la facture + {{count}} jours, délai du fournisseur).',
    due_default_none:
      'Par défaut : {{date}}, la date de la facture (ce fournisseur n’a pas de délai de paiement).',
    lines_legend: 'Lignes facturées',
    no_line: 'Aucune ligne : choisissez un bon de commande ou ajoutez une ligne.',
    line_default: 'Article',
    line_label: 'Ligne {{rank}}',
    line_description: 'Ligne {{rank}} : désignation',
    line_quantity: 'Ligne {{rank}} : quantité',
    line_quantity_unit: 'Ligne {{rank}} : quantité ({{unit}})',
    line_price: 'Ligne {{rank}} : prix unitaire (F CFA)',
    line_price_unit: 'Ligne {{rank}} : prix par {{unit}} (F CFA)',
    line_total: 'Ligne {{rank}} : montant de la facture (F CFA)',
    line_skipped: 'Ligne non facturée.',
    line_incomplete: 'Montant à compléter.',
    line_amount: 'Montant : {{amount}}',
    line_remove: 'Retirer la ligne {{rank}}',
    line_add: 'Ajouter une ligne hors bon de commande',
    printed_label: 'Total imprimé sur la facture (facultatif)',
    comment_label: 'Commentaire (facultatif)',
    photo_label: 'Photo de la facture',
    preview_conforming:
      'Aperçu indicatif : rapprochement conforme ({{total}}), la facture sera approuvée.',
    preview_mismatch:
      'Aperçu indicatif : écart de rapprochement ({{total}}), la facture sera à décider :',
    preview_error: {
      PO_LINE_MISMATCH: 'Une ligne ne correspond pas au bon de commande choisi.',
      INVOICE_LINE_INVALID:
        'Ligne incomplète : quantité et prix sont attendus pour une ligne du bon de commande.',
      AMOUNT_INVALID: 'Montant invalide sur une ligne.',
    },
    issue: {
      SUPPLIER: 'Choisissez le fournisseur.',
      REF: 'Indiquez la référence de la facture.',
      DUPLICATE: 'Référence déjà enregistrée pour ce fournisseur.',
      DATE: 'Date de facture invalide.',
      DUE_DATE: 'L’échéance précède la date de la facture.',
      NO_LINE: 'Ajoutez au moins une ligne facturée.',
      LINE_DESCRIPTION: 'Chaque ligne porte une désignation.',
      LINE_QUANTITY: 'Quantité invalide sur une ligne (nombre positif, trois décimales au plus).',
      LINE_PRICE: 'Prix unitaire invalide sur une ligne.',
      LINE_TOTAL: 'Montant manquant ou invalide sur une ligne.',
      PRINTED_TOTAL: 'Total imprimé invalide.',
      TOO_MANY_LINES: '100 lignes au plus par facture.',
    },
    submit: 'Enregistrer la facture',
    record_result: {
      WAITING_offline: 'Facture enregistrée sur ce téléphone : elle partira au retour du réseau.',
      WAITING_online: 'Facture en cours d’envoi…',
      DONE: 'Facture {{doc}} enregistrée.',
      WARNING: 'Facture {{doc}} enregistrée, avec un signalement.',
      CONFLICT: 'Facture en attente d’examen (conflit).',
      REJECTED: 'Facture refusée : {{reason}}',
    },
    after: {
      APPROVED: 'Rapprochement conforme : facture approuvée, la dette est ouverte.',
      MISMATCH:
        'Écart de rapprochement : la facture attend une décision (contestation ou approbation motivée).',
    },
  },
  costs_margins: {
    offline_banner: 'Hors ligne : les chiffres affichés peuvent être périmés.',
    online_only: 'Lecture en ligne seulement : reconnectez-vous pour l’afficher.',
    refreshing: 'Actualisation en cours…',
    denied: 'Non accessible avec vos droits.',
    not_visible: 'non visible',
    block_hidden: 'Non visible avec vos droits.',
    axes_note:
      'Les marges par produit, point de vente et commercial viendront avec l’explorateur des indicateurs.',
    period_title: 'Période',
    period_label: 'Période étudiée',
    period: {
      MONTH: 'Mois en cours',
      WEEK: 'Semaine en cours',
      TODAY: 'Aujourd’hui',
      YEAR: 'Année en cours',
      CUSTOM: 'Autre période',
    },
    period_range: 'Du {{from}} au {{to}}',
    overview_title: 'Vue d’ensemble de la période',
    overview_failed: 'Vue d’ensemble indisponible : le serveur n’a pas répondu.',
    retry_overview: 'Relire la vue d’ensemble',
    revenue_title: 'Ventes',
    // Refonte (lot L8) : tuiles de la vue d'ensemble (libellé, valeur, puis une précision).
    tile_revenue: 'Chiffre d’affaires net',
    tile_revenue_detail_one: 'Brut {{gross}} · annulé {{cancelled}} · {{count}} vente',
    tile_revenue_detail_other: 'Brut {{gross}} · annulé {{cancelled}} · {{count}} ventes',
    tile_margin: 'Marge brute des ventes',
    tile_margin_detail: 'Coût des ventes {{cost}}',
    collected_detail_one: '{{count}} encaissement, annulations déduites',
    collected_detail_other: '{{count}} encaissements, annulations déduites',
    expenses_detail_one: '{{count}} dépense enregistrée, approuvée ou payée',
    expenses_detail_other: '{{count}} dépenses enregistrées, approuvées ou payées',
    tile_receivables: 'Créances clients',
    receivables_open_detail_one: '{{count}} vente ouverte',
    receivables_open_detail_other: '{{count}} ventes ouvertes',
    tile_payables: 'Dettes fournisseurs',
    payables_detail: 'échu {{overdue}} · avances {{advance}} · net {{net}}',
    tile_cash: 'Trésorerie',
    collected_title: 'Encaissé',
    expenses_title: 'Dépenses',
    expense_category: '{{label}} : {{amount}} ({{count}})',
    expenses_pending:
      'En attente de validation : {{pending}} · refusées après paiement : {{unjustified}}',
    balances_title: 'Soldes au {{date}}',
    balances_note:
      'Créances, dettes et trésorerie sont lues à ce jour, pas à la fin de la période : elles ne se déduisent pas des ventes et de l’encaissé ci-dessus.',
    receivables_overdue_one: 'échu {{amount}} ({{count}} vente)',
    receivables_overdue_other: 'échu {{amount}} ({{count}} ventes)',
    receivables_hidden: 'Créances clients : non visibles avec vos droits.',
    payables_hidden: 'Dettes fournisseurs : non visibles avec vos droits.',
    cash_type: '{{type}} : {{amount}}',
    cash_hidden: 'Trésorerie : non visible avec vos droits.',
    losses_title: 'Pertes de la période',
    losses_note:
      'Valeur indicative des pertes déclarées, au coût : elle n’entre pas dans le coût des lots.',
    losses_group_label: 'Regrouper les pertes',
    losses_group: {
      product: 'Par produit',
      lot: 'Par lot',
      reason: 'Par motif',
    },
    losses_failed: 'Pertes indisponibles : le serveur n’a pas répondu.',
    retry_losses: 'Relire les pertes',
    losses_empty: 'Aucune perte déclarée sur la période.',
    losses_lot_note: 'Par lot : seules les pertes rattachées à un lot sont comptées.',
    losses_lot_empty: 'Aucune perte rattachée à un lot sur la période.',
    losses_lot_total:
      'Valeur des pertes rattachées à un lot : {{amount}} (pertes sans lot exclues)',
    lot_unknown: 'lot {{id}}',
    reason_unknown: 'motif {{id}}',
    period_needed: 'Choisissez une période valide pour lire ces chiffres.',
    losses_total: 'Valeur des pertes : {{amount}}',
    loss_quantity_one: 'Perdu : {{quantity}} ({{count}} déclaration)',
    loss_quantity_other: 'Perdu : {{quantity}} ({{count}} déclarations)',
    loss_declarations_one: '{{count}} déclaration',
    loss_declarations_other: '{{count}} déclarations',
    loss_pending_quantity: 'En attente de validation : {{quantity}} ({{amount}})',
    loss_pending: 'En attente de validation : {{amount}}',
    lots_title: 'Coûts et marges d’un lot',
    lot_status_label: 'Lots',
    lot_status: {
      ACTIVE: 'En cours',
      SELLING: 'En vente',
      CLOSED: 'Clôturés',
    },
    lot_label: 'Lot',
    lot_placeholder: 'Choisir un lot',
    lot_option: '{{code}} · {{product}}',
    lots_failed: 'Lots indisponibles : le serveur n’a pas répondu.',
    retry_lots: 'Relire les lots',
    lots_empty: 'Aucun lot dans ce statut.',
    lots_truncated: 'Seuls les {{limit}} lots les plus récents sont proposés.',
    lot_failed: 'Fiche du lot indisponible : le serveur n’a pas répondu.',
    retry_lot: 'Relire la fiche du lot',
    lot_card: 'Lot {{code}}',
    lot_costs_title: 'Coûts du lot {{code}}',
    cost_line: '{{type}} : {{amount}}',
    cost_total: 'Coût net {{net}} · coût restant {{remaining}}',
    cost_per_head: 'Coût restant par tête : {{amount}}',
    lot_margins_title: 'Ventes et marges du lot',
    lot_revenue_one:
      'Chiffre d’affaires net {{revenue}}, dont produits d’abattage {{slaughter}} ({{count}} tête vendue)',
    lot_revenue_other:
      'Chiffre d’affaires net {{revenue}}, dont produits d’abattage {{slaughter}} ({{count}} têtes vendues)',
    lot_margin: 'Marge du lot (chiffre d’affaires net − coût net du lot) : {{amount}}',
    lot_gross_margin:
      'Coût des ventes {{cost}} · marge brute des ventes (chiffre d’affaires net − coût des ventes) : {{amount}}',
    margins_note:
      'Le chiffre d’affaires d’un lot comprend ses produits d’abattage et exclut les œufs, déjà portés en production transférée.',
    no_sales: 'Aucune vente pour ce lot.',
    lot_sheet_link: 'Ouvrir la fiche du lot {{code}}',
    entries_show: 'Voir les écritures de coût de {{code}}',
    entries_hide: 'Masquer les écritures de coût de {{code}}',
    entries_failed: 'Écritures de coût indisponibles : le serveur n’a pas répondu.',
    retry_entries: 'Relire les écritures de coût',
    entries_empty: 'Aucune écriture de coût pour ce lot.',
    entries_truncated: 'Liste partielle : seules les écritures les plus récentes sont affichées.',
    entry: {
      DEBIT: '{{date}} · {{type}} : {{amount}} ({{source}})',
      CREDIT: '{{date}} · {{type}} : − {{amount}} ({{source}})',
    },
    entry_reversal: 'corrige une écriture antérieure',
    source: {
      STOCK_MOVE: 'mouvement de stock',
      EXPENSE: 'dépense',
      MANUAL: 'saisie manuelle',
      OVERHEAD_ENTRY: 'frais généraux',
      ALLOCATION: 'répartition des frais généraux',
      PRODUCTION: 'production',
    },
  },
  receivables: {
    offline_banner: 'Hors ligne : les créances affichées peuvent être périmées.',
    online_only: 'Lecture en ligne seulement : reconnectez-vous pour afficher les créances.',
    refreshing: 'Actualisation en cours…',
    denied: 'Non accessible avec vos droits.',
    failed: 'Créances indisponibles : le serveur n’a pas répondu.',
    retry: 'Relire les créances',
    summary_title: 'Encours',
    as_of: 'Situation au {{date}}',
    aging_title: 'Encours par retard',
    buckets_label: 'Échu par retard',
    bucket_line: '{{label}} : {{amount}}',
    bucket: {
      '0-30': '1 à 30 jours de retard',
      '31-60': '31 à 60 jours de retard',
      '61-90': '61 à 90 jours de retard',
      '>90': 'Plus de 90 jours de retard',
    },
    scope_note:
      'Ventes à crédit d’un client identifié, dans votre périmètre ; une vente au comptant restée impayée sans client n’a pas d’échéance et n’apparaît pas ici.',
    list_title: 'Liste des créances',
    view_label: 'Afficher',
    view: {
      ALL: 'Toutes les créances',
      OVERDUE: 'Échues',
      NOT_DUE: 'À échoir',
      '0-30': '1 à 30 jours de retard',
      '31-60': '31 à 60 jours de retard',
      '61-90': '61 à 90 jours de retard',
      '>90': 'Plus de 90 jours de retard',
    },
    site_label: 'Site',
    all_sites: 'Tous les sites',
    grouping_label: 'Regrouper par',
    grouping: {
      customer: 'Client',
      owner: 'Titulaire (commercial, à défaut vendeur)',
    },
    names_failed:
      'Noms des clients indisponibles : les clients sont désignés par un identifiant abrégé.',
    retry_names: 'Relire les noms des clients',
    owners_failed:
      'Noms des titulaires indisponibles : ils sont désignés par un identifiant abrégé.',
    retry_owners: 'Relire les noms des titulaires',
    names_truncated: 'Liste des clients partielle : certains noms restent abrégés.',
    selection_one: 'Sélection : {{amount}} dû, dont {{overdue}} échu ({{count}} vente)',
    selection_other: 'Sélection : {{amount}} dû, dont {{overdue}} échu ({{count}} ventes)',
    empty: 'Aucune créance ouverte.',
    filter_empty: 'Aucune créance pour ce choix.',
    reminder_note:
      'Les relances ne sont pas enregistrées dans l’application : appelez le client, ou passez par son commercial.',
    no_customer: 'Sans client identifié',
    customer_unknown: 'client {{id}}',
    group_overdue_one: 'Échu {{amount}} · jusqu’à {{count}} jour de retard',
    group_overdue_other: 'Échu {{amount}} · jusqu’à {{count}} jours de retard',
    group_not_due: 'Rien d’échu',
    group_sales_one: '{{count}} vente à régler · échéance la plus ancienne : {{date}}',
    group_sales_other: '{{count}} ventes à régler · échéance la plus ancienne : {{date}}',
    call: 'Appeler le {{phone}}',
    customer_sheet: 'Fiche de {{name}}',
    collect: 'Encaisser auprès de {{name}}',
    show_sales: 'Voir les ventes de {{name}}',
    hide_sales: 'Masquer les ventes de {{name}}',
    show_sales_self: 'Voir mes ventes',
    hide_sales_self: 'Masquer mes ventes',
    // Refonte (lot L8) : tuiles de l'encours, colonnes du tableau et libellé visible court des
    // actions (le nom accessible reste la phrase complète ci-dessus).
    tile_total: 'Dû par les clients',
    tile_total_detail_one: '{{count}} vente',
    tile_total_detail_other: '{{count}} ventes',
    tile_overdue: 'Échu',
    tile_overdue_detail: '{{percent}} % de l’encours',
    tile_not_due: 'À échoir',
    col_customer: 'Client',
    col_owner: 'Titulaire',
    col_balance: 'Dû',
    col_overdue: 'Échu',
    collect_short: 'Encaisser',
    show_sales_short: 'Ventes',
    hide_sales_short: 'Masquer',
    owner_self: 'Mes créances',
    sale_dates: 'Vendue le {{sold}} · échéance le {{due}}',
    days_overdue_one: '{{count}} jour de retard',
    days_overdue_other: '{{count}} jours de retard',
    sale_not_due: 'À échoir',
    sale_balance: 'Reste dû {{balance}} sur {{total}} (payé {{paid}})',
    collect_sale: 'Encaisser le reste dû de {{doc}}',
  },
  order_matching: {
    offline_banner: 'Hors ligne : les bons affichés peuvent être périmés.',
    online_only: 'Rapprochement en ligne seulement : reconnectez-vous pour le lire.',
    list_title: 'Bons de commande',
    col: {
      order: 'Bon',
      supplier: 'Fournisseur',
      dates: 'Dates et montant',
      gaps: 'Écarts',
    },
    group_label: 'Bons à afficher',
    group: {
      OPEN: 'Attendus (envoyés ou reçus en partie)',
      RECEIVED: 'Reçus',
      CLOSED: 'Clôturés',
    },
    filter_supplier: 'Filtrer par fournisseur',
    gaps_only: 'Seulement les bons avec un écart',
    denied: 'Bons de commande non accessibles avec vos droits.',
    failed: 'Bons de commande indisponibles : le serveur n’a pas répondu.',
    failed_stale:
      'Actualisation impossible : le serveur n’a pas répondu. La liste ci-dessous peut être périmée.',
    retry_list: 'Relire les bons',
    refreshing: 'Actualisation en cours…',
    empty: 'Aucun bon dans ce groupe.',
    no_gap: 'Aucun bon de ce groupe ne présente d’écart.',
    truncated: 'Liste partielle : seuls les plus récents sont affichés.',
    card_dates: 'commandé le {{date}}',
    expected: 'livraison prévue le {{date}}',
    total: 'total {{amount}}',
    indicator: {
      overdue_one: 'Date de livraison prévue dépassée de {{count}} jour, reliquat attendu.',
      overdue_other: 'Date de livraison prévue dépassée de {{count}} jours, reliquat attendu.',
      remaining_one: 'Reliquat sur {{count}} ligne.',
      remaining_other: 'Reliquat sur {{count}} lignes.',
      rejected: 'Quantités rejetées à la réception.',
      quarantined: 'Réception en quarantaine, à trancher.',
      excess: 'Excédent reçu au-delà du commandé.',
      over_invoiced: 'Facturé au-delà de l’accepté.',
    },
    line_summary: '{{product}} : commandé {{ordered}}, accepté {{accepted}}, reste {{remaining}}',
    detail_show: 'Voir le rapprochement de {{doc}}',
    detail_hide: 'Masquer le rapprochement de {{doc}}',
    lines_title: 'Rapprochement par ligne',
    line_label: 'Ligne {{product}}',
    line_flow:
      'Commandé {{ordered}} · livré {{delivered}} · rejeté {{rejected}} · accepté {{accepted}}',
    line_rest: 'Reste {{remaining}} · clôturé {{closed}} · facturé {{invoiced}}',
    line_quarantined:
      'En quarantaine : {{quantity}}, sans effet sur le stock tant que non tranché.',
    line_excess: 'Excédent reçu : {{quantity}}.',
    line_over_invoiced:
      'Facturé {{invoiced}} pour {{accepted}} acceptés : écart à régler par un avoir.',
    line_values:
      'Valeurs au prix du bon : commandé {{ordered}} · accepté {{accepted}} · facturé {{invoiced}}',
    totals:
      'Total du bon : commandé {{ordered}} · accepté {{accepted}} · facturé {{invoiced}} au prix du bon · payé {{paid}}',
    not_available: 'non disponible',
    matching_failed: 'Rapprochement indisponible : le serveur n’a pas répondu.',
    retry_matching: 'Relire le rapprochement de {{doc}}',
    section_denied: 'Non accessible avec vos droits.',
    receipts_title: 'Réceptions',
    receipts_failed: 'Réceptions indisponibles : le serveur n’a pas répondu.',
    retry_receipts: 'Relire les réceptions de {{doc}}',
    no_receipt: 'Aucune réception pour ce bon.',
    delivery_note: 'bon de livraison {{ref}}',
    receipt_line: '{{product}} : livré {{delivered}}, rejeté {{rejected}}, accepté {{accepted}}',
    cancel_comment: 'Motif de l’annulation : {{comment}}',
    invoices_title: 'Factures du bon',
    invoices_failed: 'Factures indisponibles : le serveur n’a pas répondu.',
    retry_invoices: 'Relire les factures de {{doc}}',
    no_invoice: 'Aucune facture pour ce bon.',
    invoice_line: 'Réf. fournisseur {{ref}} · {{total}} · payé {{paid}}',
    invoices_link: 'Ouvrir les factures fournisseurs',
  },
  supplier_payment: {
    online_only: 'Lecture en ligne seulement : reconnectez-vous pour afficher les paiements.',
    offline_banner:
      'Hors ligne : les paiements affichés peuvent être périmés ; affectations, annulations et enregistrement attendent le retour du réseau.',
    form_offline:
      'Hors ligne : le paiement sera enregistrable au retour du réseau ; votre saisie est gardée.',
    denied: 'Paiements fournisseurs non accessibles avec vos droits.',
    failed: 'Paiements indisponibles : le serveur n’a pas répondu.',
    failed_stale:
      'Actualisation impossible : le serveur n’a pas répondu. La liste ci-dessous peut être périmée ; les actions restent bloquées jusqu’à une relecture.',
    empty: 'Aucun paiement dans ce groupe.',
    list_title: 'Paiements',
    group_label: 'Paiements à afficher',
    group: {
      PENDING: 'En attente de la Direction',
      RECORDED: 'Décaissés',
      ADVANCES: 'Avances à affecter',
      CLOSED: 'Refusés ou annulés',
    },
    filter_supplier: 'Filtrer par fournisseur',
    decide_link: 'Décider les paiements dans la file des validations',
    accounts_failed: 'Comptes de trésorerie indisponibles : le serveur n’a pas répondu.',
    retry_accounts: 'Relire les comptes',
    retry_list: 'Relire les paiements',
    retry_payables: 'Relire les factures à payer',
    retry_allocations: 'Relire les affectations de {{doc}}',
    card_meta: '{{method}} depuis {{account}} · saisi le {{date}} par {{author}}',
    // Refonte (lot L8) : colonnes du tableau des paiements et libellés courts des actions (le nom
    // accessible reste la phrase complète du document).
    col_payment: 'Paiement',
    col_amount: 'Montant',
    col_status: 'Situation',
    allocate_short: 'Affecter',
    cancel_short: 'Annuler',
    allocations_show_short: 'Voir les affectations',
    allocations_hide_short: 'Masquer les affectations',
    status: {
      PENDING_APPROVAL: 'En attente de la Direction (rien n’est décaissé)',
      RECORDED: 'Décaissé',
      REJECTED: 'Refusé par la Direction',
      CANCELLED: 'Annulé',
    },
    unallocated: 'avance non affectée : {{amount}}',
    decided_at: 'décidé le {{date}}',
    disbursed_at: 'décaissé le {{date}}',
    cancel_reason: 'Motif de l’annulation : {{reason}}',
    allocations_show: 'Voir les affectations de {{doc}}',
    allocations_hide: 'Masquer les affectations de {{doc}}',
    allocations_failed: 'Affectations indisponibles : le serveur n’a pas répondu.',
    allocations_denied: 'Affectations de ce paiement non accessibles avec vos droits.',
    no_allocation: 'Aucune facture payée : tout le montant est une avance.',
    no_allocation_pending:
      'Aucune facture prévue : le montant deviendra une avance si la Direction l’approuve.',
    no_allocation_closed: 'Aucune affectation.',
    allocation_read: '{{invoice}} : {{amount}} ({{status}})',
    allocate_open: 'Affecter l’avance de {{doc}}',
    cancel_open: 'Annuler {{doc}}',
    allocate_legend: 'Affecter l’avance de {{doc}} ({{amount}} disponibles)',
    allocation_short: '{{doc}} (reste dû {{balance}})',
    spread: 'Répartir sur les factures, la plus ancienne échéance d’abord',
    no_invoice: 'Aucune facture à payer pour ce fournisseur.',
    plan_error: {
      ALLOCATION_INVALID:
        'Affectation impossible : seule une facture approuvée de ce fournisseur, non soldée, reçoit un paiement, une seule fois.',
      ALLOCATION_EXCEEDS: 'Une affectation dépasse le reste dû de sa facture.',
      SUPPLIER_PAYMENT_EXCEEDS: 'Les affectations dépassent le montant disponible.',
      ADVANCE_NOT_ALLOWED:
        'Le montant dépasse les factures affectées et les avances ne sont pas admises : affectez tout le montant.',
      AMOUNT_INVALID: 'Montant d’affectation invalide : un nombre entier positif est attendu.',
    },
    allocate_submit: 'Affecter',
    cancel_reason_label: 'Motif de l’annulation',
    cancel_recorded_note:
      'Le montant revient au compte et les factures payées par ce paiement redeviennent dues.',
    cancel_pending_note: 'Rien n’a été décaissé : la demande à la Direction est close.',
    cancel_submit: 'Annuler le paiement',
    allocate_result: {
      WAITING_offline:
        'Affectation enregistrée sur ce téléphone : elle partira au retour du réseau.',
      WAITING_online: 'Affectation en cours d’envoi…',
      DONE: 'Avance de {{doc}} affectée.',
      WARNING: 'Avance de {{doc}} affectée, avec un signalement.',
      CONFLICT: 'Affectation en attente d’examen (conflit).',
      REJECTED: 'Affectation refusée : {{reason}}',
    },
    cancel_result: {
      WAITING_offline:
        'Annulation enregistrée sur ce téléphone : elle partira au retour du réseau.',
      WAITING_online: 'Annulation en cours d’envoi…',
      DONE: 'Paiement {{doc}} annulé.',
      WARNING: 'Paiement {{doc}} annulé, avec un signalement.',
      CONFLICT: 'Annulation en attente d’examen (conflit).',
      REJECTED: 'Annulation refusée : {{reason}}',
    },
    record_result: {
      WAITING_offline: 'Paiement enregistré sur ce téléphone : il partira au retour du réseau.',
      WAITING_online: 'Paiement en cours d’envoi…',
      DONE: 'Paiement {{doc}} enregistré.',
      WARNING: 'Paiement {{doc}} enregistré, avec un signalement.',
      CONFLICT: 'Paiement en attente d’examen (conflit).',
      REJECTED: 'Paiement refusé : {{reason}}',
    },
    new_title: 'Nouveau paiement fournisseur',
    another: 'Nouveau paiement',
    correct: 'Corriger la saisie',
    supplier_label: 'Fournisseur du paiement',
    supplier_inactive: '{{name}} (inactif)',
    pending_failed:
      'Paiements en attente de ce fournisseur indisponibles : le serveur n’a pas répondu.',
    retry_pending: 'Relire les paiements en attente',
    planned_on_invoice: 'Déjà prévu : {{amount}} par {{docs}}, en attente de la Direction.',
    supplier_placeholder: 'Choisir le fournisseur',
    pending_for_supplier_one:
      '{{count}} paiement de ce fournisseur attend la Direction ({{total}}) : ses affectations ne sont pas encore déduites des restes dus.',
    pending_for_supplier_other:
      '{{count}} paiements de ce fournisseur attendent la Direction ({{total}}) : leurs affectations ne sont pas encore déduites des restes dus.',
    amount_label: 'Montant payé (F CFA)',
    method_label: 'Moyen de paiement',
    method_placeholder: 'Choisir le moyen',
    account_label: 'Payé depuis le compte',
    account_placeholder: 'Choisir le compte',
    account_option: '{{name}} (solde {{balance}})',
    no_account: 'Aucun compte actif pour ce moyen de paiement.',
    pos_session_note: 'Caisse de point de vente : une session de caisse ouverte est exigée.',
    reference_label: 'Référence (facultative)',
    reference_required: 'Référence du paiement (exigée par ce moyen)',
    allocations_legend: 'Factures payées',
    allocation_label: '{{doc}} (réf. {{ref}}) · reste dû {{balance}} · échéance le {{due}}',
    overdue_one: 'Échue depuis {{count}} jour.',
    overdue_other: 'Échue depuis {{count}} jours.',
    comment_label: 'Commentaire (facultatif)',
    advance: 'Reste non affecté : {{amount}}, enregistré comme avance au fournisseur.',
    needs_approval:
      'Au-delà de {{threshold}} (seuil indicatif) : le paiement attendra la décision de la Direction, rien ne sera décaissé avant.',
    cash_insufficient:
      'Solde de la caisse insuffisant : décaissé tout de suite, ce paiement serait refusé par le serveur.',
    issue: {
      SUPPLIER: 'Choisissez le fournisseur.',
      AMOUNT: 'Indiquez un montant entier positif.',
      METHOD: 'Choisissez le moyen de paiement.',
      ACCOUNT: 'Choisissez le compte d’où payer.',
      REFERENCE: 'Ce moyen de paiement exige une référence.',
      ALLOCATION_AMOUNT: 'Montant invalide sur une facture (nombre entier positif).',
      TOO_MANY_ALLOCATIONS: '50 factures au plus par paiement.',
    },
    reading: 'Lecture des factures à payer du fournisseur en cours…',
    form_denied: 'Lecture non accessible avec vos droits.',
    payables_failed: 'Factures à payer indisponibles : le serveur n’a pas répondu.',
    submit: 'Enregistrer le paiement',
    after: {
      RECORDED: 'Paiement décaissé : le compte et les restes dus sont mis à jour.',
      PENDING_APPROVAL:
        'Paiement en attente de la Direction : rien n’est décaissé avant sa décision.',
    },
  },
  expense: {
    views_label: 'Vues des dépenses',
    entry_tab: 'Saisir une dépense',
    follow_tab: 'Suivi des dépenses',
    offline_banner:
      'Hors ligne : la dépense sera enregistrée sur ce téléphone et envoyée au retour du réseau.',
    new_title: 'Nouvelle dépense',
    payment_legend: 'Paiement',
    paid_now: 'Payée maintenant',
    to_pay: 'À payer par la Finance',
    no_account:
      'Aucune caisse n’est servie à ce téléphone : la dépense sera payée plus tard par la Finance.',
    account_label: 'Caisse qui paie',
    account_placeholder: 'Choisir la caisse',
    balance: 'Solde indicatif de la caisse : {{amount}}',
    balance_blind: 'Solde de la caisse du point de vente non affiché (comptage à l’aveugle).',
    balance_unknown: 'Solde de cette caisse inconnu sur ce téléphone.',
    over_balance:
      'Montant au-delà du solde indicatif de la caisse : en ligne, le serveur refusera une caisse négative ; hors ligne, la dépense sera enregistrée et signalée à la Finance.',
    category_label: 'Catégorie',
    category_placeholder: 'Choisir la catégorie',
    no_category:
      'Aucune catégorie de dépense sur ce téléphone : synchronisez avec du réseau, puis réessayez.',
    amount_label: 'Montant (F CFA)',
    description_label: 'Objet de la dépense',
    photo_label: 'Justificatif (photo du reçu)',
    photo_hint:
      'Exigé au-delà de {{threshold}} : sans lui, la dépense est refusée en ligne, et signalée à la Finance hors ligne.',
    site_label: 'Site de la dépense',
    site_placeholder: 'Choisir le site',
    site_fixed: 'Site : {{name}}',
    no_site: 'Aucun site actif sur ce téléphone : synchronisez avec du réseau.',
    imputation_label: 'Imputation du coût',
    imputation: {
      SITE: 'Dépense directe du site',
      NONE: 'Non imputée',
      OVERHEAD_VOLAILLE: 'Frais généraux volaille (répartis entre les lots)',
      OVERHEAD_PORC: 'Frais généraux porc (répartis entre les lots)',
      PRODUCTION_LOT: 'Lot {{code}}',
      INCUBATION_BATCH: 'Incubation {{code}}',
    },
    payee_label: 'Bénéficiaire (facultatif)',
    supplier_label: 'Fournisseur (facultatif)',
    supplier_none: 'Aucun',
    issue: {
      CATEGORY: 'Choisissez la catégorie.',
      AMOUNT: 'Montant invalide : un nombre entier de francs, supérieur à zéro.',
      AMOUNT_EMPTY: 'Indiquez le montant.',
      DESCRIPTION: 'Indiquez l’objet de la dépense.',
      SITE: 'Choisissez le site de la dépense.',
      ACCOUNT: 'Choisissez la caisse qui paie.',
    },
    review: {
      CATEGORY_INACTIVE: 'catégorie désactivée',
      CATEGORY_NOT_ALLOWED_ON_POS: 'catégorie non permise à la caisse du point de vente',
      POS_CAP_EXCEEDED: 'montant au-delà du plafond de la caisse du point de vente ({{cap}})',
      ATTACHMENT_MISSING: 'justificatif manquant',
      COST_OBJECT_CLOSED: 'lot clos',
    },
    review_online: 'Envoi impossible en ligne : {{reasons}}.',
    review_offline:
      'Hors ligne : la dépense sera enregistrée puis placée en validation a posteriori ({{reasons}}).',
    review_offline_flagged:
      'Hors ligne : la dépense sera enregistrée et signalée à la Finance ({{reasons}}).',
    approval_announce: 'Au-delà de {{threshold}} : la dépense sera soumise à validation.',
    submit: 'Enregistrer la dépense',
    another: 'Nouvelle dépense',
    result: {
      WAITING_offline: 'Dépense enregistrée sur ce téléphone : elle partira au retour du réseau.',
      WAITING_online: 'Dépense en cours d’envoi…',
      DONE: 'Dépense {{doc}} enregistrée.',
      WARNING: 'Dépense {{doc}} enregistrée avec un signalement.',
      WARNING_EXPENSE_REVIEW_REQUIRED:
        'Dépense {{doc}} enregistrée hors ligne avec un point à revoir : la Finance est prévenue.',
      WARNING_CASH_NEGATIVE:
        'Dépense {{doc}} enregistrée : la caisse est passée en négatif, la Finance est prévenue.',
      WARNING_CASH_SESSION_MISSING:
        'Dépense {{doc}} enregistrée sans caisse ouverte : la Finance est prévenue.',
      WARNING_LOCAL_REF_DUPLICATE:
        'Dépense {{doc}} enregistrée ; sa référence locale était déjà prise sur ce téléphone.',
      CONFLICT: 'Dépense {{doc}} en attente d’examen (conflit).',
      REJECTED: 'Dépense refusée : {{reason}}',
    },
    after: {
      PENDING_APPROVAL: 'Statut : en attente de validation.',
      PAID_PENDING_APPROVAL: 'Statut : payée, en attente de validation.',
    },
    mine_title: 'Mes dépenses',
    mine_empty: 'Aucune dépense ces 30 derniers jours.',
    pending_label: 'Dépense de {{amount}} ({{time}})',
    payee: 'à {{name}}',
    paid_from: 'Payée par {{account}}',
    not_paid: 'À payer',
    to_pay_after_approval: 'À payer après validation',
    other_account: 'une autre caisse',
    state_waiting: 'En attente d’envoi.',
    state_unpulled: 'Envoyée, en attente de la prochaine synchronisation.',
    cancelling_WAITING: 'Annulation en attente d’envoi.',
    cancelling_SENT: 'Annulation envoyée, en attente de la prochaine synchronisation.',
    status: {
      RECORDED: 'Enregistrée, à payer',
      PENDING_APPROVAL: 'En attente de validation',
      APPROVED: 'Validée, à payer',
      REJECTED: 'Refusée',
      PAID: 'Payée',
      PAID_PENDING_APPROVAL: 'Payée, en attente de validation',
      PAID_REJECTED: 'Refusée après paiement : reclassée « non justifiée »',
      CANCELLED: 'Annulée',
    },
    review_reasons: 'À revoir : {{reasons}}.',
    review_reasons_past: 'Relevé à la saisie : {{reasons}}.',
    rejection: 'Motif du refus : {{comment}}',
    cancel_reason: 'Motif de l’annulation : {{reason}}',
    attach_receipt: 'Joindre le justificatif de {{label}}',
    cancel_open: 'Annuler {{label}}',
    cancel_reason_label: 'Motif de l’annulation',
    cancel_submit: 'Confirmer l’annulation',
    cancel_online_only: 'Annulation possible en ligne seulement.',
    close_form: 'Fermer',
    cancel_result: {
      WAITING_offline:
        'Annulation enregistrée sur ce téléphone : elle partira au retour du réseau.',
      WAITING_online: 'Annulation en cours d’envoi…',
      DONE: 'Dépense {{doc}} annulée.',
      WARNING: 'Dépense {{doc}} annulée avec un signalement.',
      CONFLICT: 'Annulation en attente d’examen (conflit).',
      REJECTED: 'Annulation refusée : {{reason}}',
    },
    you: 'vous',
    follow_online_only: 'Suivi des dépenses en ligne seulement : reconnectez-vous.',
    follow_denied: 'Suivi des dépenses non accessible avec vos droits.',
    follow_unavailable: 'Suivi des dépenses indisponible : le serveur n’a pas répondu.',
    follow_failed_stale:
      'Actualisation impossible : le serveur n’a pas répondu. La liste ci-dessous peut être périmée ; les actions restent bloquées jusqu’à une relecture.',
    follow_empty: 'Aucune dépense dans ce groupe.',
    none_to_pay: 'Aucune dépense à payer.',
    refreshing: 'Actualisation en cours… Les actions reprennent à la réponse du serveur.',
    truncated: 'Liste partielle : seules les plus récentes sont montrées.',
    group_label: 'Dépenses',
    group: {
      TO_PAY: 'À payer',
      TO_DECIDE: 'En attente de validation',
      PAID: 'Payées',
      REJECTED: 'Refusées',
      CANCELLED: 'Annulées',
    },
    closed_window: 'Dépenses saisies depuis moins de {{days}} jours.',
    by: 'Saisie par {{name}}',
    captured_offline: 'saisie hors ligne',
    charged_to: 'À la charge de {{name}} (rejet après paiement).',
    no_receipt: 'Aucun justificatif joint.',
    receipt_alt: 'Justificatif de la dépense {{doc}}',
    view_receipt: 'Voir le justificatif de {{doc}}',
    receipt_uploading: 'Justificatif en cours d’envoi par l’auteur.',
    receipt_refused: 'Justificatif refusé par le serveur : l’auteur doit le renvoyer.',
    receipt_missing: 'Justificatif jamais reçu par le serveur : l’auteur doit le renvoyer.',
    decide_link: 'Décider dans les validations',
    pay_open: 'Payer {{doc}}',
    pay_account_label: 'Compte qui paie',
    pay_no_account: 'Aucun compte actif ne vous est accessible pour payer cette dépense.',
    pay_accounts_failed: 'Comptes indisponibles : le serveur n’a pas répondu. Réessayez.',
    pay_submit: 'Payer {{amount}}',
    pay_result: {
      WAITING_offline: 'Paiement enregistré sur ce téléphone : il partira au retour du réseau.',
      WAITING_online: 'Paiement en cours d’envoi…',
      DONE: 'Dépense {{doc}} payée.',
      WARNING: 'Dépense {{doc}} payée avec un signalement.',
      CONFLICT: 'Paiement en attente d’examen (conflit).',
      REJECTED: 'Paiement refusé : {{reason}}',
    },
  },
  treasury: {
    expenses_title: 'Dépenses à payer',
    expenses_follow_link: 'Toutes les dépenses',
    supplier_payments_title: 'Paiements fournisseurs en attente de la Direction',
    supplier_payments_note:
      'Au-delà du seuil, rien n’est décaissé avant la décision de la Direction.',
    supplier_payments_empty: 'Aucun paiement fournisseur en attente.',
    supplier_payments_denied: 'Paiements fournisseurs non accessibles avec vos droits.',
    supplier_payments_failed: 'Paiements fournisseurs indisponibles : le serveur n’a pas répondu.',
    supplier_payments_failed_stale:
      'Actualisation des paiements fournisseurs impossible : la liste ci-dessous peut être périmée.',
    supplier_payment_meta: '{{method}} depuis {{account}} · {{date}} · saisi par {{author}}',
    supplier_payment_reference: 'référence {{reference}}',
    supplier_payments_decide: 'Décider dans les validations',
    supplier_payment_status: {
      PENDING_APPROVAL: 'en attente de la Direction',
      RECORDED: 'décaissé',
      REJECTED: 'refusé',
      CANCELLED: 'annulé',
    },
    movement_document_label: '{{amount}} du {{date}}',
    movement_document_open: {
      EXPENSE: 'Voir la dépense ({{label}})',
      SUPPLIER_PAYMENT: 'Voir le paiement fournisseur ({{label}})',
    },
    movement_document_expense: '{{doc}} · {{category}} · {{description}} · {{status}}',
    movement_document_payment: '{{doc}} · {{supplier}} · {{status}}',
    suppliers_failed: 'Noms des fournisseurs indisponibles : le serveur n’a pas répondu.',
    movement_document_denied: 'Pièce non accessible avec vos droits.',
    movement_document_failed: 'Pièce indisponible : le serveur n’a pas répondu.',
    online_only:
      'Trésorerie en ligne seulement : reconnectez-vous. Les remises de fonds se font aussi hors ligne, depuis l’écran « Remises de fonds ».',
    denied: 'Trésorerie non accessible avec vos droits.',
    failed: 'Trésorerie indisponible : le serveur n’a pas répondu.',
    failed_stale:
      'Actualisation impossible : le serveur n’a pas répondu. Les chiffres ci-dessous peuvent être périmés ; les actions restent bloquées jusqu’à une relecture.',
    refreshing: 'Actualisation en cours… Les actions reprennent à la réponse du serveur.',
    partial: 'Liste partielle : seules les {{count}} plus récentes sont montrées.',
    // Refonte (lot L8) : tuiles de tête et colonnes des tableaux.
    tile_balance: 'Solde des comptes',
    tile_balance_detail_one: '{{count}} compte',
    tile_balance_detail_other: '{{count}} comptes',
    tile_sessions: 'Sessions à valider',
    tile_discrepancies: 'Écarts à décider',
    tile_transit: 'Remises à recevoir',
    col_account: 'Compte',
    col_type: 'Type',
    col_balance: 'Solde',
    col_payment: 'Paiement',
    col_amount: 'Montant',
    col_detail: 'Détail',
    you: 'vous',
    sessions_title: 'Sessions de caisse à valider',
    sessions_empty: 'Aucune session de caisse en attente de validation.',
    session_label: '{{account}} du {{date}}, {{from}}–{{to}}',
    session_people:
      'Ouverte par {{opened}} le {{openedAt}} ; clôturée par {{closed}} le {{closedAt}}.',
    opening_variance: 'Écart d’ouverture : {{amount}} (période précédente).',
    closing_variance: 'Écart de clôture : {{amount}} (compté {{counted}}, attendu {{expected}}).',
    force_closed: 'Clôture forcée par la Finance.',
    clock_suspect: 'Horloge de l’appareil suspecte à la saisie.',
    session_open: 'Examiner la session {{label}}',
    session_close: 'Replier la session {{label}}',
    adjustments_hint:
      'Les opérations tardives d’une session déjà validée forment des ajustements à valider à part : ils ne sont pas encore listés ici.',
    detail_denied: 'Détail de la session non accessible avec vos droits.',
    detail_failed: 'Détail de la session indisponible : le serveur n’a pas répondu.',
    detail_failed_stale:
      'Actualisation du détail impossible : le serveur n’a pas répondu ; les chiffres ci-dessous peuvent être périmés.',
    session_not_pending: 'Cette session n’est plus en attente de validation.',
    refresh_list: 'Relire la liste',
    flows_title: 'Flux de la session',
    window: 'Du {{from}} au {{to}}.',
    window_open: 'Depuis le {{from}}.',
    sellers_empty: 'Aucun encaissement en espèces dans la session.',
    seller_line_one: '{{who}} : {{amount}} ({{count}} encaissement)',
    seller_line_other: '{{who}} : {{amount}} ({{count}} encaissements)',
    unattributed: 'Encaissé sans vendeur identifié : {{amount}}.',
    other_line_one: '{{type}} : {{amount}} ({{count}} mouvement)',
    other_line_other: '{{type}} : {{amount}} ({{count}} mouvements)',
    net_flows: 'Flux nets : {{net}} ; attendu recalculé : {{expected}}.',
    sellers_hint:
      'Les encaissements par vendeur n’additionnent pas l’attendu : remboursements, remises et autres sorties s’y retranchent.',
    adjustments_title: 'Ajustements',
    adjustment_line: '{{cause}} du {{date}} : {{delta}}',
    adjustment_cause: {
      LATE_OPERATION: 'Opération tardive',
      REVERSAL: 'Annulation',
    },
    current_variance: 'Écart courant : {{amount}}',
    validator_involved:
      'Vous avez ouvert, clôturé ou forcé cette session : un autre valideur doit la valider.',
    reason_required: 'Motif de la validation (obligatoire : la session présente un écart)',
    reason_optional: 'Motif de la validation (facultatif)',
    validate_submit: 'Valider la session',
    discrepancies_title: 'Écarts de remise à décider',
    discrepancies_empty: 'Aucun écart de remise en attente.',
    discrepancy_line: 'Reçu {{received}} par {{receiver}} : écart de {{gap}}.',
    decision_block: {
      INVOLVED:
        'Vous avez envoyé, porté ou reçu cette remise : un autre valideur doit décider de l’écart.',
      NOT_PENDING: 'Cet écart n’est plus en attente.',
    },
    decide_open: 'Décider de l’écart de la remise {{doc}}',
    imputation_label: 'Imputation de l’écart',
    imputation_placeholder: 'Choisir à qui l’écart est imputé',
    imputation: {
      SENDER: 'Expéditeur',
      CARRIER: 'Porteur',
      RECEIVER: 'Récepteur',
      COMPANY: 'Entreprise',
    },
    decision_reason: 'Motif (obligatoire, 500 caractères au plus)',
    decide_submit: 'Clore l’écart',
    transit_title: 'Remises en transit',
    transit_empty: 'Aucune remise en transit.',
    transfer_people: 'Envoyée le {{date}} par {{sender}} ; porteur : {{carrier}}.',
    accounts_title: 'Comptes de trésorerie',
    accounts_empty: 'Aucun compte de trésorerie dans votre périmètre.',
    account_type: {
      CAISSE_PDV: 'Caisse de PDV',
      CAISSE_UTILISATEUR: 'Caisse personnelle',
      CAISSE_CENTRALE: 'Caisse centrale',
      MOBILE_MONEY: 'Mobile money',
      BANQUE: 'Banque',
    },
    inactive: 'inactif',
    movements_open: 'Mouvements de {{name}}',
    movements_close: 'Replier les mouvements de {{name}}',
    movements_short: 'Mouvements',
    movements_close_short: 'Replier',
    movements_failed: 'Mouvements indisponibles : le serveur n’a pas répondu.',
    movements_failed_stale:
      'Actualisation des mouvements impossible : le serveur n’a pas répondu ; la liste ci-dessous peut être périmée.',
    movements_empty: 'Aucun mouvement.',
    movements_more: 'Mouvements plus anciens',
    movement_line: '{{date}} · {{type}} : {{amount}}',
    reversal: '(annulation)',
    movement_type: {
      CUSTOMER_PAYMENT: 'Encaissement',
      REFUND: 'Remboursement',
      SUPPLIER_PAYMENT: 'Paiement fournisseur',
      EXPENSE: 'Dépense',
      TRANSFER_OUT: 'Remise envoyée',
      TRANSFER_IN: 'Remise reçue',
      SESSION_VARIANCE: 'Écart de caisse',
      OPENING_BALANCE: 'Solde d’ouverture',
    },
    validate_result: {
      WAITING_offline: 'Validation enregistrée ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Validation en cours d’envoi…',
      DONE: 'Session de caisse validée.',
      WARNING: 'Session validée, avec un signalement.',
      CONFLICT: 'Validation mise en attente d’examen (conflit).',
      REJECTED: 'Validation refusée : {{reason}}',
    },
    decide_result: {
      WAITING_offline: 'Décision enregistrée ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Décision en cours d’envoi…',
      DONE: 'Écart de remise clos.',
      WARNING: 'Écart clos, avec un signalement.',
      CONFLICT: 'Décision mise en attente d’examen (conflit).',
      REJECTED: 'Décision refusée : {{reason}}',
    },
    receive_result: {
      WAITING_offline: 'Réception enregistrée ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Réception en cours d’envoi…',
      DONE: 'Remise reçue.',
      WARNING: 'Réception enregistrée, avec un signalement.',
      WARNING_CASH_TRANSFER_DISCREPANCY:
        'Réception enregistrée avec un écart : il attend la décision d’un autre valideur.',
      WARNING_CASH_TRANSFER_RECEIVED_AFTER_CANCEL:
        'La remise avait été annulée : la réception est signalée, sans mouvement.',
      WARNING_CASH_TRANSFER_RECEIVED_TWICE:
        'La remise était déjà reçue : cette réception est signalée, sans mouvement.',
      CONFLICT: 'Réception mise en attente d’examen (conflit).',
      REJECTED: 'Réception refusée : {{reason}}',
    },
  },
  cash_transfer: {
    offline_banner:
      'Hors ligne : les remises sont enregistrées sur ce téléphone et partiront au retour du réseau.',
    personal_account: 'caisse personnelle',
    pending_label: 'Remise de {{amount}} vers {{to}}, {{time}}',
    you: 'vous',
    route: '{{from}} → {{to}}',
    sent_at: 'Envoyée le {{date}} par {{who}}',
    carrier: 'porteur : {{name}}',
    state_waiting: 'Enregistrée sur ce téléphone, en attente d’envoi.',
    state_unpulled: 'Envoyée au serveur ; en transit dès la prochaine synchronisation.',
    state_in_transit: 'En transit : pas encore reçue.',
    state_discrepancy: 'Reçue avec un écart : en attente de la décision de la Finance.',
    cancelling_WAITING: 'Annulation enregistrée, en attente d’envoi.',
    cancelling_SENT:
      'Annulation envoyée ; elle sortira de la liste à la prochaine synchronisation.',
    receiving_WAITING: 'Réception enregistrée, en attente d’envoi.',
    receiving_SENT: 'Réception envoyée ; elle sortira de la liste à la prochaine synchronisation.',
    receiving_gap_WAITING:
      'Réception enregistrée avec un écart de {{amount}}, en attente d’envoi ; la remise restera en attente de la décision de la Finance.',
    receiving_gap_SENT:
      'Réception envoyée avec un écart de {{amount}} : la remise reste en attente de la décision de la Finance.',
    incoming_title: 'À recevoir',
    receive_open: 'Recevoir la remise {{label}}',
    received_label: 'Montant reçu, compté (F CFA)',
    received_invalid: 'Montant invalide : un nombre entier de francs (0 si rien n’est arrivé).',
    match: 'Montant conforme à l’envoi.',
    discrepancy: 'Écart de {{amount}} : il sera soumis à la Finance, qui l’impute et le motive.',
    lost: 'Rien reçu : la remise sera déclarée perdue et soumise à la Finance.',
    receive_submit: 'Confirmer la réception de {{amount}}',
    sent_title: 'Mes remises',
    sent_empty: 'Aucune remise envoyée en cours.',
    cancel_open: 'Annuler la remise {{label}}',
    reason_label: 'Motif de l’annulation',
    cancel_submit: 'Confirmer l’annulation',
    close_form: 'Fermer',
    new_title: 'Nouvelle remise',
    no_source: 'Aucune caisse de ce téléphone ne peut remettre des fonds.',
    source_label: 'Depuis la caisse',
    balance: 'Solde indicatif : {{amount}} (opérations de ce téléphone comprises).',
    balance_unknown: 'Solde de cette caisse non servi à ce téléphone.',
    balance_blind:
      'Solde de la caisse non affiché : comptage à l’aveugle (il s’affiche après votre clôture). En ligne, le serveur refuse une remise supérieure au solde.',
    target_label: 'Vers',
    target_placeholder: 'Choisir la destination',
    amount_label: 'Montant remis (F CFA)',
    amount_invalid: 'Montant invalide : un nombre entier de francs, plus de 0.',
    over_balance:
      'Au-delà du solde indicatif : en ligne, le serveur refusera si la caisse n’a pas ce montant ; hors ligne, la remise sera enregistrée et signalée à la Finance.',
    carrier_label: 'Porteur (facultatif)',
    comment_label: 'Commentaire (facultatif)',
    send_submit: 'Envoyer la remise',
    send_result: {
      WAITING_offline:
        'Remise enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Remise en cours d’envoi…',
      DONE: 'Remise {{doc}} envoyée.',
      WARNING: 'Remise {{doc}} envoyée, avec un signalement.',
      WARNING_CASH_NEGATIVE:
        'Remise {{doc}} envoyée ; la caisse passe en négatif : la Finance est alertée.',
      WARNING_CASH_TRANSFER_PAIR_NOT_ALLOWED:
        'Remise {{doc}} envoyée vers une destination non admise : la Finance est alertée.',
      WARNING_LOCAL_REF_DUPLICATE: 'Remise {{doc}} envoyée, sans sa référence locale (déjà prise).',
      CONFLICT: 'Remise mise en attente d’examen (conflit).',
      REJECTED: 'Remise refusée : {{reason}}',
    },
    cancel_result: {
      WAITING_offline: 'Annulation enregistrée ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Annulation en cours d’envoi…',
      DONE: 'Remise annulée.',
      WARNING: 'Annulation enregistrée, avec un signalement.',
      WARNING_CASH_TRANSFER_CANCEL_AFTER_RECEIVE:
        'La remise avait déjà été reçue : l’annulation est signalée à la Finance, sans mouvement.',
      CONFLICT: 'Annulation mise en attente d’examen (conflit).',
      REJECTED: 'Annulation refusée : {{reason}}',
    },
    receive_result: {
      WAITING_offline: 'Réception enregistrée ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Réception en cours d’envoi…',
      DONE: 'Remise reçue.',
      WARNING: 'Réception enregistrée, avec un signalement.',
      WARNING_CASH_TRANSFER_DISCREPANCY:
        'Réception enregistrée avec un écart : il est soumis à la Finance.',
      WARNING_CASH_TRANSFER_RECEIVED_AFTER_CANCEL:
        'La remise avait été annulée : la réception est signalée à la Finance, sans mouvement.',
      WARNING_CASH_TRANSFER_RECEIVED_TWICE:
        'La remise était déjà reçue : cette réception est signalée à la Finance, sans mouvement.',
      CONFLICT: 'Réception mise en attente d’examen (conflit).',
      REJECTED: 'Réception refusée : {{reason}}',
    },
  },
  pos_home: {
    offline_banner:
      'Hors ligne : chiffres de ce téléphone ; les opérations des autres téléphones arrivent avec la synchronisation.',
    cash_title: 'Caisse',
    cash_unknown: 'La caisse de ce point de vente n’est pas encore sur ce téléphone.',
    cash_open: 'Caisse ouverte depuis le {{date}} par {{who}}.',
    cash_closed: 'Caisse fermée : ouvrez-la avant d’encaisser en espèces.',
    cash_state_open: 'Ouverte',
    cash_state_closed: 'Fermée',
    cash_closing_elsewhere:
      'Clôture saisie sur cette tablette par un autre utilisateur, pas encore envoyée : la caisse reste ouverte au serveur jusqu’à son envoi, à sa prochaine connexion avec le réseau.',
    open_cash: 'Ouvrir la caisse',
    close_cash: 'Clôturer la caisse',
    sales_title: 'Ventes du jour',
    sales_line_one: '{{count}} vente, {{net}} ; encaissé affecté à cette vente : {{paid}}',
    sales_line_other: '{{count}} ventes, {{net}} ; encaissé affecté à ces ventes : {{paid}}',
    sales_pending_one: 'Dont {{count}} vente en attente d’envoi.',
    sales_pending_other: 'Dont {{count}} ventes en attente d’envoi.',
    sales_hint:
      'Ventes connues de ce téléphone sur ce point de vente (annulées exclues) ; l’encaissé comprend les acomptes reportés et les règlements reçus plus tard. Le tableau de distribution donne le chiffre de tous les téléphones et l’encaissé du jour.',
    quota_title: 'Mes quotas sur ce téléphone',
    quota_empty: 'Aucun quota sur ce téléphone pour ce point de vente.',
    quota_line: 'Reste : {{remaining}} {{unit}}.',
    quota_line_until: 'Reste : {{remaining}} {{unit}}, jusqu’au {{until}}.',
    quota_expired: 'Quota échu : libérez-le pour rendre le reste au stock commun.',
    quota_revoking:
      'Ce téléphone est signalé : le quota ne se consomme plus et sera révoqué par le serveur.',
    quota_releasing: 'Libération enregistrée, en attente d’envoi.',
    release: 'Libérer le quota {{name}}',
    release_confirm: 'Confirmer : rendre {{remaining}} {{unit}} au stock commun',
    release_cancel: 'Annuler',
    release_result: {
      WAITING_offline:
        'Libération enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Libération en cours d’envoi…',
      DONE: 'Quota libéré.',
      WARNING: 'Quota libéré, avec un signalement (reste différent de celui du serveur).',
      CONFLICT: 'Libération mise en attente d’examen (conflit).',
      REJECTED: 'Libération refusée : {{reason}}',
    },
    stock_title: 'Stock du point de vente',
    stock_empty: 'Aucun stock connu de ce téléphone sur ce point de vente.',
    stock_line: 'Disponible sur ce téléphone : {{available}} {{unit}}',
    stock_line_shared:
      'Stock physique : {{physical}} {{unit}} (hors ligne, la vente se fait dans vos quotas)',
    stock_line_other_device:
      'Stock physique : {{physical}} {{unit}} (confié à un autre téléphone : pas de vente hors ligne depuis celui-ci)',
    actions_title: 'Opérations du point de vente',
  },
  distribution: {
    online_only:
      'Tableau en ligne seulement : il est calculé par le serveur à la demande. Reconnectez-vous pour le consulter.',
    period_label: 'Période',
    period: {
      TODAY: 'Aujourd’hui',
      YESTERDAY: 'Hier',
      WEEK: 'Cette semaine',
      MONTH: 'Ce mois',
      YEAR: 'Cette année',
    },
    period_range: 'Du {{from}} au {{to}} · calculé le {{at}}',
    denied: 'Tableau non accessible avec vos droits.',
    failed: 'Tableau indisponible : le serveur n’a pas répondu.',
    retry: 'Réessayer',
    stale_devices_one:
      '{{count}} appareil sans contact depuis plus de {{hours}} h : ses opérations peuvent manquer.',
    stale_devices_other:
      '{{count}} appareils sans contact depuis plus de {{hours}} h : leurs opérations peuvent manquer.',
    empty: 'Aucun point de vente dans votre périmètre.',
    totals_title_one: 'Total ({{count}} point de vente)',
    totals_title_other: 'Total ({{count}} points de vente)',
    sold_net: 'Vendu net',
    cashed: 'Encaissé',
    cashed_value_one: '{{amount}} ({{count}} encaissement)',
    cashed_value_other: '{{amount}} ({{count}} encaissements)',
    remaining_value: 'Valeur du restant',
    lost_value: 'Valeur des pertes',
    to_validate: 'À valider',
    to_validate_value: '{{sessions}} session(s) de caisse, {{adjustments}} ajustement(s)',
    transfers: 'Remises en transit',
    transfers_value: '{{count}} remise(s), {{amount}}',
    transfers_overdue: '{{count}} remise(s) non reçue(s) depuis plus de {{hours}} h : {{amount}}.',
    transfers_discrepancy: '{{count}} remise(s) en écart à trancher.',
    site_label: 'Point de vente',
    all_sites: 'Tous les points de vente',
    not_reconciled: 'Écart de rapprochement sur au moins un produit : voir le détail ci-dessous.',
    sales: 'Ventes',
    sales_value: '{{net}} net ({{cancelled}} annulé)',
    by_method: '{{method}} : {{amount}} ({{count}})',
    by_receiver_title: 'Encaissé par vendeur',
    by_receiver: '{{name}} : {{amount}} ({{count}})',
    unknown_user: 'Utilisateur inconnu',
    cash_title: 'Caisse',
    session: {
      open: 'Session ouverte depuis le {{since}} ({{status}}), écart courant : {{variance}}',
      closed: 'Session du {{since}} ({{status}}), écart : {{variance}}',
    },
    session_status: {
      OPEN: 'ouverte',
      PENDING_VALIDATION: 'à valider',
      VALIDATED: 'validée',
    },
    no_session: 'Aucune session de caisse aujourd’hui.',
    session_open_long: 'Session ouverte depuis trop longtemps : à clôturer.',
    devices_title: 'Appareils ({{count}})',
    no_devices: 'Aucun appareil rattaché.',
    device: '{{name}} : dernier contact le {{at}}',
    device_stale: '{{name}} : sans contact depuis le {{at}}',
    device_never: '{{name}} : jamais connecté',
    device_inactive: '{{name}} : {{status}}',
    device_status: {
      PENDING: 'en attente d’activation',
      BLOCKED: 'bloqué',
      LOST: 'déclaré perdu',
      RETIRED: 'retiré',
    },
    device_pending_one: ', {{count}} opération en attente déclarée',
    device_pending_other: ', {{count}} opérations en attente déclarées',
    products_title: 'Produits',
    no_products: 'Aucun mouvement sur la période.',
    sent: 'Envoyé',
    received: 'Reçu',
    sold: 'Vendu',
    sold_value: '{{quantity}} ({{amount}})',
    lost: 'Perdu',
    lost_pending: 'Pertes en attente (variation sur la période)',
    transit_pending: 'Écarts de transfert en attente (variation sur la période)',
    now_caption: 'À l’instant de lecture :',
    now_caption_past: 'Maintenant (pas sur la période choisie) :',
    remaining: 'Restant',
    to_deliver: 'Reste à livrer',
    allocated: 'Quotas',
    allocated_value: '{{allocated}} alloué, {{unallocated}} libre',
    overcommitted: 'Quotas au-delà du stock : à régulariser.',
    replenish: 'À réapprovisionner : {{quantity}}',
    residual: 'Écart de rapprochement : {{quantity}}',
    sales_mismatch: 'Vendu : ventes et registre de stock divergent.',
  },
  replenish: {
    offline_banner:
      'Hors ligne : la demande sera enregistrée sur ce téléphone et envoyée au retour du réseau.',
    no_pos:
      'Aucun point de vente sur ce téléphone : il arrive avec la synchronisation de votre point de vente.',
    pos_label: 'Point de vente',
    destination: 'Pour : {{name}}',
    source: 'Depuis : {{name}}',
    source_label: 'Depuis le magasin',
    source_unknown: 'Depuis : magasin configuré du point de vente (en attente de synchronisation)',
    source_placeholder: 'Choisir le magasin',
    no_source_hint:
      'Aucun magasin n’est configuré pour ce point de vente : choisissez celui qui l’approvisionne.',
    no_source_blocked:
      'Aucun magasin n’est configuré pour ce point de vente : la demande attend sa configuration.',
    suggest_title: 'À réapprovisionner',
    suggest_empty: 'Aucun produit sous sa cible connue de ce téléphone.',
    suggest_line: 'Disponible : {{available}} · en route : {{transit}} · suggéré : {{suggested}}',
    add_suggestion: 'Ajouter {{name}} à la demande',
    state: {
      STOCK_OUT: 'Rupture',
      STOCK_LOW: 'Sous le minimum',
    },
    new_title: 'Nouvelle demande',
    already_requested: 'Déjà demandé, pas encore expédié : {{quantity}} {{unit}}.',
    already_requested_blocked:
      'Produit déjà demandé vers ce point de vente : attendez son expédition ou annulez la demande.',
    duplicate: 'Produit déjà présent dans la demande : regroupez les quantités.',
    notes_label: 'Note pour le magasin (facultatif)',
    submit: 'Envoyer la demande',
    pending_title: 'Demandes en attente d’expédition',
    pending_empty: 'Aucune demande en attente vers ce point de vente.',
    pending_number: 'Demande en attente du numéro',
    from: 'depuis {{name}}',
    request_line: '{{name}} : {{quantity}} {{unit}}',
    on_device: 'Enregistrée sur ce téléphone, en attente d’envoi.',
    sent_awaiting_pull: 'Envoyée ; son numéro apparaîtra à la prochaine synchronisation.',
    cancelling: 'Annulation en attente d’envoi.',
    cancel_sent: 'Annulation envoyée ; elle apparaîtra à la prochaine synchronisation.',
    cancel: 'Annuler la demande {{doc}}',
    cancel_confirm: 'Confirmer l’annulation de {{doc}}',
    back: 'Retour aux demandes',
    request_result: {
      WAITING_offline:
        'Demande enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Demande en cours d’envoi…',
      DONE: 'Demande envoyée au magasin ; son numéro apparaîtra à la prochaine synchronisation.',
      WARNING: 'Demande envoyée au magasin, avec un signalement.',
      CONFLICT: 'Demande mise en attente d’examen (conflit).',
      REJECTED: 'Demande refusée : {{reason}}',
    },
    cancel_result: {
      WAITING_offline:
        'Annulation enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Annulation en cours d’envoi…',
      DONE: 'Demande annulée.',
      WARNING: 'Demande annulée, avec un signalement.',
      CONFLICT: 'Annulation mise en attente d’examen (conflit).',
      REJECTED: 'Annulation refusée : {{reason}}',
    },
  },
  cash: {
    account_label: 'Caisse',
    no_account:
      'Aucune caisse de point de vente sur ce téléphone : elle arrive avec la synchronisation de votre point de vente.',
    closed_state: 'Caisse fermée.',
    state_open: 'Ouverte',
    state_closed: 'Fermée',
    closing_pending: 'Clôture de la caisse enregistrée sur ce téléphone, en attente d’envoi.',
    closed_to_validate: 'Dernière session close, en attente de validation par la Finance.',
    closed_validated: 'Dernière session close et validée d’office.',
    closed_last: 'Dernière session close.',
    foreign_openings:
      'Une ouverture saisie par un autre utilisateur de cette tablette attend sa reconnexion : elle sera rattachée à la session ouverte entre-temps.',
    attached:
      'Votre ouverture du {{date}} est rattachée à une session existante de la journée : aucune caisse n’est ouverte sur ce téléphone, votre comptage est conservé comme constat pour la Finance.',
    attached_open:
      'Votre ouverture du {{date}} est rattachée à la session déjà ouverte de cette caisse : la caisse est ouverte, elle s’affichera ici à la prochaine synchronisation. Votre comptage est conservé comme constat pour la Finance.',
    attached_closed:
      'Votre ouverture du {{date}} est rattachée à une session de la journée déjà close ({{status}}) : aucune caisse n’est ouverte. Votre comptage est conservé comme constat pour la Finance.',
    target_status: {
      OPEN: 'ouverte',
      PENDING_VALIDATION: 'à valider',
      VALIDATED: 'validée',
    },
    shifted: 'Ouverture décalée au {{date}}, à la clôture de la session précédente.',
    merged_figures:
      'Comptage rattaché à une session existante de cette caisse : aucun attendu ni écart n’est calculé, il est conservé comme constat pour la Finance.',
    merged_figures_open:
      'Comptage rattaché à la session déjà ouverte de cette caisse : la caisse est ouverte ; aucun attendu ni écart n’est calculé, le comptage est conservé comme constat pour la Finance.',
    merged_figures_closed:
      'Comptage rattaché à une session de cette caisse déjà close : aucune caisse n’est ouverte ; aucun attendu ni écart n’est calculé, le comptage est conservé comme constat pour la Finance.',
    observed_figures:
      'Session déjà close au serveur : aucun attendu ni écart n’est calculé, votre comptage est conservé comme constat pour la Finance.',
    open_state: 'Caisse ouverte le {{date}} par {{who}}, fonds compté : {{amount}}.',
    opened_by_you: 'vous',
    opened_by_other: 'un autre utilisateur',
    open_pending: 'Ouverture enregistrée sur ce téléphone, en attente d’envoi.',
    previous_day: 'Caisse ouverte un jour précédent : clôturez-la avant d’en ouvrir une autre.',
    counted_label: 'Montant compté (F CFA)',
    count_invalid: 'Saisissez le montant compté en francs entiers.',
    comment_label: 'Commentaire (facultatif)',
    blind_hint:
      'Comptez les espèces sans vous fier à un montant attendu : il s’affiche après l’enregistrement de votre comptage.',
    figures_title: 'Attendu et écart',
    expected_indicative: 'Attendu indicatif (calculé sur ce téléphone) : {{amount}}',
    variance_indicative: 'Écart indicatif : {{amount}}',
    indicative_hint:
      'Estimation de ce téléphone : le serveur recalcule l’attendu sur toutes les opérations de la caisse, y compris celles des autres téléphones.',
    expected_server: 'Attendu : {{amount}}',
    variance_server: 'Écart : {{amount}}',
    status_after_close: {
      VALIDATED: 'Session validée d’office : écart dans la tolérance.',
      PENDING_VALIDATION: 'Session à valider par la Finance.',
    },
    open_merge:
      'Une session de cette caisse existe déjà ou la limite du jour est atteinte : hors ligne, votre comptage sera rattaché à cette session et signalé à la Finance.',
    open_refused: {
      CASH_SESSION_ALREADY_OPEN:
        'Une session de cette caisse est déjà ouverte : clôturez-la d’abord.',
      CASH_SESSION_OVERLAP:
        'Une session de cette caisse couvre déjà cette heure : le serveur refuserait l’ouverture.',
      CASH_SESSION_DAILY_LIMIT:
        'Le nombre de sessions de caisse du jour est atteint : le serveur refuserait l’ouverture.',
    },
    back: 'Retour à la caisse',
    prompt: {
      unknown:
        'Encaissement en espèces au point de vente : la caisse du PDV doit être ouverte (ce téléphone ne la voit pas). Sans session ouverte, le serveur refusera l’encaissement en ligne et le signalera à la Finance hors ligne.',
      closed_online: 'Caisse du point de vente fermée : ouvrez-la avant d’encaisser en espèces.',
      closed_offline:
        'Aucune caisse ouverte sur ce téléphone : un encaissement en espèces sera enregistré et signalé à la Finance. Ouvrez la caisse d’abord.',
      open_link: 'Ouvrir la caisse',
      expense_unknown:
        'Dépense payée par la caisse du point de vente : la caisse doit être ouverte (ce téléphone ne la voit pas). Sans session ouverte, le serveur refusera la dépense en ligne et la signalera à la Finance hors ligne.',
      expense_closed_online:
        'Caisse du point de vente fermée : ouvrez-la avant de payer une dépense en espèces, ou laissez la dépense à payer.',
      expense_closed_offline:
        'Aucune caisse ouverte sur ce téléphone : la dépense sera enregistrée et signalée à la Finance. Ouvrez la caisse d’abord.',
      blocked:
        'Encaissement en espèces impossible sans caisse ouverte : ouvrez la caisse ou choisissez un autre moyen de paiement.',
    },
    open: {
      title: 'Ouvrir la caisse',
      hint: 'Comptez le fonds de caisse présent dans le tiroir.',
      submit: 'Ouvrir la caisse',
      result: {
        WAITING_offline:
          'Ouverture de la caisse enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
        WAITING_online: 'Ouverture de la caisse en cours d’envoi…',
        DONE: 'Caisse ouverte.',
        WARNING: 'Caisse ouverte, avec un signalement.',
        WARNING_CASH_SESSION_CONCURRENT_OPEN:
          'Ouverture signalée à la Finance : une autre session de cette caisse existait (ouverte, close dans la journée ou restée ouverte la veille).',
        CONFLICT: 'Ouverture mise en attente d’examen (conflit).',
        REJECTED: 'Ouverture refusée : {{reason}}',
      },
    },
    close: {
      title: 'Clôturer la caisse',
      hint: 'Comptez toutes les espèces de la caisse.',
      submit: 'Clôturer la caisse',
      result: {
        WAITING_offline:
          'Clôture de la caisse enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
        WAITING_online: 'Clôture de la caisse en cours d’envoi…',
        DONE: 'Caisse clôturée.',
        WARNING: 'Caisse clôturée, avec un signalement.',
        WARNING_CASH_SESSION_CONCURRENT_CLOSE:
          'La session était déjà close : votre comptage est conservé comme constat pour la Finance.',
        WARNING_CASH_SESSION_CLOSE_BEFORE_MOVEMENTS:
          'Clôture datée avant des opérations déjà enregistrées : la session sera validée par la Finance.',
        CONFLICT: 'Clôture mise en attente d’examen (conflit).',
        REJECTED: 'Clôture refusée : {{reason}}',
      },
    },
  },
  photo: {
    take: 'Prendre une photo',
    choose: 'Choisir une image',
    ready: 'Photo prête ({{size}} Ko).',
    remove: 'Retirer la photo',
    preparing: 'Préparation de la photo…',
    send: 'Envoyer la photo',
    cancel: 'Annuler',
    view: 'Voir la photo',
    retry: 'Réessayer',
    mortality_awaiting_title: 'Mortalités en attente de validation',
    mortality_awaiting_hint:
      'Le valideur attend la photo avant d’approuver : joignez-la si elle manque.',
    mortality_awaiting_line: '{{heads}} · {{date}}',
    loading: 'Chargement de la photo…',
    error: {
      UNSUPPORTED:
        'Image illisible sur ce téléphone (format non pris en charge) : prenez la photo avec l’appareil photo.',
      TOO_LARGE: 'Photo trop lourde, même réduite : reprenez-la.',
      BUDGET:
        'Trop de photos attendent leur envoi sur ce téléphone : synchronisez, puis réessayez. L’opération peut partir sans photo.',
    },
    state: {
      LOCAL: 'Photo enregistrée sur ce téléphone : elle partira à la prochaine synchronisation.',
      UPLOADING: 'Photo en cours d’envoi ({{percent}} %).',
      RECEIVED: 'Photo reçue par le serveur.',
      REFUSED: 'Photo refusée par le serveur : prenez-en une nouvelle.',
      OTHER_AUTHOR:
        'Photo prise sur ce téléphone par un autre utilisateur : elle partira à sa prochaine connexion.',
    },
    view_error: {
      OFFLINE: 'Photo indisponible hors connexion.',
      SESSION_INVALID: 'Reconnectez-vous pour voir la photo.',
      FORBIDDEN: 'Cette photo n’est pas dans votre périmètre.',
      NOT_AVAILABLE: 'Photo pas encore reçue par le serveur.',
      ERROR: 'Photo indisponible pour le moment.',
    },
    receipt_label: 'Photo du bon de livraison',
    receipt_hint:
      'À partir de {{threshold}} acceptés, la photo du bon est exigée : sans elle, la réception est enregistrée « photo manquante » et signalée aux Achats.',
    loss_label: 'Photo de la perte (facultative)',
    mortality_label: 'Photo des animaux morts',
    mortality_hint:
      'La mortalité est soumise à validation : le valideur attend la photo avant d’approuver.',
    attach_receipt: 'Joindre la photo du bon',
    attach_mortality: 'Joindre la photo',
    missing_badge: 'Photo manquante',
    approval_alt: 'Photo jointe : {{summary}}',
  },
  sync: {
    device_pending:
      'Enregistrée sur ce téléphone, qui attend son approbation (par l’administrateur ou un responsable habilité) : l’opération partira après l’approbation.',
    device_pending_notice:
      'Ce téléphone attend son approbation (par l’administrateur ou un responsable habilité) : vos opérations enregistrées partiront après l’approbation. Si l’attente dure, le serveur les signalera comme bloquées.',
    read_only_refused:
      'Lecture seule : aucune synchronisation depuis plus longtemps que l’autonomie permise. Rien n’a été enregistré ; synchronisez ce téléphone avant toute nouvelle opération.',
    autonomy_warning:
      'Aucune synchronisation depuis {{hours}} h : connectez-vous dès que possible. Au-delà de {{max}} h, ce téléphone passera en lecture seule.',
    autonomy_read_only:
      'Lecture seule : aucune synchronisation depuis {{hours}} h (plus de {{max}} h). Les nouvelles opérations sont bloquées jusqu’à une synchronisation réussie ; celles déjà enregistrées restent sur ce téléphone et partiront au retour du réseau.',
    held_behind_failure:
      'Cette opération attend une opération précédente que le serveur renvoie sans l’appliquer ({{reason}}). Elle partira dès que celle-ci passera, ou au plus tard quand le serveur l’aura écartée.',
    foreign_pending:
      'Opération saisie sur cette tablette par un autre utilisateur, pas encore envoyée : la suite sera possible après son envoi, à sa prochaine connexion avec le réseau.',
    repeated_failure:
      'Le serveur renvoie cette opération sans l’appliquer ({{reason}}). Elle est réessayée automatiquement ; signalez-la à l’administrateur si cela dure.',
    screen_title: 'Synchronisation',
    force_sync: 'Forcer la synchronisation',
    syncing: 'Synchronisation…',
    pending_operations: 'Opérations en attente',
    no_pending: 'Aucune opération en attente.',
    attention_operations: 'Opérations à examiner',
    photos_pending_one: '{{count}} photo à envoyer ({{size}} Ko).',
    photos_pending_other: '{{count}} photos à envoyer ({{size}} Ko).',
    photos_refused_one:
      '{{count}} photo refusée par le serveur : reprenez-la depuis son document (réception, mortalité).',
    photos_refused_other:
      '{{count}} photos refusées par le serveur : reprenez-les depuis leur document (réception, mortalité).',
    acknowledge: 'Classer',
    help_title: 'Que veut dire « classer » ?',
    acknowledge_label: 'Classer : {{operation}}',
    acknowledge_help:
      'Classer une opération la retire de cette liste, sans l’effacer. Refusée, elle n’a pas été appliquée par le serveur : ressaisissez-la si besoin, ou voyez votre responsable.',
    acknowledged_conflict:
      '(la classer ne règle pas le conflit : il reste ouvert pour votre responsable sur le serveur)',
    operation: {
      approvals_policy_set: 'Politique de contrôle',
      approvals_request_approve: 'Approbation d’une demande',
      approvals_request_reject: 'Refus d’une demande',
      approvals_request_withdraw: 'Retrait d’une demande',
      attachments_attachment_register: 'Pièce jointe',
      catalog_customer_category_create: 'Catégorie de client créée',
      catalog_customer_category_deactivate: 'Catégorie de client désactivée',
      catalog_customer_category_reactivate: 'Catégorie de client réactivée',
      catalog_product_create: 'Produit créé',
      catalog_product_deactivate: 'Produit désactivé',
      catalog_product_reactivate: 'Produit réactivé',
      catalog_product_update: 'Produit modifié',
      catalog_product_category_create: 'Catégorie de produit créée',
      catalog_product_category_deactivate: 'Catégorie de produit désactivée',
      catalog_product_category_reactivate: 'Catégorie de produit réactivée',
      catalog_product_standard_cost_set: 'Coût standard d’un produit',
      catalog_product_unit_set: 'Conditionnement d’un produit',
      catalog_reason_code_set: 'Motif',
      catalog_reason_code_update: 'Motif modifié',
      catalog_reason_code_deactivate: 'Motif désactivé',
      catalog_reason_code_reactivate: 'Motif réactivé',
      catalog_sales_channel_create: 'Canal de vente créé',
      catalog_sales_channel_deactivate: 'Canal de vente désactivé',
      catalog_sales_channel_reactivate: 'Canal de vente réactivé',
      catalog_unit_create: 'Unité créée',
      catalog_unit_deactivate: 'Unité désactivée',
      catalog_unit_reactivate: 'Unité réactivée',
      crm_customer_create: 'Nouveau client ou prospect',
      crm_customer_mark_lost: 'Prospect perdu',
      crm_customer_merge: 'Fusion de clients',
      crm_customer_reassign: 'Réaffectation d’un client',
      crm_customer_reopen: 'Prospect rouvert',
      crm_customer_set_credit_terms: 'Conditions de crédit d’un client',
      crm_customer_set_pipeline_step: 'Étape d’un prospect',
      crm_customer_update: 'Fiche client modifiée',
      crm_interaction_cancel: 'Échange annulé',
      crm_interaction_record: 'Échange avec un client',
      crm_pipeline_configure: 'Étapes des prospects',
      crm_target_cancel: 'Objectif annulé',
      crm_target_set: 'Objectif',
      crm_visit_cancel: 'Visite annulée',
      crm_visit_record: 'Visite',
      fieldwork_checkin_record: 'Prise ou fin de service',
      fieldwork_checkin_request_override: 'Demande de dérogation de pointage',
      finance_cash_account_create: 'Compte de trésorerie créé',
      finance_cash_account_deactivate: 'Compte de trésorerie désactivé',
      finance_cash_account_update: 'Compte de trésorerie modifié',
      finance_cash_session_close: 'Clôture de caisse',
      finance_cash_session_force_close: 'Clôture forcée de caisse',
      finance_cash_session_open: 'Ouverture de caisse',
      finance_cash_session_validate: 'Validation d’une session de caisse',
      finance_cash_transfer_cancel: 'Remise de fonds annulée',
      finance_cash_transfer_close_discrepancy: 'Écart de remise décidé',
      finance_cash_transfer_receive: 'Remise de fonds reçue',
      finance_cash_transfer_send: 'Remise de fonds envoyée',
      finance_expense_cancel: 'Dépense annulée',
      finance_expense_pay: 'Paiement d’une dépense',
      finance_expense_record: 'Dépense',
      finance_expense_category_create: 'Catégorie de dépense créée',
      finance_expense_category_deactivate: 'Catégorie de dépense désactivée',
      finance_expense_category_reactivate: 'Catégorie de dépense réactivée',
      finance_expense_category_update: 'Catégorie de dépense modifiée',
      finance_supplier_invoice_approve: 'Facture fournisseur approuvée',
      finance_supplier_invoice_cancel: 'Facture fournisseur annulée',
      finance_supplier_invoice_dispute: 'Facture fournisseur contestée',
      finance_supplier_invoice_record: 'Facture fournisseur',
      finance_supplier_payment_allocate: 'Affectation d’un paiement fournisseur',
      finance_supplier_payment_cancel: 'Paiement fournisseur annulé',
      finance_supplier_payment_record: 'Paiement fournisseur',
      identity_device_approve: 'Appareil approuvé',
      identity_device_block: 'Appareil bloqué',
      identity_device_declare_lost: 'Appareil déclaré perdu',
      identity_device_designate: 'Rattachement d’un appareil',
      identity_device_retire: 'Appareil retiré',
      identity_device_unblock: 'Appareil débloqué',
      identity_role_assignment_grant: 'Rôle attribué',
      identity_role_assignment_revoke: 'Rôle retiré',
      identity_user_create: 'Compte créé',
      identity_user_deactivate: 'Compte désactivé',
      identity_user_reactivate: 'Compte réactivé',
      identity_user_suspend: 'Compte suspendu',
      inventory_allocation_grant: 'Quota accordé',
      inventory_allocation_increase: 'Quota augmenté',
      inventory_allocation_release: 'Quota libéré',
      inventory_allocation_revoke: 'Quota révoqué',
      inventory_consumption_cancel: 'Consommation annulée',
      inventory_consumption_record: 'Consommation',
      inventory_count_cancel: 'Inventaire annulé',
      inventory_count_open: 'Inventaire ouvert',
      inventory_count_record_lines: 'Comptage d’inventaire',
      inventory_count_submit: 'Inventaire soumis',
      inventory_loss_declare: 'Perte déclarée',
      inventory_loss_withdraw: 'Perte retirée',
      inventory_overhead_cancel: 'Frais généraux annulés',
      inventory_overhead_record: 'Frais généraux',
      inventory_threshold_set: 'Seuil de stock',
      inventory_transfer_cancel: 'Transfert annulé',
      inventory_transfer_decline: 'Demande de transfert refusée',
      inventory_transfer_dispatch: 'Expédition de stock',
      inventory_transfer_move_internal: 'Mouvement interne',
      inventory_transfer_receive: 'Réception de transfert',
      inventory_transfer_request: 'Demande de transfert',
      inventory_transfer_return_to_source: 'Retour à l’expéditeur',
      organization_location_create: 'Emplacement créé',
      organization_location_deactivate: 'Emplacement désactivé',
      organization_location_update: 'Emplacement modifié',
      organization_pos_configure: 'Configuration d’un point de vente',
      organization_setting_set: 'Paramètre',
      organization_site_close: 'Site fermé',
      organization_site_create: 'Site créé',
      organization_site_update: 'Site modifié',
      organization_team_assign_member: 'Membre ajouté à une équipe',
      organization_team_create: 'Équipe créée',
      organization_team_remove_member: 'Membre retiré d’une équipe',
      organization_zone_create: 'Zone créée',
      organization_zone_update: 'Zone modifiée',
      pricing_campaign_create: 'Campagne commerciale',
      pricing_rule_activate: 'Règle de prix activée',
      pricing_rule_cancel: 'Règle de prix annulée',
      pricing_rule_draft: 'Règle de prix (brouillon)',
      pricing_rule_end: 'Règle de prix terminée',
      pricing_rule_supersede: 'Règle de prix remplacée',
      procurement_order_cancel: 'Bon de commande annulé',
      procurement_order_close_remaining: 'Reste d’un bon de commande clos',
      procurement_order_create: 'Bon de commande',
      procurement_order_mark_sent: 'Bon de commande envoyé',
      procurement_order_submit: 'Bon de commande soumis',
      procurement_order_update: 'Bon de commande modifié',
      procurement_receipt_record: 'Réception d’achat',
      procurement_receipt_request_cancellation: 'Demande d’annulation d’une réception',
      procurement_request_cancel: 'Demande d’achat annulée',
      procurement_request_close: 'Demande d’achat close',
      procurement_request_submit: 'Demande d’achat',
      procurement_supplier_create: 'Fournisseur créé',
      procurement_supplier_deactivate: 'Fournisseur désactivé',
      procurement_supplier_reactivate: 'Fournisseur réactivé',
      procurement_supplier_update: 'Fournisseur modifié',
      production_egg_collection_cancel: 'Collecte d’œufs annulée',
      production_egg_collection_record: 'Collecte d’œufs',
      production_incubation_cancel: 'Incubation annulée',
      production_incubation_cancel_candling: 'Mirage annulé',
      production_incubation_record_candling: 'Mirage',
      production_incubation_record_hatch: 'Éclosion',
      production_incubation_start: 'Mise en incubation',
      production_incubation_transfer_to_hatcher: 'Transfert en éclosoir',
      production_input_record: 'Intrant d’un lot',
      production_lot_cancel: 'Lot annulé',
      production_lot_cancel_entry: 'Entrée de lot annulée',
      production_lot_close: 'Lot clôturé',
      production_lot_create: 'Lot créé',
      production_lot_record_entry: 'Entrée dans un lot',
      production_lot_set_status: 'État d’un lot',
      production_mortality_record: 'Mortalité',
      production_observation_record: 'Observation',
      production_overhead_allocate: 'Répartition des frais généraux',
      production_slaughter_cancel: 'Abattage annulé',
      production_slaughter_record: 'Abattage',
      production_weighing_cancel: 'Pesée annulée',
      production_weighing_record: 'Pesée',
      sales_order_cancel: 'Commande annulée',
      sales_order_close_remaining: 'Reste d’une commande clos',
      sales_order_confirm_remaining: 'Reste d’une commande confirmé',
      sales_order_fulfil: 'Livraison',
      sales_order_place: 'Commande',
      sales_order_save_draft: 'Commande (brouillon)',
      sales_order_update: 'Commande modifiée',
      sales_payment_reallocate: 'Réaffectation d’un encaissement',
      sales_payment_record: 'Encaissement',
      sales_payment_refund: 'Remboursement',
      sales_payment_request_cancellation: 'Demande d’annulation d’un encaissement',
      sales_sale_cancel: 'Vente annulée',
      sales_sale_record: 'Vente',
      sales_sale_request_cancellation: 'Demande d’annulation d’une vente',
      sync_conflict_dismiss: 'Conflit classé',
      sync_conflict_resolve: 'Conflit résolu',
    },
    badge: {
      synced: 'Synchronisé',
      pending: '{{count}} opération en attente',
      pending_other: '{{count}} opérations en attente',
      attention: '{{count}} opération à examiner',
      attention_other: '{{count}} opérations à examiner',
      offline_since: 'Hors ligne depuis {{hours}} h',
      offline_unknown: 'Hors ligne',
    },
    status: {
      LOCAL_ONLY: 'En attente d’envoi',
      PENDING_SYNC: 'En attente d’envoi',
      SYNCING: 'Envoi en cours',
      SYNCED: 'Synchronisé',
      SYNCED_WITH_WARNING: 'Synchronisé (avertissement)',
      CONFLICT: 'Conflit',
      REJECTED: 'Rejeté',
    },
  },
  piece: {
    show: 'Voir la pièce',
    hide: 'Masquer la pièce',
    print: 'Imprimer',
    loading: 'Préparation de la pièce…',
    frame_title: 'Pièce {{code}} : {{title}}',
    duplicate_hint: 'Déjà imprimée le {{date}} : la réimpression portera la mention « DUPLICATA ».',
    error: 'La pièce n’a pas pu être préparée.',
    invoice: 'Facture',
    receipt_of: 'Reçu {{doc}}',
    cash_sheet_today: 'Feuille de caisse du jour',
    receipts_journal: 'Journal des encaissements (7 jours)',
    lot_week: 'Fiche de suivi de la semaine',
  },
  admin_config: {
    online_only:
      'Écran en ligne seulement : reconnectez-vous pour consulter et modifier cette configuration.',
    offline_refused:
      'Connexion perdue : rien n’a été envoyé. Réessayez quand le réseau est revenu.',
  },
  org: {
    help_title: 'À quoi sert cet écran ?',
    intro:
      'Décrivez l’organisation de GIC : les zones (avec leur géorepère de pointage), les sites (ferme, magasin, point de vente, bureau) et leurs emplacements, les équipes commerciales et la configuration de chaque point de vente. Chaque changement est enregistré par une opération datée, jamais effacée.',
    refresh: 'Actualiser',
    denied: 'Lecture refusée : droit insuffisant.',
    unavailable: 'Organisation indisponible : réessayez quand le serveur répond.',
    tabs_label: 'Parties de l’organisation',
    tab: { sites: 'Sites', zones: 'Zones', teams: 'Équipes', pos: 'Points de vente' },
    cancel: 'Annuler',
    save: 'Enregistrer',
    edit: 'Modifier',
    choose: 'Choisir…',
    none: 'Aucun',
    unknown_user: 'utilisateur inconnu',
    field: {
      code: 'Code',
      name: 'Nom',
      site_type: 'Type de site',
      zone: 'Zone',
      address: 'Adresse',
      opened_on: 'Date d’ouverture',
      location_type: 'Type d’emplacement',
      custodian: 'Titulaire de l’emplacement',
      parent_location: 'Emplacement parent',
      capacity: 'Capacité',
      level: 'Niveau',
      parent_zone: 'Zone parente',
      lat: 'Latitude',
      lng: 'Longitude',
      radius: 'Rayon',
      accuracy: 'Précision GPS maximale',
      manager: 'Responsable de l’équipe',
      sales_location: 'Emplacement de vente',
      source_location: 'Magasin source du réapprovisionnement',
      custody_mode: 'Mode de garde du stock',
      designated_device: 'Appareil désigné',
      opening_hours: 'Horaires d’ouverture',
      pos_site: 'Site du point de vente',
    },
    hint: {
      code: '{{max}} caractères au plus : lettres, chiffres, - et _ (mis en majuscules).',
      site_type:
        'Le type ne change plus ensuite. Un point de vente se configure dans l’onglet « Points de vente ».',
      custodian: 'Un emplacement mobile est confié à une personne.',
      radius: 'De {{min}} à {{max}} m ; vide : le rayon par défaut des paramètres.',
      accuracy: 'Vide : la précision par défaut des paramètres.',
      parent_zone: 'Le parent ne change plus ensuite.',
      manager: 'Le responsable commercial de l’équipe.',
      sales_location: 'Un emplacement de type « Point de vente » actif de ce site.',
      source_location: 'Un magasin actif d’un autre site, d’où part le réapprovisionnement.',
      designated_device: 'Un appareil actif, non rattaché à un autre site.',
    },
    problem: {
      CAPACITY: 'La capacité est un entier strictement positif.',
      LAT: 'Latitude entre -90 et 90.',
      LNG: 'Longitude entre -180 et 180.',
      RADIUS: 'Rayon entre 50 et 5 000 m.',
      ACCURACY: 'La précision est un nombre strictement positif.',
    },
    site_type: {
      FERME: 'Ferme',
      MAGASIN: 'Magasin',
      POINT_DE_VENTE: 'Point de vente',
      BUREAU: 'Bureau',
    },
    site_status: { ACTIVE: 'Actif', CLOSED: 'Fermé' },
    location_type: {
      STORE: 'Magasin',
      POS: 'Point de vente',
      BUILDING: 'Bâtiment',
      PEN: 'Enclos',
      INCUBATOR: 'Incubateur',
      HATCHER: 'Éclosoir',
      MOBILE: 'Mobile (véhicule, commercial)',
      SLAUGHTERHOUSE: 'Abattoir',
      V_TO_DELIVER: 'Vendu, à livrer',
    },
    location_status: { ACTIVE: 'Actif', INACTIVE: 'Désactivé' },
    zone_level: {
      PAYS: 'Pays',
      REGION: 'Région',
      VILLE: 'Ville',
      MARCHE: 'Marché',
      QUARTIER: 'Quartier',
      SECTEUR: 'Secteur',
    },
    custody: { SHARED: 'Partagé', EXCLUSIVE_DEVICE: 'Appareil désigné' },
    custody_hint: {
      SHARED:
        'Plusieurs vendeurs et appareils vendent depuis ce point de vente ; le stock se répartit par quotas.',
      EXCLUSIVE_DEVICE:
        'Un seul appareil vend depuis ce point de vente : changer d’appareil exige que l’ancien ait tout synchronisé.',
    },
    sites: {
      new: 'Nouveau site',
      empty: 'Aucun site pour le moment. Créez le magasin central, la ferme ou le point de vente.',
      needs_zone: 'Créez d’abord une zone (onglet « Zones »).',
      create_confirm: 'Créer le site',
      card: 'Site {{name}}',
      zone: 'Zone : {{name}}',
      opened: 'ouvert le {{date}}',
      closed: 'fermé le {{date}}',
      locations: 'Emplacements',
      close: 'Fermer le site',
      close_confirm: 'Confirmer la fermeture de {{name}}',
      close_warning:
        'Un site fermé reste consultable mais ne reçoit plus d’opérations. La fermeture est définitive : aucune réouverture n’est proposée.',
    },
    locations: {
      title: 'Emplacements de {{name}}',
      unavailable: 'Emplacements indisponibles : réessayez quand le serveur répond.',
      empty: 'Aucun emplacement pour ce site.',
      capacity: 'capacité {{count}}',
      deactivate: 'Désactiver',
      deactivate_warning:
        'Un emplacement désactivé ne reçoit plus de stock ; le serveur refuse tant qu’il porte du stock, un quota ou un transfert en cours.',
      deactivate_confirm: 'Confirmer la désactivation de {{name}}',
      new: 'Nouvel emplacement',
      create_confirm: 'Créer l’emplacement',
    },
    zones: {
      new: 'Nouvelle zone',
      empty: 'Aucune zone pour le moment. Commencez par le pays ou la région.',
      create_confirm: 'Créer la zone',
      card: 'Zone {{name}}',
      geofence: 'Géorepère de pointage',
      geofence_hint:
        'Zone dans laquelle un commercial peut pointer. Latitude et longitude vont ensemble ; laissez tout vide pour une zone sans pointage.',
      geofence_value: 'géorepère {{lat}}, {{lng}} · {{radius}} m',
      no_geofence: 'sans géorepère',
      active: 'Active',
      inactive: 'Inactive',
      deactivate: 'Désactiver',
      reactivate: 'Réactiver',
      unchanged: 'Aucun changement à enregistrer.',
    },
    position: {
      use: 'Utiliser ma position',
      waiting: 'Position en cours…',
      DENIED: 'Position refusée : autorisez la localisation de ce téléphone dans le navigateur.',
      UNAVAILABLE: 'Position indisponible : saisissez les coordonnées à la main.',
      TIMEOUT: 'La position n’a pas été trouvée à temps : réessayez en extérieur.',
    },
    teams: {
      new: 'Nouvelle équipe',
      empty: 'Aucune équipe pour le moment.',
      create_confirm: 'Créer l’équipe',
      card: 'Équipe {{name}}',
      manager: 'responsable : {{name}}',
      members_count_one: '{{count}} membre',
      members_count_other: '{{count}} membres',
      inactive: 'Inactive',
      members: 'Membres de {{name}}',
      since: 'depuis le {{date}}',
      remove: 'Retirer {{name}} de {{team}}',
      remove_short: 'Retirer',
      no_members: 'Aucun membre pour le moment.',
      add_label: 'Personne à ajouter',
      move_warning:
        'Cette personne quitte l’équipe {{team}} : elle n’est membre que d’une équipe à la fois.',
      add_confirm: 'Ajouter à {{name}}',
      add: 'Ajouter un membre',
    },
    pos: {
      new: 'Configurer un point de vente',
      empty: 'Aucun point de vente configuré.',
      needs_site: 'Créez d’abord un site de type « Point de vente » et son emplacement de vente.',
      card: 'Point de vente {{name}}',
      device_unknown: 'appareil inconnu',
      locations_unavailable: 'Emplacements indisponibles : réessayez quand le serveur répond.',
      no_sales_location:
        'Ce site n’a pas d’emplacement de type « Point de vente » actif : créez-le dans l’onglet « Sites » (Emplacements).',
      save: 'Enregistrer la configuration',
    },
    result: {
      WAITING_offline:
        'Opération enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Opération en cours d’envoi…',
      DONE: 'Opération enregistrée.',
      WARNING: 'Opération enregistrée, avec un signalement.',
      CONFLICT: 'Opération mise en attente d’examen (conflit).',
      REJECTED: 'Opération refusée : {{reason}}',
    },
  },
  settings: {
    help_title: 'À quoi sert cet écran ?',
    intro:
      'Réglez les seuils et les règles de l’application. Chaque changement crée une nouvelle version datée : l’ancienne valeur reste consultable dans l’historique. Les politiques de contrôle décident quelles opérations exigent une photo, un commentaire ou la validation d’un responsable.',
    tabs_label: 'Paramètres et politiques',
    tab: { settings: 'Paramètres', policies: 'Politiques de contrôle' },
    denied: 'Lecture refusée : droit insuffisant.',
    unavailable: 'Paramètres indisponibles : réessayez quand le serveur répond.',
    search_label: 'Rechercher un paramètre',
    show_technical: 'Afficher les réglages techniques (synchronisation, plateforme, analyse)',
    none: 'Aucun paramètre à afficher.',
    no_global: 'pas de valeur globale',
    yes: 'Oui',
    no: 'Non',
    empty_text: '(vide)',
    empty_list: '(aucun)',
    unit: {
      xaf: 'XAF',
      pct: '%',
      hours: 'h',
      days: 'j',
      minutes: 'min',
      seconds: 's',
      ms: 'ms',
      m: 'm',
      kmh: 'km/h',
      heads: 'têtes',
      rows: 'lignes',
    },
    option: {
      WHEN_ALLOCATED: 'Seulement si un quota existe',
      ALWAYS: 'Toujours',
      NEVER: 'Jamais',
      REVIEW: 'Mettre en revue',
      APPROVE: 'Approuver d’office',
      QUARANTINE: 'Mettre en quarantaine',
      APPLY_WITH_REVIEW: 'Appliquer et signaler',
      SUBSET: 'Produits reçus inclus dans les produits expédiés',
      EXACT: 'Identité exacte',
      BELOW_TARGET: 'Sous la cible',
      BELOW_MINIMUM: 'Sous le minimum',
    },
    weekday: {
      '1': 'lun.',
      '2': 'mar.',
      '3': 'mer.',
      '4': 'jeu.',
      '5': 'ven.',
      '6': 'sam.',
      '7': 'dim.',
    },
    weekday_long: {
      '1': 'Lundi',
      '2': 'Mardi',
      '3': 'Mercredi',
      '4': 'Jeudi',
      '5': 'Vendredi',
      '6': 'Samedi',
      '7': 'Dimanche',
    },
    group: {
      sales: 'Ventes, clients et prix',
      finance: 'Caisse et finance',
      procurement: 'Achats',
      inventory: 'Stock',
      production: 'Production',
      field: 'Terrain et hors ligne',
      control: 'Validations et contrôles',
      technical: 'Réglages techniques',
      other: 'Autres',
    },
    field: { scope: 'S’applique à', reason: 'Motif du changement' },
    scope: {
      global: 'Tous les sites',
      site: 'Site : {{name}}',
      ZONE: 'Zone …{{id}}',
      ROLE: 'Rôle …{{id}}',
    },
    hint: {
      scope:
        'Ce réglage peut être différent pour un site : sans valeur propre, le site suit la valeur globale.',
      range: 'Entre {{min}} et {{max}}.',
      min: 'Au moins {{min}}.',
      max: 'Au plus {{max}}.',
      list: 'Une valeur par ligne.',
      json: 'Objet JSON (accolades), tel que la valeur actuelle.',
    },
    problem: {
      TYPE: 'Saisissez un nombre.',
      NOT_INTEGER: 'Saisissez un nombre entier.',
      MIN: 'La valeur est trop petite.',
      MAX: 'La valeur est trop grande.',
      OPTION: 'Choisissez une des options.',
      EMPTY_ITEM: 'Une ligne de la liste est vide.',
      WEEKDAY: 'Jour de la semaine inconnu.',
      JSON_OBJECT: 'Saisissez un objet JSON, entre accolades.',
      PARSE: 'Le texte n’est pas du JSON valide.',
    },
    edit: 'Modifier',
    edit_title: 'Modifier {{name}}',
    history: 'Historique',
    close_history: 'Fermer l’historique',
    history_title: 'Historique des versions',
    history_unavailable: 'Historique indisponible : réessayez quand le serveur répond.',
    reason: 'Motif : {{reason}}',
    unchanged: 'La valeur n’a pas changé : rien à enregistrer.',
    save: 'Enregistrer la nouvelle valeur',
    technical_warning:
      'Réglage technique : une mauvaise valeur peut ralentir ou bloquer la synchronisation, les alertes ou les analyses. Ne le changez que si vous savez pourquoi.',
    policy: {
      intro:
        'Pour chaque type d’opération, la politique en vigueur dit si la photo ou un commentaire sont exigés, et si un responsable doit valider. Réviser une politique en crée une nouvelle version et retire l’ancienne ; la condition (seuils, catégories) reste inchangée.',
      none: 'Aucune politique de contrôle en vigueur.',
      card: 'Politique {{code}}',
      version: 'version {{version}}',
      badge_approval: 'Validation',
      badge_free: 'Sans validation',
      photo: 'photo exigée',
      comment: 'commentaire exigé',
      approval: 'validation par « {{permission}} » ({{scope}})',
      no_approval: 'sans validation',
      scope: { ALL: 'toute la structure', ZONE: 'zone', SITE: 'site', TEAM: 'équipe' },
      status_active: 'en vigueur',
      status_retired: 'retirée',
      edit_title: 'Réviser la politique {{code}}',
      requires_photo: 'Photo exigée',
      requires_comment: 'Commentaire exigé',
      requires_approval: 'Validation d’un responsable exigée',
      drop_warning:
        'Sans validation, cette opération ne demandera plus l’accord d’un responsable. Le serveur peut alors refuser certaines opérations faute de politique décidable.',
      approver: 'Permission de l’approbateur',
      approver_hint: 'Les responsables qui détiennent cette permission décident la demande.',
      problem_approver: 'Choisissez la permission de l’approbateur.',
      scope_label: 'Portée de l’approbateur',
      condition_note:
        'La condition de la politique (seuils, catégories) n’est pas modifiable ici : elle est reprise telle quelle.',
      save: 'Enregistrer la nouvelle version',
    },
    result: {
      WAITING_offline:
        'Changement enregistré sur ce téléphone ; il sera envoyé au retour du réseau.',
      WAITING_online: 'Changement en cours d’envoi…',
      DONE: 'Changement enregistré : nouvelle version en vigueur.',
      WARNING: 'Changement enregistré, avec un signalement.',
      CONFLICT: 'Changement mis en attente d’examen (conflit).',
      REJECTED: 'Changement refusé : {{reason}}',
    },
    label: {
      pricing_max_discount_pct: 'Remise maximale du rôle {{role}}',
      crm_inactive_after_days: 'Jours sans achat avant qu’un client soit inactif',
      crm_visit_max_distance_m: 'Distance maximale d’une visite par rapport au client',
      crm_phone_default_country_code: 'Indicatif téléphonique par défaut',
      crm_commercial_role_codes: 'Rôles considérés comme commerciaux',
      sales_direct_cancel_minutes: 'Délai d’annulation directe d’une vente',
      sales_default_payment_terms_days: 'Délai de paiement par défaut (vente à crédit)',
      sales_duplicate_payment_window_minutes: 'Fenêtre de détection d’un encaissement en double',
      sales_offline_over_allocation_allowed: 'Vente hors ligne au-delà du quota autorisée',
      sales_offline_unallocated_sale_signal: 'Signalement d’une vente hors ligne sans quota',
      sales_undelivered_alert_days: 'Jours avant l’alerte d’une vente non livrée',
      sales_high_loss_alert_sales_pct: 'Seuil d’alerte de perte, en part des ventes',
      sales_commercial_working_days: 'Jours travaillés des commerciaux',
      sales_inactive_commercial_role_codes: 'Rôles suivis pour inactivité commerciale',
      pricing_stale_rules_hours: 'Ancienneté maximale des règles de prix sur un appareil',
      finance_cash_holding_max_xaf: 'Plafond d’encaisse d’un utilisateur',
      finance_cash_sessions_per_day_max:
        'Sessions de caisse par jour et par point de vente (0 : sans limite)',
      finance_cash_session_force_close_hours:
        'Durée avant la clôture d’office d’une session de caisse',
      finance_cash_variance_critical_xaf: 'Écart de caisse critique',
      finance_cash_variance_tolerance_xaf: 'Écart de caisse toléré sans validation',
      finance_cash_transfer_unreceived_hours:
        'Délai avant l’alerte d’une remise de fonds non reçue',
      finance_cash_holding_max_days: 'Jours d’encaisse sans remise avant alerte',
      finance_cash_transfer_allowed_pairs: 'Remises de fonds autorisées entre types de comptes',
      finance_cash_transfer_series_code: 'Code de la série des remises de fonds',
      finance_supplier_payment_approval_xaf: 'Seuil de validation d’un paiement fournisseur',
      finance_expense_approval_threshold_xaf: 'Seuil de validation d’une dépense',
      finance_expense_photo_threshold_xaf: 'Seuil d’un justificatif photo pour une dépense',
      finance_pos_expense_max_xaf: 'Plafond d’une dépense payée par la caisse d’un point de vente',
      finance_invoice_match_qty_tolerance_pct:
        'Tolérance de quantité au rapprochement des factures',
      finance_invoice_match_price_tolerance_xaf: 'Tolérance de prix unitaire au rapprochement',
      finance_invoice_without_po_mode: 'Facture sans bon de commande',
      finance_supplier_advance_allowed: 'Avance fournisseur autorisée',
      finance_supplier_series_code: 'Code de la série des factures et paiements fournisseurs',
      procurement_po_approval_threshold_xaf: 'Seuil de validation d’un bon de commande',
      procurement_receipt_over_tolerance_pct: 'Tolérance de réception au-delà du commandé',
      procurement_offline_over_receipt_mode: 'Réception hors ligne au-delà du reliquat',
      procurement_receipt_photo_threshold_xaf:
        'Valeur de réception exigeant la photo du bon de livraison',
      procurement_receipt_incomplete_alert_days: 'Jours avant l’alerte d’une réception incomplète',
      inventory_count_approval_threshold_xaf: 'Seuil de validation d’un écart d’inventaire',
      inventory_transfer_unmatched_hours:
        'Délai avant le rapprochement d’une réception sans document',
      inventory_blind_receipt_match_mode: 'Règle de rapprochement d’une réception sans document',
      inventory_high_loss_alert_xaf: 'Seuil d’alerte d’une perte importante',
      inventory_transfer_not_received_hours: 'Délai avant l’alerte d’un transfert non reçu',
      inventory_lot_expiring_days: 'Jours avant la péremption d’un lot (alerte)',
      inventory_replenish_alert_scope: 'Alerte de réapprovisionnement',
      production_egg_grade_product_codes: 'Produits des calibres d’œufs (dans l’ordre)',
      production_hatching_egg_product_code: 'Produit des œufs à couver',
      production_egg_shelf_life_days: 'Durée de conservation des œufs collectés',
      production_slaughter_shelf_life_days: 'Durée de conservation après abattage',
      production_incubation_durations: 'Échéancier d’incubation par espèce',
      production_slaughterable_lot_types: 'Types de lots abattables',
      production_high_mortality_alert_pct: 'Seuil d’alerte de mortalité (en % de l’effectif)',
      production_high_mortality_alert_heads: 'Seuil d’alerte de mortalité (nombre de têtes)',
      production_daily_entry_working_days: 'Jours de saisie attendue d’un lot',
      production_standard_cost_alert_lookback_days: 'Fenêtre de contrôle du coût standard',
      fieldwork_geofence_radius_m: 'Rayon du géorepère par défaut',
      fieldwork_max_gps_accuracy_m: 'Précision GPS maximale acceptée',
      fieldwork_override_min_attempts: 'Tentatives de pointage avant une dérogation',
      fieldwork_override_min_minutes: 'Minutes d’attente avant une dérogation de pointage',
      fieldwork_suspicion_max_speed_kmh: 'Vitesse jugée suspecte entre deux pointages',
      fieldwork_suspicion_repeated_accuracy_count: 'Précisions GPS identiques jugées suspectes',
      offline_max_autonomy_hours: 'Autonomie maximale hors ligne',
      offline_warning_hours: 'Avertissement avant la fin de l’autonomie hors ligne',
      sync_backdate_max_hours: 'Antidatage maximal d’une opération',
      sync_justification_after_hours: 'Heures au-delà desquelles un motif d’antidatage est exigé',
      approvals_pending_long_hours: 'Heures avant l’alerte d’une validation en attente',
      attachments_missing_alert_days: 'Jours avant l’alerte d’une pièce manquante',
      attachments_missing_alert_horizon_days: 'Historique contrôlé pour les pièces manquantes',
      audit_finance_entity_types: 'Entités du journal d’audit lisibles par la Finance',
      sync_clock_skew_flag_minutes: 'Écart d’horloge signalé',
      sync_occurred_at_future_tolerance_minutes: 'Tolérance d’une date de fait dans le futur',
      sync_retry_hold_minutes: 'Attente avant de renvoyer une commande en échec',
      sync_pull_settle_seconds: 'Fenêtre de stabilisation du flux de synchronisation',
      sync_pull_record_grain_seconds: 'Pas d’enregistrement du dernier tirage d’un appareil',
      sync_stale_hours: 'Heures sans contact avant qu’un appareil soit en retard',
      sync_error_alert_window_hours: 'Fenêtre de l’alerte d’erreurs de synchronisation',
      sync_error_alert_excluded_codes: 'Codes d’erreur exclus de l’alerte',
      sync_stale_horizon_days: 'Jours de silence avant qu’un appareil soit abandonné',
      sync_seq_gap_alert_hours: 'Heures avant l’alerte d’un trou de séquence',
      platform_job_lag_alert_seconds: 'Retard d’une tâche planifiée avant alerte',
      platform_failed_jobs_window_hours: 'Fenêtre de l’alerte de tâches en échec',
      platform_job_retention_days: 'Conservation de l’historique des tâches',
      platform_consumer_failure_retention_days: 'Conservation des échecs de consommateurs',
      communication_push_group_minutes: 'Regroupement des notifications push',
      communication_consumer_lag_alert_seconds:
        'Retard d’un consommateur d’événements avant alerte',
      communication_consumer_failure_window_hours: 'Fenêtre de l’alerte d’échec de consommateur',
      communication_direct_notification_disabled_types: 'Types d’alerte sans notification directe',
      communication_alert_retention_days: 'Conservation des alertes',
      communication_notification_retention_days: 'Conservation des notifications',
      analytics_freshness_warning_hours: 'Fraîcheur des données avant avertissement',
      analytics_dashboard_max_period_days: 'Période maximale d’un tableau de bord',
      analytics_query_max_rows: 'Lignes maximales d’une exploration',
      analytics_query_max_execution_ms: 'Durée maximale d’une exploration',
      analytics_query_max_period_days: 'Période maximale d’une exploration',
      analytics_query_max_concurrent_per_user: 'Explorations simultanées par utilisateur',
      analytics_query_max_concurrent_global: 'Explorations simultanées au total (0 : sans plafond)',
      analytics_scope_max_candidates: 'Candidats maximaux de l’étendue d’une exploration',
      analytics_pending_approvals_count_cap: 'Plafond du compteur de validations en attente',
      analytics_snapshot_active_days: 'Jours d’activité pour les instantanés personnels',
      analytics_snapshot_batch_size: 'Utilisateurs traités par exécution des instantanés',
      analytics_snapshot_time_budget_seconds: 'Durée maximale d’une exécution des instantanés',
      analytics_export_max_rows: 'Lignes maximales d’un export',
      analytics_export_max_seconds: 'Durée maximale du calcul d’un export',
      analytics_export_file_hours: 'Validité du fichier d’un export',
      analytics_export_max_pending_per_user: 'Exports simultanés par demandeur',
      analytics_export_file_retention_days: 'Conservation du fichier d’un export',
      analytics_query_log_retention_days: 'Conservation du journal des explorations',
      analytics_export_abandon_hours: 'Heures avant l’abandon d’un export bloqué',
      analytics_export_download_revalidate_rights:
        'Revérifier les droits au téléchargement d’un export',
    },
  },
  audit: {
    help_title: 'À quoi sert cet écran ?',
    intro:
      'Le journal d’audit garde, sans jamais rien effacer, qui a fait quoi, quand et depuis quel appareil, avec l’état avant et après. Cherchez par période, entité, acteur ou action. La Finance ne voit que les entités financières. Chaque recherche est elle-même inscrite au journal.',
    filters: 'Filtres du journal',
    field: {
      from: 'Du',
      to: 'Au',
      entity_type: 'Type d’entité',
      actor: 'Qui',
      action: 'Action',
      entity_id: 'Identifiant de l’entité',
    },
    hint: { action: 'Texte exact, par exemple identity.user.create.' },
    all: 'Tous',
    search: 'Rechercher',
    reset: 'Réinitialiser',
    problem: {
      PERIOD: 'La date de fin précède celle de début.',
      ENTITY_ID: 'Un identifiant d’entité est un UUID (32 caractères et des tirets).',
    },
    denied: 'Lecture refusée : droit insuffisant.',
    unavailable: 'Journal indisponible : réessayez quand le serveur répond.',
    none: 'Aucune entrée ne correspond à cette recherche.',
    count_one: '{{count}} entrée affichée, de la plus récente à la plus ancienne.',
    count_other: '{{count}} entrées affichées, de la plus récente à la plus ancienne.',
    older: 'Voir les entrées plus anciennes',
    older_unavailable: 'Entrées plus anciennes indisponibles : réessayez.',
    entry_label: 'Entrée {{seq}} du journal',
    unknown_actor: 'Acteur inconnu',
    result: {
      SUCCESS: 'Réussi',
      DENIED: 'Refusé',
      FAILED: 'Échoué',
      QUARANTINED: 'En quarantaine',
    },
    changed: 'Champs modifiés : {{fields}}',
    detail: 'Voir le détail',
    occurred_at: 'Date du fait',
    roles: 'Rôles',
    reason: 'Motif',
    device: 'Appareil',
    command: 'Commande',
    before: 'Avant',
    after: 'Après',
    entity: {
      ALERT: 'Alerte',
      ALERT_DETECTION_PASS: 'Passe de détection d’alertes',
      ANALYTICS_DATASET: 'Jeu de données d’analyse',
      ANALYTICS_EXPORT: 'Export d’analyse',
      APPROVAL_REQUEST: 'Demande de validation',
      ATTACHMENT: 'Pièce jointe',
      AUDIT_LOG: 'Journal d’audit',
      AUDIT_PARTITION: 'Partition du journal',
      CASH_ACCOUNT: 'Compte de caisse',
      CASH_RECONCILIATION: 'Rapprochement de caisse',
      CASH_SESSION: 'Session de caisse',
      CASH_TRANSFER: 'Remise de fonds',
      CASH_TRANSFER_TARGET: 'Destination de remise',
      CONTROL_POLICY: 'Politique de contrôle',
      CUSTOMER: 'Client',
      CUSTOMER_PAYMENT: 'Encaissement client',
      DELIVERY_NOTE: 'Bon de livraison',
      DEVICE: 'Appareil',
      DEVICE_ERROR: 'Erreur d’appareil',
      EVENT_CONSUMER: 'Consommateur d’événements',
      EXPENSE: 'Dépense',
      EXPENSE_CATEGORY: 'Catégorie de dépense',
      GEO_CHECKIN: 'Pointage',
      GOODS_RECEIPT: 'Réception de marchandises',
      INCUBATION_BATCH: 'Lot d’incubation',
      INTERNAL_NOTE: 'Note interne',
      INVENTORY_COUNT: 'Inventaire',
      JOB_TYPE: 'Type de tâche',
      KPI_SNAPSHOT: 'Instantané d’indicateurs',
      LEDGER_RECONCILIATION: 'Rapprochement du registre',
      LOCATION: 'Emplacement',
      LOSS_DAY: 'Jour de perte',
      LOT_CONSUMPTION: 'Consommation d’un lot',
      LOT_DAY: 'Saisie du jour d’un lot',
      LOT_LOSS: 'Perte d’un lot',
      PIPELINE_STEP: 'Étape de pipeline',
      POINT_OF_SALE: 'Point de vente',
      POS_PRODUCT: 'Produit de point de vente',
      PRICE_RULE: 'Règle de prix',
      PRODUCT: 'Produit',
      PRODUCT_CATEGORY: 'Catégorie de produit',
      PRODUCT_STANDARD_COST: 'Coût standard',
      PRODUCT_UNIT: 'Conditionnement',
      PRODUCTION_LOT: 'Lot de production',
      PURCHASE_ORDER: 'Bon de commande',
      PURCHASE_REQUEST: 'Demande d’achat',
      REASON_CODE: 'Motif',
      ROLE_ASSIGNMENT: 'Affectation de rôle',
      SALE: 'Vente',
      SALE_CANCELLATION: 'Annulation de vente',
      SALES_CHANNEL: 'Canal de vente',
      SALES_ORDER: 'Commande client',
      SALES_TARGET: 'Objectif de vente',
      SAVED_VIEW: 'Vue sauvegardée',
      SESSION: 'Session de connexion',
      SETTING: 'Paramètre',
      SITE: 'Site',
      STOCK_ALLOCATION: 'Quota de stock',
      STOCK_BALANCE: 'Solde de stock',
      STOCK_LOSS: 'Perte de stock',
      STOCK_LOT: 'Lot de stock',
      STOCK_POSITION: 'Position de stock',
      STOCK_THRESHOLD: 'Seuil de stock',
      STOCK_TRANSFER: 'Transfert de stock',
      SUPPLIER: 'Fournisseur',
      SUPPLIER_INVOICE: 'Facture fournisseur',
      SUPPLIER_PAYMENT: 'Paiement fournisseur',
      SYNC_COMMAND: 'Commande de synchronisation',
      SYNC_CONFLICT: 'Conflit de synchronisation',
      TEAM: 'Équipe',
      TEAM_MEMBERSHIP: 'Appartenance à une équipe',
      UNIT: 'Unité',
      USER: 'Utilisateur',
      WORK_SESSION: 'Service (pointage)',
      ZONE: 'Zone',
    },
  },
  reassign: {
    help_title: 'À quoi sert cet écran ?',
    intro:
      'Réaffectez un client ou un prospect à un autre commercial actif de votre périmètre. Chaque réaffectation exige un motif et laisse une trace immuable ; le nouveau titulaire reçoit le compte à sa prochaine synchronisation.',
    online_only:
      'Écran en ligne seulement : reconnectez-vous pour consulter les clients du serveur et les réaffecter.',
    denied: 'Lecture des clients refusée : droit insuffisant.',
    read_error: 'Clients indisponibles : réessayez quand le serveur répond.',
    refresh: 'Actualiser',
    refreshing: 'Relecture des clients après la dernière réaffectation…',
    empty: 'Aucun client ne correspond à ces filtres.',
    owner_filter_label: 'Titulaire actuel',
    owner_filter_all: 'Tous les titulaires',
    owner_none: 'Sans titulaire',
    owner_unknown: 'Titulaire hors liste',
    current_owner: 'Titulaire : {{owner}}',
    open_action: 'Réaffecter…',
    form_aria: 'Réaffecter {{name}}',
    new_owner_label: 'Nouveau titulaire',
    new_owner_placeholder: 'Choisir un commercial actif…',
    reason_label: 'Motif de la réaffectation',
    reason_hint: 'Précisez la raison (départ, rééquilibrage de portefeuille, changement de zone…).',
    confirm: 'Confirmer la réaffectation',
    result: {
      WAITING_offline:
        'Réaffectation enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Réaffectation en cours d’envoi…',
      DONE: 'Client réaffecté.',
      WARNING: 'Client réaffecté, avec un signalement.',
      CONFLICT: 'Réaffectation mise en attente d’examen (conflit).',
      REJECTED: 'Réaffectation refusée : {{reason}}',
    },
  },
  team_activity: {
    help_title: 'À quoi sert cet écran ?',
    intro:
      'Suivez par équipe et par jour métier les prises et fins de service, les tentatives refusées hors zone ou imprécises, les demandes de dérogation en attente ainsi que les visites et échanges enregistrés.',
    online_only:
      'Écran en ligne seulement : reconnectez-vous pour consulter les présences et l’activité de l’équipe.',
    denied: 'Lecture de l’activité d’équipe refusée : droit insuffisant.',
    unavailable: 'Activité de l’équipe indisponible : réessayez quand le serveur répond.',
    refresh: 'Actualiser',
    no_team: 'Aucune équipe active dans votre périmètre.',
    empty_filter: 'Aucun membre de l’équipe dans ce filtre.',
    team_label: 'Équipe',
    date_label: 'Jour métier',
    filter_label: 'Filtre de présence',
    filter: {
      ALL: 'Tous',
      PRESENT: 'Présents',
      ABSENT: 'Absents',
      ANOMALY: 'Anomalies',
    },
    pending_overrides_banner_one: '{{count}} demande de dérogation de pointage attend validation.',
    pending_overrides_banner_other:
      '{{count}} demandes de dérogation de pointage attendent validation.',
    open_approvals: 'Ouvrir les validations',
    summary_line:
      '{{present}} / {{total}} en service ou passés · {{visits}} visite(s) · {{interactions}} échange(s) · {{rejected}} tentative(s) refusée(s)',
    member_counts: '{{visits}} visite(s) · {{interactions}} échange(s)',
    status: {
      IN_SERVICE: 'En service',
      ENDED: 'Service terminé',
      PENDING_OVERRIDE: 'Dérogation en attente',
      REJECTED_ONLY: 'Pointage refusé',
      ABSENT: 'Sans pointage',
    },
    override: {
      PENDING: 'dérogation en attente',
      PENDING_APPROVAL: 'dérogation en attente',
      APPROVED: 'dérogation accordée',
      REJECTED: 'dérogation refusée',
    },
    session_open: 'En service dans {{zone}} depuis {{start}}',
    session_closed: 'Service dans {{zone}} de {{start}} à {{end}}',
    checkins_aria: 'Pointages du membre',
    flagged_visits_one: '{{count}} visite signalée (hors zone, hors session ou GPS suspect).',
    flagged_visits_other: '{{count}} visites signalées (hors zone, hors session ou GPS suspect).',
  },
  sync_monitor: {
    help_title: 'À quoi sert cet écran ?',
    intro:
      'Suivez la fraîcheur de synchronisation des appareils, le volume d’opérations en attente qu’ils signalent et les conflits ouverts. Un appareil en retard approche ou dépasse le seuil d’autonomie hors ligne.',
    online_only:
      'Écran en ligne seulement : reconnectez-vous pour superviser la synchronisation des appareils.',
    denied: 'Supervision refusée : droit insuffisant.',
    unavailable: 'Supervision indisponible : réessayez quand le serveur répond.',
    refresh: 'Actualiser',
    open_conflicts_banner_one: '{{count}} conflit de synchronisation ouvert attend résolution.',
    open_conflicts_banner_other:
      '{{count}} conflits de synchronisation ouverts attendent résolution.',
    open_conflicts_action: 'Résoudre les conflits',
    summary_line:
      '{{active}} appareil(s) actif(s) · {{stale}} en retard · {{pendingDevices}} en attente d’approbation · {{pendingOps}} opération(s) en file signalée(s)',
    filter_label: 'Filtrer les appareils',
    filter: {
      ALL: 'Tous',
      STALE: 'En retard',
      PENDING_QUEUE: 'File en attente',
      INACTIVE: 'Bloqués / retirés',
    },
    none: {
      ALL: 'Aucun appareil enregistré.',
      STALE: 'Aucun appareil en retard de synchronisation.',
      PENDING_QUEUE: 'Aucun appareil ne signale d’opérations en attente.',
      INACTIVE: 'Aucun appareil bloqué, perdu ou retiré.',
    },
    badge_stale: 'En retard',
    badge_pending_one: '{{count}} en attente',
    badge_pending_other: '{{count}} en attente',
    last_push: 'Dernier envoi : {{date}}',
    no_push: 'Aucun envoi reçu',
    last_pull: 'Dernière réception : {{date}}',
    no_pull: 'Aucune réception',
  },
  sync_conflicts: {
    help_title: 'À quoi sert cet écran ?',
    intro:
      'Examinez et clôturez les conflits et anomalies de synchronisation relevant de votre rôle. Une opération déjà appliquée ne s’annule jamais par une réécriture : elle se régularise par écriture compensatoire motivée ou se classe après contrôle. L’auteur d’une anomalie déjà appliquée ne peut pas la clore lui-même (AV-192).',
    online_only:
      'Écran en ligne seulement : reconnectez-vous pour consulter et résoudre les conflits de synchronisation.',
    denied: 'Lecture des conflits refusée : droit insuffisant.',
    unavailable: 'Conflits indisponibles : réessayez quand le serveur répond.',
    refresh: 'Actualiser',
    refreshing: 'Relecture des conflits après la dernière décision…',
    filter_label: 'Statut des conflits',
    status: {
      OPEN: 'Ouverts',
      RESOLVED: 'Résolus',
      DISMISSED: 'Classés sans suite',
    },
    none: {
      OPEN: 'Aucun conflit ouvert dans votre périmètre.',
      RESOLVED: 'Aucun conflit résolu à afficher.',
      DISMISSED: 'Aucun conflit classé sans suite.',
    },
    badge_applied: 'Appliqué (à régulariser)',
    badge_quarantined: 'En quarantaine',
    author: 'Auteur : {{name}}',
    entity_line: 'Entité : {{entityType}} · Rôle responsable : {{ownerRole}}',
    managed_elsewhere: 'Ce doublon de référence de paiement se décide par sa validation dédiée.',
    self_forbidden:
      'Vous êtes l’auteur de cette opération déjà appliquée : un autre responsable habilité doit clore ce conflit (AV-192).',
    action: {
      RESOLVE: 'Résoudre…',
      DISMISS: 'Classer sans suite…',
    },
    resolution_legend: 'Mode de résolution',
    resolution: {
      ACCEPT_CLIENT: 'Accepter l’opération du téléphone',
      KEEP_SERVER: 'Maintenir l’état du serveur',
      MERGE: 'Fusionner les écritures',
      COMPENSATE: 'Régulariser par opération(s) compensatoire(s)',
    },
    refs_required_label: 'Identifiant(s) UUID des opérations compensatoires (exigé)',
    refs_optional_label: 'Identifiant(s) UUID de référence (facultatif)',
    refs_hint: 'Séparez plusieurs identifiants UUID par une virgule ou un espace.',
    comment_label: 'Commentaire justificatif (exigé)',
    problem: {
      REFS_REQUIRED:
        'Indiquez au moins un identifiant UUID d’opération compensatoire pour ce mode de résolution.',
      REFS_INVALID: 'Chaque référence doit être un identifiant UUID valide.',
    },
    confirm: {
      RESOLVE: 'Confirmer la résolution',
      DISMISS: 'Confirmer le classement sans suite',
    },
    result: {
      WAITING_offline:
        'Décision enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Décision en cours d’envoi…',
      DONE: 'Conflit mis à jour.',
      WARNING: 'Conflit mis à jour, avec un signalement.',
      CONFLICT: 'Décision mise en attente d’examen (conflit).',
      REJECTED: 'Décision refusée : {{reason}}',
    },
    type: {
      STOCK_NEGATIVE: 'Stock devenu négatif',
      CASH_NEGATIVE: 'Caisse devenue négative',
      CASH_SESSION_MISSING: 'Espèces encaissées sans caisse ouverte',
      CASH_SESSION_CONCURRENT_OPEN: 'Caisse ouverte deux fois',
      CASH_SESSION_CONCURRENT_CLOSE: 'Caisse fermée deux fois',
      CASH_ACCOUNT_INACTIVE: 'Caisse désactivée',
      CASH_SESSION_SITE_CLOSED: 'Caisse d’un site fermé',
      CASH_SESSION_CLOSE_BEFORE_MOVEMENTS: 'Caisse fermée avant ses mouvements',
      CASH_SESSION_REEVALUATED: 'Caisse réévaluée',
      CASH_TRANSFER_RECEIVED_AFTER_CANCEL: 'Remise reçue après son annulation',
      CASH_TRANSFER_CANCEL_AFTER_RECEIVE: 'Remise annulée après sa réception',
      CASH_TRANSFER_RECEIVED_TWICE: 'Remise reçue deux fois',
      CASH_TRANSFER_PAIR_NOT_ALLOWED: 'Remise entre caisses non prévue',
      PAYMENT_REFERENCE_DUPLICATE: 'Référence de paiement en double',
      PAYMENT_UNALLOCATED: 'Encaissement non affecté',
      DUPLICATE_SUSPECTED: 'Doublon suspecté',
      DUPLICATE_CUSTOMER: 'Client en double',
      PRICE_MISMATCH: 'Prix différent du catalogue',
      PRODUCT_INACTIVE: 'Produit désactivé',
      ANONYMOUS_UNPAID: 'Vente anonyme non payée',
      ORDER_REFUSED_PAYMENT_KEPT: 'Commande refusée, acompte conservé',
      ORDER_OVER_FULFILMENT: 'Livraison au-delà de la commande',
      VERSION_CONFLICT: 'Modification concurrente',
      LOT_CLOSED: 'Lot clôturé',
      LOT_NOT_SLAUGHTERABLE: 'Lot non abattable',
      LOT_NOT_SELLABLE: 'Lot non vendable',
      INCUBATION_BALANCE: 'Écart d’incubation',
      TRANSFER_UNMATCHED: 'Transfert non rapproché',
      TRANSFER_REQUEST_CLOSED: 'Demande de transfert close',
      TRANSFER_OVER_RECEIVED: 'Transfert reçu au-delà de l’envoi',
      TRANSFER_RETURN_CONFLICT: 'Retour de transfert en conflit',
      TRANSFER_DISPATCH_CONFLICT: 'Expédition en conflit',
      OVER_RECEIPT: 'Réception au-delà de la commande',
      RECEIPT_PHOTO_MISSING: 'Photo du bon de livraison manquante',
      CUSTODY_DEVICE_MISMATCH: 'Stock confié à un autre appareil',
      ALLOCATION_RELEASE_MISMATCH: 'Restitution de quota en écart',
      ALLOCATION_OVERCOMMITTED: 'Quotas au-delà du stock',
      ALLOCATION_REVOKED_CONSUMED: 'Quota révoqué déjà consommé',
      ALLOCATION_EXCEEDED: 'Quota dépassé',
      SYNC_COMMAND_STALLED: 'Opération bloquée à l’envoi',
    },
  },
  comm: {
    refresh: 'Actualiser',
    cancel: 'Annuler',
    result: {
      WAITING_offline:
        'Opération enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Opération en cours d’envoi…',
      DONE: 'Opération enregistrée.',
      WARNING: 'Opération enregistrée, avec un signalement.',
      CONFLICT: 'Opération mise en attente d’examen (conflit).',
      REJECTED: 'Opération refusée : {{reason}}',
    },
    alerts: {
      subtitle: 'Suivi des alertes opérationnelles, notifications et règles de déclenchement.',
      offlineBanner:
        'Hors ligne : affichage des alertes et notifications synchronisées sur cet appareil.',
      tab: {
        alerts: 'Alertes',
        notifications: 'Notifications',
        rules: 'Règles d’alerte',
      },
      summaryLabel: 'Synthèse des alertes',
      summaryOpen: 'Ouvertes',
      summaryAcked: 'Prises en compte',
      filterStatus: 'Statut',
      filterSeverity: 'Gravité',
      allOption: 'Toutes',
      status: {
        OPEN: 'Ouverte',
        ACKNOWLEDGED: 'Prise en compte',
        RESOLVED: 'Résolue',
        DISMISSED: 'Écartée',
      },
      severity: {
        INFO: 'Information',
        WARNING: 'Avertissement',
        CRITICAL: 'Critique',
      },
      nature: {
        STATE: 'État',
        EVENT: 'Événement',
      },
      typeLabel: 'Type',
      emptyTitle: 'Aucune alerte à afficher',
      emptyHint: 'Aucune alerte ne correspond aux filtres sélectionnés.',
      openSourceScreen: 'Ouvrir l’écran concerné',
      acknowledgeAction: 'Prendre en compte',
      resolveAction: 'Résoudre',
      dismissAction: 'Écarter',
      ackCommentLabel: 'Commentaire de prise en compte (facultatif)',
      resolveCommentLabel: 'Commentaire de résolution (exigé)',
      dismissReasonLabel: 'Motif de classement sans suite (exigé)',
      confirmAction: 'Valider',
    },
    notifications: {
      unreadOnly: 'Non lues seulement',
      markAllRead: 'Tout marquer comme lu',
      emptyTitle: 'Aucune notification',
      emptyHint: 'Vous n’avez aucune notification dans cette vue.',
      readBadge: 'Lue',
      unreadBadge: 'Non lue',
      kind: {
        ALERT: 'Alerte',
        NOTE: 'Note de direction',
        EVENT: 'Événement',
        EXPORT_READY: 'Export prêt',
      },
      openTarget: 'Ouvrir',
      markRead: 'Marquer comme lue',
    },
    rules: {
      onlineOnlyBanner: 'La configuration des règles d’alerte nécessite une connexion en ligne.',
      enabled: 'Active',
      disabled: 'Désactivée',
      recipients: 'Destinataires',
      escalationAfter: 'Escalade après {{hours}} h',
      enableSwitch: 'Activer cette règle d’alerte',
      severityLabel: 'Gravité',
      escalationHoursLabel: 'Délai d’escalade (heures)',
      editAction: 'Modifier',
      saveAction: 'Enregistrer',
    },
    notes: {
      subtitle: 'Instructions, procédures et informations officielles de la Direction.',
      pendingSubtitle: '{{count}} note(s) en attente d’accusé de réception.',
      offlineBanner: 'Hors ligne : affichage des notes synchronisées sur cet appareil.',
      openPublish: 'Publier / suivre',
      filterPendingAck: 'En attente d’accusé seulement',
      emptyTitle: 'Aucune note de direction',
      emptyHint: 'Aucune note publiée ne correspond à ce filtre.',
      category: {
        PRIX: 'Prix & tarifs',
        INSTRUCTION: 'Instruction',
        PROCEDURE: 'Procédure',
        INFORMATION: 'Information',
      },
      status: {
        DRAFT: 'Brouillon',
        PUBLISHED: 'Publiée',
        ARCHIVED: 'Archivée',
      },
      statusAcked: 'Accusé donné',
      statusAckRequired: 'Accusé requis',
      statusRead: 'Lue',
      statusUnread: 'Nouvelle',
      publishedAt: 'Publiée le {{date}}',
      expiresAt: 'Valable jusqu’au {{date}}',
      acknowledgeAction: 'Accuser réception',
      markReadAction: 'Marquer comme lue',
      publishSubtitle: 'Rédaction, publication ciblée et suivi des accusés de réception.',
      publishOffline: 'La publication et l’archivage des notes nécessitent une connexion en ligne.',
      tab: {
        compose: 'Rédiger / publier',
        manage: 'Notes & suivi',
      },
      fieldTitle: 'Titre de la note',
      fieldCategory: 'Catégorie',
      fieldBody: 'Contenu de la note',
      fieldRequiresAck: 'Exiger un accusé de réception des destinataires',
      fieldExpiresAt: 'Date d’expiration',
      fieldAudience: 'Destinataires (audience)',
      audienceTargetType: 'Cibler par',
      audienceTargetRef: 'Cible',
      audienceType: {
        ALL: 'Tout le personnel',
        ROLE: 'Rôle',
        SITE: 'Site',
        ZONE: 'Zone commerciale',
        USER: 'Utilisateur nominatif',
      },
      addAudience: 'Ajouter à l’audience',
      errorRequired: 'Veuillez renseigner le titre, le contenu et au moins une cible d’audience.',
      publishAction: 'Publier la note',
      saveDraftAction: 'Enregistrer en brouillon',
      filterStatus: 'Statut',
      emptyManageTitle: 'Aucune note enregistrée',
      emptyManageHint: 'Rédigez un brouillon ou publiez une note depuis le premier onglet.',
      editDraftAction: 'Modifier le brouillon',
      followUpAction: 'Suivi des lectures',
      archiveAction: 'Archiver',
      followUpAudience: 'Destinataires',
      followUpRead: 'Lues',
      followUpAcked: 'Accusés reçus',
      followUpMissing: 'En attente',
      followUpMissingNames: 'En attente : {{names}}',
      archiveReasonLabel: 'Motif d’archivage (exigé)',
      confirmArchive: 'Confirmer l’archivage',
    },
  },
  analytics: {
    refresh: 'Actualiser',
    subtitle: 'Indicateurs consolidés et aide à la décision.',
    periodRange: 'Période du {{from}} au {{to}}',
    periodLabel: 'Période',
    period: {
      TODAY: 'Aujourd’hui',
      YESTERDAY: 'Hier',
      WEEK: 'Semaine en cours',
      MONTH: 'Mois en cours',
      QUARTER: 'Trimestre en cours',
      YEAR: 'Année en cours',
    },
    computedAt: 'Calculé le {{date}}',
    staleDevices: '{{stale}} / {{total}} appareil(s) en retard de synchro',
    freshOk: 'Appareils à jour',
    onlineOnlyBanner: 'Cet écran nécessite une connexion en ligne pour interroger le serveur.',
    snapshotFallbackBanner:
      'Hors ligne : affichage du dernier instantané synchronisé ({{period}}, calculé le {{date}}).',
    snapshotSectionTitle: 'Indicateurs synchronisés hors ligne',
    exploreAction: 'Explorer',
    blockForbidden: 'Bloc masqué : vos droits ne permettent pas de consulter cette section.',
    blockError: 'Bloc momentanément indisponible.',
    emptyDashboardTitle: 'Aucune donnée analytique disponible',
    emptyDashboardHint: 'Connectez-vous au réseau et actualisez pour charger les indicateurs.',
    result: {
      WAITING_offline:
        'Demande enregistrée sur ce téléphone ; elle sera envoyée au retour du réseau.',
      WAITING_online: 'Demande en cours d’envoi…',
      DONE: 'Opération analytique enregistrée.',
      WARNING: 'Opération analytique enregistrée, avec un signalement.',
      CONFLICT: 'Opération mise en attente d’examen.',
      REJECTED: 'Opération refusée : {{reason}}',
    },
    explorer: {
      subtitle: 'Exploration sur catalogue gouverné, vues sauvegardées et exports audités.',
      tab: {
        explore: 'Explorer',
        views: 'Vues sauvegardées',
        exports: 'Exports',
      },
      datasetLabel: 'Jeu de données',
      dimensionsLabel: 'Regrouper par (dimensions, max 3)',
      measuresLabel: 'Indicateurs (mesures, max 5)',
      runAction: 'Exécuter l’analyse',
      saveViewAction: 'Sauvegarder la vue',
      exportFormatAction: 'Exporter {{format}}',
      viewNameLabel: 'Nom de la vue',
      confirmSaveView: 'Enregistrer',
      queryError: 'Impossible d’exécuter la requête analytique.',
      resultsTitle: 'Résultats ({{count}} ligne(s))',
      scopeTruncated:
        'Périmètre volumineux : le résultat est borné aux premiers enregistrements autorisés.',
      totalLabel: 'Total · {{label}}',
      emptyRowsTitle: 'Aucun résultat',
      emptyRowsHint: 'Aucune ligne ne correspond à cette définition sur la période.',
      openSourceDoc: 'Ouvrir la pièce source',
    },
    views: {
      emptyTitle: 'Aucune vue sauvegardée',
      emptyHint: 'Enregistrez une exploration depuis le premier onglet pour la retrouver ici.',
      executability: {
        OK: 'Prête',
        OUTDATED: 'Catalogue évolué',
        UNAVAILABLE: 'Indisponible',
      },
      defaultForRole: 'Par défaut : {{role}}',
      sharedWith: 'Partagée avec : {{roles}}',
      openAction: 'Exécuter',
      shareAction: 'Partager',
      deleteAction: 'Supprimer',
      setDefaultAction: 'Définir par défaut',
      unsetDefaultAction: 'Retirer par défaut',
      shareRoleLabel: 'Ajouter un rôle au partage',
      confirmShare: 'Partager avec ce rôle',
    },
    exports: {
      emptyTitle: 'Aucun export récent',
      emptyHint: 'Lancez un export CSV ou XLSX depuis l’onglet Explorer.',
      status: {
        QUEUED: 'En file',
        RUNNING: 'En cours',
        DONE: 'Prêt',
        FAILED: 'Échec',
        EXPIRED: 'Expiré',
      },
      rowCount: '{{count}} ligne(s)',
      downloadAction: 'Télécharger le fichier',
    },
  },
} as const;
